import { notifKind } from './util/notif_kinds';
import { startViewHash } from './util/start_view';
import { closeInSandboxView } from './util/sandbox_zoom';
import { botReply, describeLastSeen, NURRO_NAME, onlineTarget } from './chat_bot';
import { taskAction } from './pilot_actions';
import { startClaimTimer, countsTime, noteClaimSave, noteClaimFinished, type TimedClaim } from './util/claim_time';
// A congratulations at 10, 25, 50 and 100 hours on a cell, and a finished Super Quest.
import './util/claim_congrats';
import { syncCellToSheet } from './sheet_sync';
import { secureUpload } from './secure_upload';
import { dayMilestonesReached, isBigDayMilestone, nextDayMilestone, wholeYears } from './util/day_milestones';
import { secureWrite } from './secure_write';
import { newOpKey, queueActivity, serverMayRecover, startActivityOutbox } from './activity_outbox';
import { trackSave } from './save_status';
import {Ref, ref, reactive, computed, watch} from 'vue';
import {ngeDefaultHoverStyle, ngeHoverStyle, ngeParseHoverStyle, ngeParsePulseColor, ngePulseColor} from 'neuroglancer/segmentation_display_state/frontend';
import {defineStore} from 'pinia';

import {Viewer} from 'neuroglancer/viewer';
import {defaultCredentialsManager} from 'neuroglancer/credentials_provider/default_manager';
import {MiddleAuthCredentialsProvider} from 'neuroglancer/datasource/middleauth/credentials_provider';
import {cancellableFetchSpecialOk, parseSpecialUrl} from 'neuroglancer/util/special_protocol_request';
import {responseJson} from 'neuroglancer/util/http_request';

import {Config, EYEWIRE_II_CAVE_CONFIG, getDatasetCaveConfig} from './config';
import {isMobileRef} from './util/mobile';
import {currentDatasetTag, canonicalDataset, currentSegLayer, DATASETS} from './datasets';
import {editsAny} from './util/dataset_access';
import {supabase} from './supabase';
import {quietly, setAnnotationCounterUser} from './util/annotation_counter';
import {isStreakMilestone, nextStreakMilestone, streakLine} from './util/streak_milestones';
import {emitScriptEvent} from './script_api';
import {ancestorAmong, getRootsFromSupervoxels, getRootFromSupervoxel, latestDescendants} from './widgets/pcg_service';
import {SegmentationUserLayer} from "neuroglancer/segmentation_user_layer";
import {makeLayer} from "neuroglancer/layer";
import pinVtkUrl from '../static/tags/pin.vtk';
import pinOtherVtkUrl from '../static/tags/pin-other.vtk';
import scytheVtkUrl from '../static/tags/scythe.vtk';
import {parsePositionString} from "neuroglancer/ui/default_clipboard_handling";
import {Uint64} from "neuroglancer/util/uint64";
import {NGE_KEEP_CUT_OPEN_KEY, ngeKeepCutOpen} from 'neuroglancer/datasource/graphene/frontend';
import {setStatedColor} from "./widgets/widget_utils";
import {mintShortStateLink} from "./util/state_link";
import {anyTabVisible, leadership, onSharedResult, shareResult} from './util/tabs';

declare const CONFIG: Config|undefined;
declare const DEFAULT_SETTINGS: {  [key: string]: any }

export const useDropdownListStore = defineStore('dropdownlist', () => {
  let dropdownCount = 0;

  const activeDropdowns = reactive({} as {[group: string]: number|undefined});

  function getDropdownId() {
    dropdownCount++;
    return dropdownCount;
  }

  return {getDropdownId, activeDropdowns};
});

export interface loginSession {
  id: number,
  key: string,
  name: string,
  email: string,
  hostname: string,
  status?: number,
}

export const useLoginStore = defineStore('login', () => {
  const TOKEN_PREFIX = 'auth_token_v2_';

  async function logout(session: loginSession) {
    window.localStorage.removeItem(session.key);
    const login_url = session.key.split(TOKEN_PREFIX)[1] as string|undefined;
    if (!login_url) return;
    const provider = defaultCredentialsManager.getCredentialsProvider('middleauth', login_url) as MiddleAuthCredentialsProvider;
    if (provider) {
      provider.updateCachedGet();
    }
    sessions.value = sessions.value.filter(x => x.key !== session.key);
  }

  async function update() {
    try {
      await doUpdate();
    } finally {
      // The mobile welcome sheet holds its login section blank until the
      // first token check settles, so this must flip true on every exit
      // path — including the mid-loop early return and thrown errors.
      checked.value = true;
    }
  }

  async function doUpdate() {
    const localStorageKeys: string[] = [];
    for (const key of Object.keys(window.localStorage)) {
      if (key.startsWith(TOKEN_PREFIX)) {
        localStorageKeys.push(key);
      }
    }

    const newSessions: loginSession[] = [];

    for (const key of localStorageKeys) {
      const login_url = key.split(TOKEN_PREFIX)[1] as string|undefined;
      if (!login_url) continue;
      const provider = defaultCredentialsManager.getCredentialsProvider('middleauth', login_url) as MiddleAuthCredentialsProvider;
      if (!provider) continue;

      const dataString = localStorage.getItem(key);
      if (!dataString) { return; }
      const data = JSON.parse(dataString);
      const {hostname} = new URL(data.url);

      try {
        const res = await fetch(data.url + '/api/v1/user/me', {
          headers: {
            "Authorization": `Bearer ${data.accessToken}`,
          }
        });
        if (res.status === 200) {
          const contentType = res.headers.get("content-type");
          const message = await ((contentType === 'application/json') ? res.json() : res.text());
          const { id, name, email } = message;
          newSessions.push({
            id,
            key,
            name,
            email,
            hostname,
          });
        } else {
          newSessions.push({
            id: 0,
            key,
            name: '',
            email: '',
            hostname,
            status: res.status,
          });
        }
      } catch (e) {
        e;
        // newSessions.push({
        //   name: '',
        //   email: '',
        //   hostname,
        //   error: e,
        // });
      }
    }

    sessions.value = newSessions;
  }
  const sessions: Ref<loginSession[]> = ref([]);
  /** True once the first update() has settled — before that, nobody knows
   *  yet whether the stored tokens are valid, so UI shouldn't claim either
   *  logged-in or logged-out. */
  const checked = ref(false);
  return {sessions, update, logout, checked};
});

export interface Volume {
  name: string,
  description: string,
  image_layers: Layer[],
  segmentation_layers: Layer[],
}

interface Layer {
  source: string,
  ngl_image_name?: string,
  name: string,
  description: string,
  type: string,
}


/* eslint-disable @typescript-eslint/no-explicit-any */
let stateRewriter: ((state: any) => Promise<any>) | null = null;
/** Let a tutorial rewrite every saved view before loadState applies it. */
export function setStateRewriter(fn: ((state: any) => Promise<any>) | null) {
  stateRewriter = fn;
}

export const useLayersStore = defineStore('layers', () => {
  const activeLayers: Set<string> = reactive(new Set());

  let viewer: Viewer|undefined = undefined;
  let segEditCleanup: (() => void) | null = null;

  function refreshLayers() {
    if (!viewer) return;
    activeLayers.clear();
    const layers = viewer.layerManager.managedLayers;
    for (const layer of layers) {
      if (!layer.layer) {
        console.log('does this ever happen?');
        continue;
      }
      const dataSources = layer.layer.dataSources;
      for (const source of dataSources) {
        activeLayers.add(source.spec.url.replace('middleauth+', ''));
      }
    }
    // Re-attach segment edit watcher whenever layers change
    watchSegmentEdits();
  }

  /**
   * Watch visible segments on the first SegmentationUserLayer.
   * Fires whenever the user merges, splits, or selects new segments.
   *
   * Heuristics:
   *   - segment count decreased → merge (fewer segments = segments were combined)
   *   - segment count increased → split or new selection
   */
  /** Cheap activity beacon for the CAVE edits sync: stamp users.last_edit_at
   *  at most once per 10 minutes, so the server-side sync only queries CAVE
   *  for users who actually edited since their mirror watermark (Amy: "do we
   *  really need to query quiet users?"). Best-effort; stats never depend on
   *  this locally. */
  let lastEditTouch = 0;
  async function touchLastEditAt() {
    const now = Date.now();
    if (now - lastEditTouch < 10 * 60 * 1000) return;
    lastEditTouch = now;
    try {
      const backendStore = useProofreadingBackendStore();
      if (!backendStore.userId) return;
      await supabase.from('users').update({ last_edit_at: new Date().toISOString() })
        .eq('id', backendStore.userId);
    } catch { /* beacon only */ }
  }

  /** Count one split or merge: the local numbers at once, then the log. */
  function countEdit(operation: 'merge' | 'split', log: Partial<EditLogEntry>) {
    const statsStore = useUserStatsStore();
    // One acknowledged operation is one edit
    if (operation === 'merge') {
      statsStore.setStats({
        editsAllTime:   statsStore.stats.editsAllTime   + 1,
        mergesAllTime:  statsStore.stats.mergesAllTime  + 1,
        editsThisWeek:  statsStore.stats.editsThisWeek  + 1,
        mergesThisWeek: statsStore.stats.mergesThisWeek + 1,
        editsThisMonth: statsStore.stats.editsThisMonth + 1,
        mergesThisMonth:statsStore.stats.mergesThisMonth+ 1,
        editsToday:     statsStore.stats.editsToday     + 1,
        mergesToday:    statsStore.stats.mergesToday     + 1,
      });
    } else {
      statsStore.setStats({
        editsAllTime:   statsStore.stats.editsAllTime   + 1,
        splitsAllTime:  statsStore.stats.splitsAllTime  + 1,
        editsThisWeek:  statsStore.stats.editsThisWeek  + 1,
        splitsThisWeek: statsStore.stats.splitsThisWeek + 1,
        editsThisMonth: statsStore.stats.editsThisMonth + 1,
        splitsThisMonth:statsStore.stats.splitsThisMonth+ 1,
        editsToday:     statsStore.stats.editsToday     + 1,
        splitsToday:    statsStore.stats.splitsToday    + 1,
      });
    }

    statsStore.logDailyEdit(operation);
    statsStore.signalEdit(operation);
    touchLastEditAt();
    // NOTE: cellsSubmitted is deliberately NOT touched here. It used to
    // be incremented every 5 edits "to animate the cell-dot canvas", but
    // it's a real stat (the Exploration badge track and the profile's
    // Cells number). It now comes only from CAVE completions
    // (users.cells_completed via cave_completions_mirror).

    // Report it; the server records and counts it
    try {
      const backendStore = useProofreadingBackendStore();
      if (backendStore.userId) {
        const viewer: any = (window as any)['viewer'];
        const pos = viewer?.navigationState?.position?.value;
        const coords = pos ? `${pos[0]}, ${pos[1]}, ${pos[2]}` : null;
        backendStore.logEdit({ operation, coordinates: coords, ...log });
        backendStore.postActivity(`${operation === 'merge' ? 'merged' : 'split'} segment`);
      }
    } catch { /* backend logging is non-critical */ }
  }

  // ── Edits the graph server acknowledged ─────────────────────────────
  // Each split or merge the server accepts answers with its operation id
  // (third_party/neuroglancer/datasource/graphene/frontend.ts). That answer
  // IS the edit: counted once, at once, with the id the server can check.
  // The visible-segment watcher below stays as the fallback for a graph
  // source that does not go through that code; while acknowledgements are
  // arriving it stands down, so nothing is counted twice. (It grouped
  // everything inside three seconds into one edit, and could only guess
  // that an edit had happened: leaderboard audit, 2026-10-05.)
  let lastAckAt = 0;
  const onGraphEdit = (ev: Event) => {
    const d = (ev as CustomEvent).detail ?? {};
    if (d.kind !== 'merge' && d.kind !== 'split') return;
    lastAckAt = Date.now();
    countEdit(d.kind, {
      segment_after: Array.isArray(d.roots) && d.roots.length ? d.roots.join(',') : null,
      metadata: { diff: 1, operation_id: d.operationId ?? null, graph: d.graph ?? null },
    });
  };
  // Listened to for the life of the page, whatever layers are loaded.
  window.addEventListener('nge-graph-edit', onGraphEdit);

  function watchSegmentEdits() {
    if (!viewer) return;

    // Clean up previous listener
    if (segEditCleanup) { segEditCleanup(); segEditCleanup = null; }

    const segLayer = viewer.layerManager.managedLayers.find(
      x => x.layer instanceof SegmentationUserLayer,
    );
    if (!segLayer || !(segLayer.layer instanceof SegmentationUserLayer)) return;

    const groupState = (segLayer.layer as SegmentationUserLayer)
      .displayState.segmentationGroupState.value;
    const visibleSegs = groupState.visibleSegments;

    let prevCount = visibleSegs.size;
    let pendingNetDiff = 0;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const pendingRemoved = new Set<string>(); // specific segments removed during this operation
    const pendingAdded   = new Set<string>(); // specific segments added during this operation
    const EDIT_DEBOUNCE_MS = 3000; // 3s to allow failed merges to revert before counting
    /** Which graphene tool was engaged when this operation's changes arrived. */
    let pendingTool: 'multicut' | 'merge' | null = null;

    /**
     * The graphene tool currently engaged, or null if none.
     *
     * `visibleSegments` changes for many innocent reasons — jumping to a cell,
     * clicking a row in the segment list, loading a `#segId` from chat, or
     * applying a dataset's default segments. Previously every one of those was
     * counted as a merge/split, which inflated users' edit totals, streaks,
     * badges and leaderboard rank, and wrote bogus rows to `edit_log`.
     * A real graph edit only ever happens through the multicut/merge tools, so
     * require one to be engaged before counting anything.
     *
     * `closingTool` covers the success-hold window: the new root IDs land while
     * the bar is still showing the result and the tool has begun closing.
     */
    function engagedTool(): 'multicut' | 'merge' | null {
      try {
        const smo = useSplitMergeOverlayStore();
        return smo.toolActive ?? smo.closingTool ?? null;
      } catch { return null; }
    }

    /** Is there a segmentation layer on a graph server (one whose edits are
     *  answered with an operation id)? */
    function hasGraphLayer(): boolean {
      try {
        for (const ml of (window as any).viewer?.layerManager?.managedLayers ?? []) {
          if (ml?.layer?.constructor?.type === 'segmentation' && ml.layer.graphConnection?.value) return true;
        }
      } catch { /* the viewer is going away */ }
      return false;
    }

    // Signal fires (segId, wasAdded) — track the specific segments involved
    const handler = (changedId?: any, wasAdded?: boolean) => {
      const newCount = visibleSegs.size;

      // Gate before accumulating so selection-only changes never count.
      const tool = engagedTool();
      if (!tool) {
        // Still re-baseline, or the next real edit would measure its delta
        // against a stale count and be mis-sized.
        prevCount = newCount;
        return;
      }
      pendingTool = tool;

      // Capture the specific segment IDs from the signal
      try {
        if (changedId != null) {
          const ids = Array.isArray(changedId) ? changedId : [changedId];
          for (const id of ids) {
            const str = id.toString();
            if (wasAdded) pendingAdded.add(str);
            else pendingRemoved.add(str);
          }
        }
      } catch { /* Uint64 toString may fail */ }

      if (newCount === prevCount) return;

      // Accumulate net segment change (merge = negative, split = positive)
      pendingNetDiff += (newCount - prevCount);
      prevCount = newCount;

      // Debounce: group rapid segment changes from a single merge/split
      // into one edit event (visibleSegments.changed fires multiple times
      // as root IDs update during a single graph operation)
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const net = pendingNetDiff;
        const tool = pendingTool;
        pendingNetDiff = 0;
        pendingTool = null;
        // Grab & clear the tracked segment IDs for this operation
        const removedIds = pendingRemoved.size ? [...pendingRemoved].join(',') : null;
        const addedIds   = pendingAdded.size   ? [...pendingAdded].join(',')   : null;
        pendingRemoved.clear();
        pendingAdded.clear();
        if (net === 0) return; // changes cancelled out
        if (!tool) return;     // not a graphene edit — nothing to count

        // The tool decides what happened; the direction must agree with it.
        // A merge collapses segments (net < 0), a split produces one more
        // (net > 0). This drops the residual case of selecting an extra
        // segment while a tool happens to be open.
        const operation: 'merge' | 'split' = tool === 'merge' ? 'merge' : 'split';
        if (operation === 'merge' && net >= 0) return;
        if (operation === 'split' && net <= 0) return;

        // The graph server's own acknowledgement already counted this one.
        if (Date.now() - lastAckAt < EDIT_DEBOUNCE_MS + 5000) return;
        // On a graph layer every real edit is acknowledged with the server's
        // operation id, and that acknowledgement is what gets counted. This
        // watcher can then only be wrong: a second copy of an edit whose
        // segments settled more than eight seconds after its acknowledgement
        // (a batch of merges), or a cell added to or taken out of the view
        // while a tool was open. From 2026-10-06 to 10-10 it wrote 335 of the
        // 3,338 edits logged, none with an operation id. It now counts only
        // where there is no graph layer to acknowledge anything.
        if (lastAckAt > 0 || hasGraphLayer()) return;
        countEdit(operation, {
          segment_before: removedIds,
          segment_after: addedIds,
          metadata: { net_segment_change: net, diff: 1 },
        });
      }, EDIT_DEBOUNCE_MS);
    };

    visibleSegs.changed.add(handler);
    segEditCleanup = () => {
      visibleSegs.changed.remove(handler);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }

  function initializeWithViewer(v: Viewer) {
    console.log("initializeWithViewer");
    viewer = v;

    // set default values in settings
    viewer.chunkQueueManager.capacities.gpuMemory.sizeLimit.value = 2e9;
    viewer.chunkQueueManager.capacities.systemMemory.sizeLimit.value = 3e9;
    // 2D detail: a saved view can carry crossSectionRenderScale above 1, which draws the image
    // at a coarser level, counts as fully loaded and never sharpens (seen on practice views,
    // 2026-10-07, src/practice.ts). Clamp it to 1 on every image layer, now and whenever a
    // layer appears or the value changes (Cell Library jumps, shared links, curated states).
    // The player may choose a coarser detail in Settings (imageDetail, Krzysztof
    // 2026-10-15): then every image layer is held at THAT value instead, and
    // still no saved view can choose for them.
    const chosenDetail = (): number => {
      try { const d = useUserPreferencesStore().prefs.imageDetail; return d === 2 || d === 4 ? d : 1; } catch { return 1; }
    };
    const hookedManaged = new WeakSet<object>();
    const clampedTargets = new WeakSet<object>();
    const detailTargets: { name: string; t: any }[] = [];
    const clampImageDetail = () => {
      for (const ml of v.layerManager.managedLayers as any[]) {
        if (!hookedManaged.has(ml) && ml.layerChanged) {
          hookedManaged.add(ml);
          ml.layerChanged.add(clampImageDetail);
        }
        const layer = ml.layer;
        if (!layer || layer.constructor?.type !== 'image') continue;
        const t = layer.sliceViewRenderScaleTarget;
        if (!t || clampedTargets.has(t)) continue;
        clampedTargets.add(t);
        detailTargets.push({ name: ml.name, t });
        const clamp = () => {
          const want = chosenDetail();
          if (want > 1 ? t.value !== want : t.value > 1) {
            console.info(`[2D] image layer "${ml.name}" asked for detail ${t.value}; set to ${want}`);
            t.value = want;
          }
        };
        t.changed.add(clamp);
        clamp();
      }
    };
    v.layerManager.layersChanged.add(clampImageDetail);
    clampImageDetail();
    // A change of the setting (or the account's copy arriving) applies at once.
    setTimeout(() => watch(() => chosenDetail(), want => {
      for (const { t } of detailTargets) if (want > 1 ? t.value !== want : t.value > 1) t.value = want;
    }), 0);
    // Mobile defaults to fullscreen 3D (Amy 2026-08-18): split screen is
    // reachable only from a share link that carries its own layout or the
    // corner view toggle. A layout named in the URL hash wins on any device.
    if (isMobileRef.value) {
      // Captured ONCE at boot, before neuroglancer starts mirroring the
      // full state (which always contains "layout") back into the hash.
      // Only an inline share link, whose hash carries a literal layout key
      // at page load, keeps its own view; everything else on a phone is
      // fullscreen 3D, including curated dataset states that arrive later
      // as #!url pointers (their remote fetch resolves after the hash has
      // been rewritten, so the hash cannot be consulted at restore time).
      const bootHadExplicitLayout = window.location.hash.includes('layout');
      if (!bootHadExplicitLayout) {
        viewer.layout.restoreState('3d');
      }
      const origRestore = viewer.state.restoreState.bind(viewer.state);
      (viewer.state as any).restoreState = (obj: any) => {
        if (obj && typeof obj === 'object' && !Array.isArray(obj) &&
            !bootHadExplicitLayout) {
          if ((obj as any).layout !== undefined) obj = {...obj, layout: '3d'};
          // The seg side panel also stays closed on phones.
          if ((obj as any).selectedLayer !== undefined) {
            obj = {...obj, selectedLayer: {...(obj as any).selectedLayer, visible: false}};
          }
        }
        origRestore(obj);
      };
    } else if (!BOOT_HASH.includes('layout')) {
      // The hash was already restored in setupViewer, so a refresh keeps the
      // layout the page was on instead of resetting to split 2D/3D.
      viewer.layout.restoreState('xy-3d');
    }

    viewer.layerManager.layersChanged.add(refreshLayers);
    refreshLayers();
  }

  function parseHexColor(hex: string): {r: number, g: number, b: number} | null {
    const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
    if (!m) return null;
    const v = parseInt(m[1], 16);
    return { r: (v >> 16) & 0xff, g: (v >> 8) & 0xff, b: v & 0xff };
  }

  // Skip the curated state-URL load for pinky_sandbox while a tutorial is active —
  // Tutorial 1 uses pinky_sandbox and drives its own state via per-step `state:` fields.
  // Lengths below are tracked manually; bump if a tutorial grows past these.
  function isUserInActiveTutorial(): boolean {
    try {
      const active = parseInt(localStorage.getItem('nge-active-tutorial') ?? '1');
      const stepKey = active === 1 ? 'nge-tutorial-step' : `nge-tutorial-${active}-step`;
      // store-pyr defaults: T1 step starts at 0 (in-progress), the others at -1 (not started).
      const defaultStep = active === 1 ? '0' : '-1';
      const step = parseInt(localStorage.getItem(stepKey) ?? defaultStep);
      // The real step counts, from Tutorial.vue. The old fixed table (T2 = 24)
      // went stale as tutorials grew, so a finished tutorial could read as
      // under way and the Sandbox landed without its cells.
      const live = (window as any).__ngeTutorialLengths as Record<string, number> | undefined;
      const length = live?.[active] ?? ({ 1: 18, 2: 24, 3: 30 } as Record<number, number>)[active] ?? 30;
      return step >= 0 && step < length;
    } catch { return false; }
  }

  /** keepHash: load in place, never redirect to a curated or start view (the
   *  boot fallback, when the address already holds the player's own view).
   *  forJump: the switch is on the way to one cell (a chat link, the Cell
   *  Library, My cells), so the dataset arrives bare, with no curated or
   *  saved start view and no starter cells, and in place, so the jump that
   *  follows is not lost to a reload (Ames 2026-10-14: "if I jump via chat
   *  it doesn't need to load the default segments, just the segment I
   *  jumped to, true of any and all jumps"). */
  async function selectLayers(layers: any[], opts: {keepHash?: boolean; forJump?: boolean} = {}) {
    if (!viewer) return;

    // Detect the target dataset (by segmentation layer name) BEFORE restoring,
    // so we can short-circuit to a curated state URL if one is configured.
    const targetSegName = (() => {
      for (const l of layers as any[]) {
        if (l && l.type === 'segmentation' && typeof l.name === 'string') return l.name;
      }
      return '';
    })();
    const dsCfgEarly = targetSegName ? getDatasetCaveConfig(targetSegName) : undefined;
    const skipStateUrl = dsCfgEarly?.skipStateUrlIfTutorialActive && isUserInActiveTutorial();
    // Settings > Switching datasets: the player's own start view wins, then
    // "without starter cells" skips the curated view, else the curated view.
    const startPrefs = useUserPreferencesStore().prefs;
    const ownHash = targetSegName && !opts.forJump ? startViewHash(startPrefs.datasetStartViews?.[targetSegName]) : '';
    const bareSwitch = !!startPrefs.datasetBareSwitch || !!opts.forJump;
    const curatedUrl = ownHash ? window.location.origin + window.location.pathname + ownHash
      : bareSwitch ? '' : (dsCfgEarly?.defaultStateUrl || '');
    if (curatedUrl && !skipStateUrl && !opts.keepHash) {
      // Apply via hash-only navigation when same-origin (so dev server doesn't bounce
      // to production). Neuroglancer's hashchange handler picks up the new state URL
      // and fetches+applies it. If the configured URL is on a different origin, the
      // hash-only swap still works because the hash itself contains the full state URL.
      const hashIdx = curatedUrl.indexOf('#');
      const hashPart = hashIdx >= 0 ? curatedUrl.slice(hashIdx) : '';
      console.info(`[layers] Loading ${ownHash ? 'your saved start view' : 'curated view'} for ${targetSegName}`);
      if (hashPart) {
        // Reload-loop guard: if the hash ALREADY points at the curated state,
        // reloading again cannot help. Without this, a curated state that
        // fails to load (e.g. middleauth not granted in this browser) left
        // activeLayers empty, the boot auto-select called selectLayers again,
        // and the page reloaded forever.
        if (window.location.hash === hashPart) {
          console.warn('[layers] curated state already in hash but layers empty — not reloading again');
          return;
        }
        window.location.hash = hashPart;
        // Force a reload so all layers/state are re-initialized cleanly from the saved URL.
        window.location.reload();
      } else {
        window.location.assign(curatedUrl);
      }
      return;
    }

    viewer.layerSpecification.restoreState(layers);
    viewer.navigationState.reset();

    const segmentationLayer = viewer.layerManager.managedLayers.filter(
        (x) => x.layer instanceof SegmentationUserLayer
    )[0];
    if (segmentationLayer) {
      const segmentationLayerName = segmentationLayer.name;
      const SETTINGS = DEFAULT_SETTINGS[segmentationLayerName]
      const dsCfg = getDatasetCaveConfig(segmentationLayerName);
      // A layer name missing from default-settings.json used to throw right
      // here, silently killing the rest of the switch (camera, default
      // segments): that is how MICrONS Live arrived with nothing selected.
      if (!SETTINGS) {
        console.warn(`[layers] No DEFAULT_SETTINGS entry for ${segmentationLayerName}; using dataset-config defaults only`);
      }
      if (SETTINGS) {
        viewer!.coordinateSpace.restoreState({
          x: [SETTINGS.dimensions[0], "m"],
          y: [SETTINGS.dimensions[1], "m"],
          z: [SETTINGS.dimensions[2], "m"],
        });
      }

      // Prefer dataset-config defaultPosition when set; fall back to DEFAULT_SETTINGS.position.
      // SETTINGS.position is an array of numbers in the JSON config; parsePositionString
      // is only used as a last-resort string parser for unexpected shapes.
      let position: Float32Array | undefined;
      if (dsCfg.defaultPosition) {
        position = Float32Array.from(dsCfg.defaultPosition);
      } else if (SETTINGS && Array.isArray(SETTINGS.position)) {
        position = Float32Array.from(SETTINGS.position);
      } else if (SETTINGS && typeof SETTINGS.position === 'string') {
        position = parsePositionString(SETTINGS.position, 3);
      }
      if (position !== undefined) {
        viewer!.navigationState.position.value = position;
      }
      if (SETTINGS) {
        viewer!.crossSectionScale.value = SETTINGS.crossSectionScale;
        viewer!.projectionScale.value = SETTINGS.projectionScale;
        viewer!.projectionOrientation.restoreState(SETTINGS.projectionOrientation);
      }

      // Add default segments + apply per-segment colors so the user lands on a visible cell.
      // Re-applied in a setTimeout below because layer mount fires async callbacks that
      // can clear the visible set + recenter the camera right after we set them.
      const applyDefaults = () => {
        if (position !== undefined) {
          viewer!.navigationState.position.value = position;
        }
        if (bareSwitch || !dsCfg.defaultSegments?.length) return;
        try {
          const segLayer = segmentationLayer.layer as SegmentationUserLayer;
          const groupState = segLayer.displayState.segmentationGroupState.value;
          const colorGroupState = segLayer.displayState.segmentationColorGroupState.value;
          for (const idStr of dsCfg.defaultSegments) {
            const segId = Uint64.parseString(idStr);
            if (!groupState.visibleSegments.has(segId)) {
              groupState.visibleSegments.add(segId);
            }
            const colorHex = dsCfg.segmentColors?.[idStr];
            const rgb = colorHex ? parseHexColor(colorHex) : null;
            if (rgb) {
              // neuroglancer packs as 0xBBGGRR.
              const packed = rgb.r | (rgb.g << 8) | (rgb.b << 16);
              setStatedColor(colorGroupState.segmentStatedColors, segId, packed);
            }
          }
          console.info(`[layers] Applied ${dsCfg.defaultSegments.length} default segment(s) for ${segmentationLayerName}`);
        } catch (e) {
          console.warn('[layers] Failed to apply default segments:', e);
        }
      };
      applyDefaults();
      // Re-apply once the layer's async mount has settled.
      setTimeout(applyDefaults, 200);
    }
  }

  /**
   * Derives the CAVE server base URL from the active neuroglancer state.
   * Priority:
   *   1. middleauth+ layer URL auto-extraction (production)
   *   2. EYEWIRE_II_CAVE_CONFIG.caveServerByDataset keyed by layer name (dev)
   *   3. EYEWIRE_II_CAVE_CONFIG.caveServerOverride (last resort)
   */
  function getCaveServerUrl(): string {
    if (!viewer) return EYEWIRE_II_CAVE_CONFIG.caveServerOverride;

    // 1. Extract host from the graphene segmentation layer's URL.
    // Only match graphene:// — the EM image layer also uses middleauth+
    // (with precomputed:// prefix) and matching that returns a malformed URL
    // that lightbulb_service tries to fetch and chokes on.
    for (const ml of viewer.layerManager.managedLayers) {
      const url = ml.layer?.dataSources?.[0]?.spec?.url ?? '';
      if (url.startsWith('graphene://middleauth+')) {
        const clean = url.replace('graphene://middleauth+', '');
        try {
          const u = new URL(clean);
          return `${u.protocol}//${u.host}${u.pathname.split('/segmentation')[0]}`;
        } catch { /* ignore */ }
      }
    }

    // 2. Fall back to per-dataset config
    for (const ml of viewer.layerManager.managedLayers) {
      const layerName = ml.name ?? '';
      const fromConfig = EYEWIRE_II_CAVE_CONFIG.caveServerByDataset?.[layerName];
      if (fromConfig) return fromConfig;
    }

    // 3. Global override
    return EYEWIRE_II_CAVE_CONFIG.caveServerOverride;
  }

  // A tutorial can rewrite a saved view before it loads (setStateRewriter;
  // none does today).
  async function loadState(url: string | Record<string, any>) {
    if (!viewer) return;
    try {
      // A state object (inline in a tutorial step) loads as is; a string is
      // a state-server link to fetch.
      let response: any;
      if (typeof url === 'string') {
        const {url: fetchUrl, credentialsProvider} = parseSpecialUrl(url, defaultCredentialsManager);
        response = await cancellableFetchSpecialOk(credentialsProvider, fetchUrl, {}, responseJson);
      } else {
        response = JSON.parse(JSON.stringify(url));
      }
      if (stateRewriter) {
        try { response = await stateRewriter(response); } catch (e) { console.warn('loadState: rewrite failed (non-fatal):', e); }
      }
      // A Sandbox view saved far out opens with its 2D view close in.
      response = closeInSandboxView(response);
      // Set layout first to avoid localPositionValid crashes during layout transitions
      // Mobile: curated views open fullscreen 3D like everything else.
      if (isMobileRef.value) response.layout = '3d';
      if (response.layout) {
        const layoutName = typeof response.layout === 'string'
          ? response.layout
          : response.layout.type;
        if (layoutName) {
          try {
            viewer!.layout.container.setSpecification(layoutName);
          } catch (e) {
            console.warn('loadState: setSpecification error (non-fatal):', e);
          }
          await new Promise(r => requestAnimationFrame(r));
        }
      }
      viewer!.state.restoreState(response);
    } catch (e) {
      console.error('loadState failed:', e);
    }
  }

  return {initializeWithViewer, activeLayers, selectLayers, getCaveServerUrl, loadState};
});

// ─── User Stats Store ────────────────────────────────────────────────────────
// Populated by the external user-profile repo via setStats().
// Call `useUserStatsStore().setStats({...})` from the profile integration to
// wire in live edit counts and cells-submitted totals.

export interface UserStats {
  editsToday: number;
  mergesToday: number;
  splitsToday: number;
  editsThisWeek: number;
  mergesThisWeek: number;
  splitsThisWeek: number;
  editsAllTime: number;
  mergesAllTime: number;
  splitsAllTime: number;
  cellsSubmitted: number;
  // Monthly stats
  editsThisMonth: number;
  mergesThisMonth: number;
  splitsThisMonth: number;
  // Streak — consecutive calendar days with ≥1 activity (edits or cell completions)
  currentStreak: number;
  longestStreak: number;
  lastEditDate: string;       // ISO date string e.g. "2026-03-01"
  // Days track — every day the player has shown up, ever. Never resets.
  totalDays: number;
  // Community totals — dataset-wide aggregate from CAVE ChunkedGraph
  communityEditsThisWeek: number;
  communityEditsThisMonth: number;
}

const STATS_KEY = 'nge_stats_v1';
const DAILY_LOG_KEY = 'nge_daily_log_v1';

export interface DailyLogEntry {
  date: string;          // ISO date "2026-03-04"
  edits: number;
  merges: number;
  splits: number;
  cellsCompleted: number;
  questsCompleted: number;
}

export const useUserStatsStore = defineStore('userStats', () => {
  // Hydrate from localStorage so stats survive page refresh
  const saved = (() => {
    try {
      const raw = localStorage.getItem(STATS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();

  const stats: Ref<UserStats> = ref({
    editsToday: 0,
    mergesToday: 0,
    splitsToday: 0,
    editsThisWeek: 0,
    mergesThisWeek: 0,
    splitsThisWeek: 0,
    editsAllTime: 0,
    mergesAllTime: 0,
    splitsAllTime: 0,
    cellsSubmitted: 0,
    editsThisMonth: 0,
    mergesThisMonth: 0,
    splitsThisMonth: 0,
    currentStreak: 0,
    longestStreak: 0,
    lastEditDate: '',
    totalDays: 0,
    communityEditsThisWeek: 0,
    communityEditsThisMonth: 0,
    ...saved,
  });

  function persist() {
    try { localStorage.setItem(STATS_KEY, JSON.stringify(stats.value)); } catch {}
  }

  function setStats(partial: Partial<UserStats>) {
    Object.assign(stats.value, partial);
    // Guard: allTime must never be less than today/week/month
    const s = stats.value;
    s.editsAllTime   = Math.max(s.editsAllTime,   s.editsToday,  s.editsThisWeek,  s.editsThisMonth);
    s.mergesAllTime  = Math.max(s.mergesAllTime,  s.mergesToday, s.mergesThisWeek, s.mergesThisMonth);
    s.splitsAllTime  = Math.max(s.splitsAllTime,  s.splitsToday, s.splitsThisWeek, s.splitsThisMonth);
    s.editsThisWeek  = Math.max(s.editsThisWeek,  s.editsToday);
    s.editsThisMonth = Math.max(s.editsThisMonth, s.editsThisWeek);
    s.mergesThisWeek = Math.max(s.mergesThisWeek, s.mergesToday);
    s.mergesThisMonth= Math.max(s.mergesThisMonth,s.mergesThisWeek);
    s.splitsThisWeek = Math.max(s.splitsThisWeek, s.splitsToday);
    s.splitsThisMonth= Math.max(s.splitsThisMonth,s.splitsThisWeek);
    persist();
  }

  // ── Daily activity log (last 30 days) ───────────────────────────
  const dailyLog = ref<DailyLogEntry[]>([]);

  function loadDailyLog() {
    try {
      const raw = localStorage.getItem(DAILY_LOG_KEY);
      if (raw) dailyLog.value = JSON.parse(raw);
    } catch {}
  }

  function persistDailyLog() {
    try {
      // Keep only last 30 days
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 30);
      const cutoffStr = cutoff.toISOString().slice(0, 10);
      dailyLog.value = dailyLog.value.filter(d => d.date >= cutoffStr);
      localStorage.setItem(DAILY_LOG_KEY, JSON.stringify(dailyLog.value));
    } catch {}
  }

  function getTodayLog(): DailyLogEntry {
    const today = new Date().toISOString().slice(0, 10);
    let entry = dailyLog.value.find(d => d.date === today);
    if (!entry) {
      entry = { date: today, edits: 0, merges: 0, splits: 0, cellsCompleted: 0, questsCompleted: 0 };
      dailyLog.value.push(entry);
    }
    return entry;
  }

  function logDailyEdit(type: 'merge' | 'split') {
    const entry = getTodayLog();
    entry.edits++;
    if (type === 'merge') entry.merges++;
    else entry.splits++;
    persistDailyLog();
  }

  function logDailyCellComplete() {
    const entry = getTodayLog();
    entry.cellsCompleted++;
    persistDailyLog();
  }

  function logDailyQuestComplete() {
    const entry = getTodayLog();
    entry.questsCompleted++;
    persistDailyLog();
  }

  loadDailyLog();

  /** Recalculate today/week/month stats from dailyLog to prevent stale accumulation. */
  function recalcFromDailyLog() {
    const now = new Date();
    const today = now.toISOString().slice(0, 10);

    // Start of current week (Monday) — matches the Mon–Sun range shown in WeeklyRecapPanel
    const todayUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const dayUTC = todayUTC.getUTCDay(); // 0 = Sun, 1 = Mon, …
    const diffToMon = dayUTC === 0 ? -6 : 1 - dayUTC;
    const monday = new Date(todayUTC);
    monday.setUTCDate(todayUTC.getUTCDate() + diffToMon);
    const weekStart = monday.toISOString().slice(0, 10);

    // First day of the current calendar month — matches the "April 2026" label
    const monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
      .toISOString().slice(0, 10);

    let editsToday = 0, mergesToday = 0, splitsToday = 0;
    let editsWeek = 0, mergesWeek = 0, splitsWeek = 0;
    let editsMonth = 0, mergesMonth = 0, splitsMonth = 0;

    for (const entry of dailyLog.value) {
      if (entry.date === today) {
        editsToday += entry.edits;
        mergesToday += entry.merges;
        splitsToday += entry.splits;
      }
      if (entry.date >= weekStart) {
        editsWeek += entry.edits;
        mergesWeek += entry.merges;
        splitsWeek += entry.splits;
      }
      if (entry.date >= monthStart) {
        editsMonth += entry.edits;
        mergesMonth += entry.merges;
        splitsMonth += entry.splits;
      }
    }

    // Only update today/week/month — preserve allTime from Supabase
    const s = stats.value;
    s.editsToday = editsToday;
    s.mergesToday = mergesToday;
    s.splitsToday = splitsToday;
    s.editsThisWeek = editsWeek;
    s.mergesThisWeek = mergesWeek;
    s.splitsThisWeek = splitsWeek;
    s.editsThisMonth = editsMonth;
    s.mergesThisMonth = mergesMonth;
    s.splitsThisMonth = splitsMonth;
    persist();
  }

  recalcFromDailyLog();

  // ── Edit event signal for UI celebrations ───────────────────────
  /** Brief reactive signal: set on merge/split, auto-clears after 2.5s. */
  const recentEditEvent = ref<{type: 'merge' | 'split'; ts: number} | null>(null);
  let editEventTimer: ReturnType<typeof setTimeout> | null = null;

  function signalEdit(type: 'merge' | 'split') {
    recentEditEvent.value = {type, ts: Date.now()};
    if (editEventTimer) clearTimeout(editEventTimer);
    editEventTimer = setTimeout(() => { recentEditEvent.value = null; }, 2500);
  }

  return {stats, setStats, dailyLog, logDailyEdit, logDailyCellComplete, logDailyQuestComplete, recentEditEvent, signalEdit};
});

// ─── User Preferences Store ───────────────────────────────────────────────────
// Persists flag emoji + bio to localStorage so they survive page reloads.

const PREFS_KEY = 'nge_prefs_v1';

export interface UserPreferences {
  flag: string;   // flag emoji e.g. "🇺🇸"
  bio: string;    // free-text, capped at 280 chars in the UI
  /** Which toolbar icons to show, in order. Empty = show all defaults. */
  toolbarIcons: string[];
  /** Icon ids that have already been auto-injected into this user's saved
   *  order (icons added after their prefs were first saved). Lets
   *  resolveToolbarOrder tell "pref predates the icon" apart from "user
   *  deliberately removed it", so removals stick. */
  toolbarIconsInjected?: string[];
  /** Mute chat: skip the green unread pip on the toolbar. Defaults
   *  to false (notifications on) when the key isn't set yet. */
  chatMuted?: boolean;
  /** Fade chat when you click away from it (quiet mode). Defaults to true;
   *  false keeps chat fully shown (Amy 2026-09-28). */
  chatFadeAway?: boolean;
  /** A thin bar down the left edge of your own chat messages, so they are
   *  easy to find (Krzysztof 2026-10-09, #84). Defaults to true; false
   *  switches it off (Ames 2026-10-10). */
  chatMarkMine?: boolean;
  /** Chat panel size in px, kept when you resize it (Ames 2026-09-30:
   *  "I have to reset it every time!"). */
  chatSize?: { w: number; h: number };
  /** EyeWire Radio: whether the music is on, and how loud (0 to 1). Follows
   *  the account, so it is the same on every computer (Ames 2026-10-05). */
  radio?: { on: boolean; volume: number };
  /** Show neuroglancer's "?" controls button in the top bar. Off by default
   *  (Amy 2026-09-30); the controls panel is still in the command palette. */
  showNgControlsButton?: boolean;
  /** Show neuroglancer's x y z corner in each view (it opens into the scale
   *  and depth panel on hover). Off by default (Ames 2026-10-27: "get rid of
   *  this"); the Neuroglancer box in Settings brings it back. */
  showXyzCorner?: boolean;
  /** What a completed cell shows: the full congratulations in the middle of
   *  the screen (the default), a small note at the top that does not cover
   *  the work, or nothing (Krzysztof Kruk 2026-10-11: the popup "might be
   *  distracting from the workflow"). */
  cellCompleteMessage?: 'full' | 'small' | 'off';
  /** Settings, "Achievement celebration" (toknow, report 239; Ames
   *  2026-10-25). False: no card and no confetti when one is earned; it is
   *  still in the Trophy Case and the bell. */
  achievementCelebration?: boolean;
  /** Settings: every notification in one list instead of My Pyr and Pyr
   *  Updates (Ames 2026-10-25). */
  notifOneList?: boolean;
  /** Settings, "Organize my notifications into categories": the bell's
   *  tabs (My Pyr, Pyr Updates, and Admin for admins). Off by default, so
   *  the bell is one feed (Ames 2026-10-27: "default it back to 1 feed and
   *  put an option to organize my notifs into categories in settings"). */
  notifCategories?: boolean;
  /** Settings, "Show Today in Pyr": false keeps the daily update out of the
   *  bell (Ames 2026-10-25). */
  showDailyDigest?: boolean;
  /** 2D image detail: 1 full (default), 2 half, 4 quarter, as neuroglancer's
   *  render scale target for image layers. Coarser loads far less on a slow
   *  connection (Krzysztof 2026-10-15). Saved views never set it; only this. */
  imageDetail?: 1 | 2 | 4;
  /** Where the 2D images load from (Ames 2026-10-22): 'auto' (the default)
   *  times both copies once and uses the faster, or the player picks the
   *  Americas or Asia. See src/util/image_source.ts. */
  imageSource?: 'auto' | 'americas' | 'asia';
  /** Keep your display settings (opacity, layout...) when the Cell Library
   *  loads a cell's view. Defaults to true (Amy 2026-09-30). */
  keepDisplayOnJump?: boolean;
  /** How the cell under the mouse is shown (wolfryder101 #181): 'spotlight' (new players), 'fade' (the classic look, existing players), 'pulse', 'hologram' ('wireframe' before), 'saturate', 'spotlight' or 'none'. */
  hoverStyle?: 'outline' | 'halo' | 'pulse' | 'hologram' | 'metallic' | 'wireframe' | 'saturate' | 'spotlight' | 'fade' | 'none';
  /** Set when hoverStyle was given (a new player's spotlight), not chosen; the
   *  account copy can still say they were here before. Local only. */
  hoverStyleAuto?: boolean;
  /** The pulse's colour, '#rrggbb', or '' for a tint of the cell's own (Nseraf). */
  pulseColor?: string;
  /** Classic layer tabs: left click hides or shows a layer, right click
   *  selects it (neuroglancer's own way). Defaults to false: left click
   *  selects, right click hides (Ames 2026-10-06). It also sets the number
   *  keys: on, 1 to 9 show and hide layers; off, a number makes its layer the
   *  active one and Shift + the number shows or hides it (Ames 2026-10-24). */
  classicLayerClicks?: boolean;
  /** Hide the EyeWire Radio speaker in the corner (Celia 2026-10-24).
   *  Defaults to false: it shows. */
  hideRadio?: boolean;
  /** Show how many steps Undelete can bring back, as a badge on its icon.
   *  Defaults to false: the icon alone (Ames 2026-10-07). */
  undeleteCounter?: boolean;
  /** Hide the Halloween pumpkin on the Sandbox cell (src/util/pumpkin.ts).
   *  Defaults to false: the pumpkin shows in season. */
  hidePumpkin?: boolean;
  /** The tab the Cell Library opens on (Nik 2026-10-09). Unset: My Cells. */
  cellLibraryStartTab?: 'mine' | 'available' | 'claimed' | 'all' | 'help' | 'links';
  /** Jumping to a cell ADDS it to the view instead of replacing what is
   *  shown. Defaults to false (replace). */
  jumpAddsToView?: boolean;
  /** Highlight mode's colours: the three built in ones (recoloured or not)
   *  and any the player added. Unset = the defaults. */
  highlightStyles?: { key: string; label: string; color: string }[];
  /** Notifications this player has already been given (newest 300 ids).
   *  "Stop sending at" only keeps a notification from reaching people who
   *  have not had it yet; whoever got it keeps it (Ames 2026-10-10). */
  notifDelivered?: number[];
  /** Offer "Pick up where you left off?" when a dataset opens. Defaults to true. */
  offerViewRestore?: boolean;
  /** Point annotation size multiplier, 1 to 4 (Amy 2026-09-30). Local only. */
  annotationSize?: number;
  /** Cut mode's red and blue points, as a multiple of the regular dot (Celia #177). */
  cutPointSize?: number;
  /** Point annotations drawn as Pyr gems in 3D. Defaults to true. */
  annotationGems?: boolean;
  /** Show Source and Rendering tabs on annotation layers. Defaults to false. */
  showAnnotationSetupTabs?: boolean;
  /** Show Source and Graph tabs on segmentation layers. Defaults to false. */
  showSegSetupTabs?: boolean;
  /** Optional datasets this player turned on (DatasetEntry.id, e.g. 'h01'). */
  extraDatasets?: string[];
  /** Switch datasets without the curated view or starter cells (faster). */
  datasetBareSwitch?: boolean;
  /** Segmentation layer name -> the player's own share link to open there. */
  datasetStartViews?: Record<string, string>;
  /** Mute help requests: hide the pending count on the Second Opinion toolbar
   *  icon. Defaults to false (badge shown) when the key isn't set yet. */
  helpMuted?: boolean;
  /** Segment list: a typed cell shows its segment ID on a second line under
   *  its label (Celia 2026-10-14). Off: the label alone, as since 2026-09-29. */
  segShowId?: boolean;
  /** The viewer's loading settings as this player set them in Viewer
   *  settings, put back whenever a new view resets them (src/util/viewer_perf.ts,
   *  Krzysztof 2026-10-15). Absent: neuroglancer's defaults. */
  viewerPerf?: ViewerPerf;
  /** Calm screen: no animations or glows in the interface (src/util/calm.ts,
   *  annkri #144, Ames 2026-10-16). Defaults to false. */
  calmScreen?: boolean;
  /** Settings, "Particles": a small burst where a Highlight, Tag, cut, merge
   *  or Find Path point lands (src/util/point_particles.ts, Ames 2026-11-02).
   *  On unless false. */
  pointParticles?: boolean;
  /** Extra large cursor over the 2D and 3D views (body.nge-cursor-xl,
   *  ng-override.css; Ames 2026-10-22, for a big screen). Defaults to false. */
  cursorXL?: boolean;
  /** Large text: Pyr's windows and the layer side panel drawn a fifth bigger
   *  (body.nge-large-text, src/large_text.css; Ames 2026-10-25, for older
   *  players). Defaults to false. */
  largeText?: boolean;
  /** The top bar hides until the mouse comes to the top edge of the window
   *  (ExtensionBar.vue, Nik #243, Ames 2026-10-25). Defaults to false. */
  hideTopBar?: boolean;
  /** Advanced: a click on the 2D image layer's tab opens its side panel, as
   *  in neuroglancer. Off (default): it shows or hides the 2D view. */
  imageLayerPanel?: boolean;
  /** Auto-show open scout tags on cells even when tag mode is closed.
   *  Defaults to true (ambient dots on); tag mode always shows them. */
  showScoutTags?: boolean;
  /** Top bar staging for new players (src/util/toolbar_staging.ts): whether
   *  this account is staged, and which icons have arrived. Absent: not decided. */
  toolbarStage?: { staged: boolean; revealed: string[]; visitDays: string[]; done: string[] };
}

export interface ViewerPerf { prefetch?: boolean; concurrentDownloads?: number; gpuMemoryLimit?: number; systemMemoryLimit?: number }

/** Settings: "Jumping to a cell adds it to my view" (off = replace). */
export function jumpAddsToView(): boolean {
  try { return useUserPreferencesStore().prefs.jumpAddsToView === true; } catch { return false; }
}

export const useUserPreferencesStore = defineStore('userPrefs', () => {
  const prefs: Ref<UserPreferences> = ref({ flag: '', bio: '', toolbarIcons: [], chatMuted: false, helpMuted: false });

  function load() {
    try {
      const raw = localStorage.getItem(PREFS_KEY);
      const stored = raw ? JSON.parse(raw) : {};
      Object.assign(prefs.value, stored);
      // 3D Hover (wolfryder101 #181): settle the style once. A browser with
      // settings in it already keeps the classic fade; a new one gets the
      // halo, marked as given, so the account copy can still say the player
      // was here before (pullFromAccount). Written now, so saving any other
      // setting later does not make a new player look like an old one.
      if (!prefs.value.hoverStyle) {
        prefs.value.hoverStyle = ngeDefaultHoverStyle(stored);
        if (prefs.value.hoverStyle === 'spotlight') prefs.value.hoverStyleAuto = true;
        localStorage.setItem(PREFS_KEY, JSON.stringify(prefs.value));
      }
    } catch { /* ignore parse errors */ }
  }

  function save(partial: Partial<UserPreferences>) {
    Object.assign(prefs.value, partial);
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs.value));
    pushToAccount();
  }

  // ── Follow the account across computers ──────────────────────────────────
  // These settings are also kept in Supabase user_settings (one private row
  // per player, owner only, through ewCommunityData). Flag and bio are not
  // here: they live on the public profile row. localStorage stays the fast
  // local copy, so the app works before sign in and if Supabase is down.
  const SYNCED: (keyof UserPreferences)[] = ['toolbarIcons', 'toolbarIconsInjected', 'chatMuted', 'chatFadeAway', 'chatMarkMine', 'chatSize', 'radio',
    'helpMuted', 'showScoutTags', 'segShowId', 'viewerPerf', 'imageDetail', 'imageSource', 'calmScreen', 'pointParticles', 'datasetBareSwitch', 'datasetStartViews', 'extraDatasets', 'highlightStyles', 'notifDelivered', 'cutPointSize', 'cursorXL', 'largeText', 'hideTopBar', 'imageLayerPanel', 'hoverStyle', 'pulseColor', 'toolbarStage', 'notifOneList', 'notifCategories', 'showDailyDigest'];
  const syncedPart = (src: any) => {
    const out: Record<string, unknown> = {};
    for (const k of SYNCED) if (src && src[k] !== undefined) out[k] = src[k];
    return out;
  };
  let pulledFor = '';
  /** True once the account's saved copy has been read (top bar staging waits for it). */
  const accountLoaded = ref(false);
  let pushTimer: ReturnType<typeof setTimeout> | null = null;

  function pushToAccount() {
    if (pushTimer) clearTimeout(pushTimer);
    pushTimer = setTimeout(async () => {
      // Not before the account copy has been read: a fresh browser would
      // otherwise overwrite the account's settings with its defaults.
      if (!pulledFor) return;
      try {
        const { error } = await supabase.from('user_settings')
          .upsert({ settings: syncedPart(prefs.value) }, { onConflict: 'user_id' });
        if (error) console.warn('[prefs] could not save settings to your account:', error.message);
      } catch (e) { console.warn('[prefs] could not save settings to your account:', e); }
    }, 800);
  }

  /**
   * Settings, "Restore all settings to default" (Ames 2026-10-15). Every
   * setting goes back to its default, on this computer and on the account.
   * Kept: the flag and bio (they are the profile, not settings) and the record
   * of which notifications were delivered (not a setting, and delivery is
   * never taken back). Resolves once the account copy is saved, so the page
   * can reload after it without the old settings coming back.
   */
  async function resetToDefaults(): Promise<void> {
    const kept = { flag: prefs.value.flag, bio: prefs.value.bio, notifDelivered: prefs.value.notifDelivered };
    prefs.value = { toolbarIcons: [], chatMuted: false, helpMuted: false, flag: kept.flag || '', bio: kept.bio || '',
      ...(kept.notifDelivered ? { notifDelivered: kept.notifDelivered } : {}) };
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs.value));
    if (pushTimer) { clearTimeout(pushTimer); pushTimer = null; }
    if (!pulledFor) return;
    const { error } = await supabase.from('user_settings').upsert({ settings: syncedPart(prefs.value) }, { onConflict: 'user_id' });
    if (error) throw new Error(error.message);
  }

  async function pullFromAccount(userId: string) {
    if (!userId || pulledFor === userId) return;
    try {
      const { data, error } = await supabase.from('user_settings').select('settings').maybeSingle();
      if (error) { console.warn('[prefs] could not read your saved settings:', error.message); return; }
      pulledFor = userId;
      accountLoaded.value = true;
      const remote = (data as any)?.settings;
      if (remote && typeof remote === 'object') {
        // What was delivered on this computer before the account copy
        // arrived is kept too: delivery is never taken back.
        const hadHere = prefs.value.notifDelivered || [];
        // A player who was here before, on a browser new to them: the halo
        // given to "a new player" here gives way to their classic fade.
        if (prefs.value.hoverStyleAuto && !remote.hoverStyle) remote.hoverStyle = 'fade';
        prefs.value.hoverStyleAuto = false;
        Object.assign(prefs.value, syncedPart(remote));
        if (hadHere.length) prefs.value.notifDelivered = [...new Set([...(prefs.value.notifDelivered || []), ...hadHere])].sort((a, b) => b - a).slice(0, 300);
        localStorage.setItem(PREFS_KEY, JSON.stringify(prefs.value));
      } else {
        prefs.value.hoverStyleAuto = false;
        pushToAccount(); // first sign in since this shipped: keep this browser's choices
      }
    } catch (e) { console.warn('[prefs] could not read your saved settings:', e); }
  }

  /** Flag and bio live on the public profile row, and this browser keeps a
   *  copy that the profile and Settings show. A browser new to the player
   *  started with an empty copy, so the country and bio looked deleted
   *  (AzureJay, report 229, first sign in on Firefox), and saving Settings
   *  there would have blanked them for real. The row's values fill the copy. */
  let profilePulledFor = '';
  async function pullProfile(userId: string) {
    if (!userId || profilePulledFor === userId) return;
    try {
      const { data, error } = await supabase.from('users').select('flag, bio').eq('id', userId).maybeSingle();
      if (error || !data) return;
      profilePulledFor = userId;
      const flag = typeof (data as any).flag === 'string' ? (data as any).flag : '';
      const bio = typeof (data as any).bio === 'string' ? (data as any).bio : '';
      let changed = false;
      if (flag && prefs.value.flag !== flag) { prefs.value.flag = flag; changed = true; }
      if (bio && prefs.value.bio !== bio) { prefs.value.bio = bio; changed = true; }
      if (changed) localStorage.setItem(PREFS_KEY, JSON.stringify(prefs.value));
    } catch (e) { console.warn('[prefs] could not read your profile:', e); }
  }

  load(); // hydrate from localStorage on store init
  // The cursor size follows the setting, on every computer the account uses.
  watch(() => prefs.value.cursorXL === true, on => { document.body?.classList.toggle('nge-cursor-xl', on); }, { immediate: true });
  watch(() => prefs.value.largeText === true, on => { document.body?.classList.toggle('nge-large-text', on); }, { immediate: true });
  watch(() => prefs.value.showXyzCorner === true, on => { document.body?.classList.toggle('nge-show-xyz', on); }, { immediate: true });
  // The renderer reads the hover style and pulse colour from these.
  watch(() => [prefs.value.hoverStyle, prefs.value.pulseColor], () => {
    ngeHoverStyle.value = ngeParseHoverStyle(prefs.value);
    ngePulseColor.value = ngeParsePulseColor(prefs.value.pulseColor);
    try { (window as any).viewer?.display?.scheduleRedraw(); } catch { /* no viewer */ }
  }, { immediate: true });
  // Another Pyr tab changed a setting: take it here too, so this tab's next
  // save does not put the old value back (Ames 2026-10-20, several tabs).
  window.addEventListener('storage', e => {
    if (e.key !== PREFS_KEY || !e.newValue) return;
    try { prefs.value = JSON.parse(e.newValue); } catch { /* ignore parse errors */ }
  });
  // Once signed in, the account's copy wins over this browser's.
  // Deferred a tick: the backend store may itself read these prefs while it
  // is being set up, and two stores creating each other would recurse.
  setTimeout(() => watch(() => useProofreadingBackendStore().userId, id => { if (id) { pullFromAccount(String(id)); void pullProfile(String(id)); } }, { immediate: true }), 0);
  return { prefs, save, resetToDefaults, accountLoaded };
});

// ─── Segment Annotation Store ─────────────────────────────────────────────────
// Tracks the currently-selected segment ID and its CAVE annotation status.
// Populated by main.ts (DOM observer) when a segment list entry appears.

export interface SegmentAnnotation {
  segId: string;
  isComplete: boolean;
  cellType: string;
  /** Classification system (e.g. 'RGC', 'AC') from cell_type_local schema. */
  classificationSystem?: string;
  annotationId?: number;
  cellTypeAnnotationId?: number;
  loading: boolean;
  error: string;
}

export const useSegmentAnnotationStore = defineStore('segAnnotation', () => {
  const activeSegId  = ref<string | null>(null);
  const caveUrl      = ref<string>('');
  const annotation   = ref<SegmentAnnotation | null>(null);

  function setActiveSegId(id: string | null, cave?: string) {
    activeSegId.value = id;
    if (cave !== undefined) caveUrl.value = cave;
    if (id === null) { annotation.value = null; return; }
    // Reset to loading state so the panel shows a spinner
    annotation.value = {
      segId: id, isComplete: false, cellType: '',
      loading: true, error: '',
    };
  }

  function setAnnotation(a: SegmentAnnotation | null) {
    annotation.value = a;
  }

  return { activeSegId, caveUrl, annotation, setActiveSegId, setAnnotation };
});

// ─── Cell History Store ──────────────────────────────────────────────────────
// Tracks every cell the user has completed, ID'd, or interacted with.
// Persists to localStorage so it survives page reloads.

const CELL_HISTORY_KEY = 'nge_cell_history_v1';

export interface CellHistoryEntry {
  segId: string;  // cached/display root ID (may be stale after edits)
  isComplete: boolean;
  cellType: string;
  /** Spatial anchor — the point-in-space claim coordinate. Primary key for matching. */
  claimPoint?: ClaimPoint;
  /** Viewer position at time of annotation — used for jump-to-cell. */
  position: [number, number, number];
  /** ISO timestamp of last update. */
  updatedAt: string;
  /** User-assigned nickname (displayed instead of segId when set). */
  nickname?: string;
  /** Favorite flag — favorite cells appear at the top of the list. */
  isFavorite?: boolean;
  /** Dataset/layer name this cell belongs to (for filtering). */
  dataset?: string;
}

export const useCellHistoryStore = defineStore('cellHistory', () => {
  const cells = ref<CellHistoryEntry[]>([]);

  function load() {
    try {
      const raw = localStorage.getItem(CELL_HISTORY_KEY);
      if (raw) cells.value = JSON.parse(raw);
    } catch { /* ignore parse errors */ }
  }

  function persist() {
    localStorage.setItem(CELL_HISTORY_KEY, JSON.stringify(cells.value));
  }

  /** Add or update a cell entry. Matches by claimPoint first, then segId. */
  function upsert(entry: Partial<CellHistoryEntry> & { segId: string }) {
    // Match by claimPoint (stable across edits) first, then by segId
    let idx = -1;
    if (entry.claimPoint) {
      idx = cells.value.findIndex(c =>
        c.claimPoint &&
        c.claimPoint[0] === entry.claimPoint![0] &&
        c.claimPoint[1] === entry.claimPoint![1] &&
        c.claimPoint[2] === entry.claimPoint![2],
      );
    }
    if (idx < 0) {
      idx = cells.value.findIndex(c => c.segId === entry.segId);
    }
    const now = new Date().toISOString();
    if (idx >= 0) {
      // Merge fields — don't overwrite existing data with empty values
      const existing = cells.value[idx];
      cells.value[idx] = {
        ...existing,
        ...entry,
        segId: entry.segId || existing.segId,  // update cached segId if provided
        cellType: entry.cellType || existing.cellType,
        position: entry.position || existing.position,
        claimPoint: entry.claimPoint || existing.claimPoint,
        dataset: entry.dataset || existing.dataset,
        updatedAt: now,
      };
    } else {
      cells.value.unshift({
        segId: entry.segId,
        isComplete: entry.isComplete ?? false,
        cellType: entry.cellType ?? '',
        claimPoint: entry.claimPoint,
        position: entry.position ?? [0, 0, 0],
        updatedAt: now,
        nickname: entry.nickname,
        isFavorite: entry.isFavorite,
        dataset: entry.dataset,
      });
    }
    // Sort: favorites first, then by most recent
    cells.value.sort((a, b) => {
      if (a.isFavorite && !b.isFavorite) return -1;
      if (!a.isFavorite && b.isFavorite) return 1;
      return b.updatedAt.localeCompare(a.updatedAt);
    });
    persist();
  }

  /** Toggle favorite status for a cell. */
  function toggleFavorite(segId: string) {
    const cell = cells.value.find(c => c.segId === segId);
    if (cell) {
      cell.isFavorite = !cell.isFavorite;
      // Re-sort
      cells.value.sort((a, b) => {
        if (a.isFavorite && !b.isFavorite) return -1;
        if (!a.isFavorite && b.isFavorite) return 1;
        return b.updatedAt.localeCompare(a.updatedAt);
      });
      persist();
    }
  }

  /** Set a nickname for a cell. */
  function setNickname(segId: string, name: string) {
    const cell = cells.value.find(c => c.segId === segId);
    if (cell) {
      cell.nickname = name.trim() || undefined;
      persist();
    }
  }

  /** Stamp `dataset` on every entry that has none (entries saved before cells
   *  carried a dataset tag). Returns how many changed; persists only then. */
  function assignUntagged(dataset: string): number {
    let n = 0;
    for (const c of cells.value) {
      if (!c.dataset) { c.dataset = dataset; n++; }
    }
    if (n) persist();
    return n;
  }

  /** Lookup a nickname by segment ID (used globally to replace segID display). */
  function getNickname(segId: string): string | undefined {
    const cell = cells.value.find(c => c.segId === segId);
    return cell?.nickname;
  }

  /** Navigate the viewer to a cell and select it.
   *  If `positionOverride` is given, jump there instead of the cell history position.
   *  The cell REPLACES what was on screen (Ames 2026-10-01: each jump used to
   *  add its cell, so a few jumps crowded the viewer with unrelated cells).
   *  `keep: true` adds it to what is already shown instead. */
  function jumpToCell(segId: string, positionOverride?: [number, number, number], opts: { keep?: boolean } = {}) {
    const keep = opts.keep || jumpAddsToView();
    const entry = cells.value.find(c => c.segId === segId);
    const viewer: any = (window as any)['viewer'];
    if (!viewer) return;
    // A quest on screen is saved and put away first (src/annotation_quests.ts
    // leaveQuestFor): its layers must not ride along to this cell.
    document.dispatchEvent(new CustomEvent('nge:leaving-for-cell', { detail: { segId } }));

    // Use override position, or claimPoint (stable anchor), or fall back to cell history
    const pos = positionOverride ?? entry?.claimPoint ?? entry?.position;
    if (pos && (pos[0] || pos[1] || pos[2])) {
      try {
        viewer.navigationState.position.value = Float32Array.from(pos);
      } catch (e) {
        console.warn('[cellHistory] Could not set position:', e);
      }
    }

    // Select the segment in the ACTIVE segmentation layer. Dataset switches
    // leave the old layer archived in managedLayers, and "first seg layer"
    // used to add the root to that archived layer instead.
    try {
      const segLayer = currentSegLayer();
      if (!segLayer?.layer) {
        console.warn('[cellHistory] No segmentation layer found');
      } else {
        const groupState = segLayer.layer.displayState?.segmentationGroupState?.value;
        if (groupState?.visibleSegments) {
          const seg = Uint64.parseString(segId);
          if (!keep && segId && segId !== '0') {
            const only = groupState.visibleSegments.size === 1 && groupState.visibleSegments.has(seg);
            if (!only) groupState.visibleSegments.clear();
          }
          if (!groupState.visibleSegments.has(seg)) {
            groupState.visibleSegments.add(seg);
          }
        } else {
          console.warn('[cellHistory] No segmentationGroupState on layer');
        }
      }
    } catch (e) {
      console.warn('[cellHistory] Could not select segment:', e);
    }
  }

  load(); // hydrate on store init

  // Seed with demo data if empty (real minnie65 segIDs)
  if (cells.value.length === 0) {
    const now = Date.now();
    const SEED: Array<Partial<CellHistoryEntry> & { segId: string }> = [
      { segId: '864691135445639570', isComplete: true, cellType: 'L2/3 Pyramidal',
        position: [120320, 103936, 21360], isFavorite: true },
      { segId: '864691135158265390', isComplete: true, cellType: 'L2/3 Pyramidal',
        position: [119040, 104448, 21280] },
      { segId: '864691135697404831', isComplete: false, cellType: 'L2/3 Pyramidal',
        position: [121600, 102912, 21440] },
      { segId: '864691136239802390', isComplete: true, cellType: 'L4 Spiny Stellate',
        position: [118784, 105984, 21200] },
      { segId: '864691135213953920', isComplete: false, cellType: 'L5 Thick-Tufted',
        position: [120064, 106496, 21120] },
      { segId: '864691135158296972', isComplete: true, cellType: 'L5 Slender-Tufted',
        position: [119808, 103168, 21520] },
      { segId: '864691134884741468', isComplete: false, cellType: '',
        position: [121856, 105472, 21040] },
      { segId: '864691135697405836', isComplete: true, cellType: 'PV+ Basket Cell',
        position: [118528, 104960, 21360], isFavorite: true },
      { segId: '864691135158267390', isComplete: false, cellType: 'SST+ Martinotti',
        position: [120576, 102400, 21600] },
      { segId: '864691136239803118', isComplete: true, cellType: 'VIP+ Bipolar',
        position: [119296, 105216, 21480] },
      { segId: '864691135158265120', isComplete: false, cellType: '',
        position: [121344, 104192, 21240] },
    ];
    SEED.forEach((s, i) => {
      cells.value.push({
        segId: s.segId,
        isComplete: s.isComplete ?? false,
        cellType: s.cellType ?? '',
        position: s.position ?? [0, 0, 0],
        updatedAt: new Date(now - i * 3600000 * 3).toISOString(),
        nickname: s.nickname,
        isFavorite: s.isFavorite,
      });
    });
    cells.value.sort((a, b) => {
      if (a.isFavorite && !b.isFavorite) return -1;
      if (!a.isFavorite && b.isFavorite) return 1;
      return b.updatedAt.localeCompare(a.updatedAt);
    });
    persist();
  }

  return { cells, upsert, jumpToCell, toggleFavorite, setNickname, getNickname, assignUntagged };
});

// ── Help requests (second-opinion) — backed by Supabase with realtime ────────

export interface HelpRequest {
  id: string;
  segId: string;
  position: [number, number, number];
  note: string;
  /** Issue category: extension, merge, black spill, doublecheck */
  issueType: string;
  createdAt: string;
  resolved: boolean;
  /** Cell type guess at time of request */
  cellType?: string;
  nickname?: string;
  /** Dataset/layer name this request belongs to (for cross-dataset filtering). */
  dataset?: string;
  /** Annotation layer attached to the ORIGINAL request (replies have their own). */
  annotationLayer?: string;
  /** Who created this request */
  userId?: string;
  userName?: string;
  /** Display name of the user who resolved this request */
  resolvedByName?: string;
  /** Public URL of an attached screenshot (Firebase Storage). */
  screenshotUrl?: string;
  /** Short link to the requester's view when they asked (Amy 2026-09-29). */
  viewUrl?: string;
  /** Thread of replies from the help_responses child table. Each reply keeps its
   *  OWN url / annotation layer / screenshot, so links accumulate instead of
   *  overwriting (the legacy response_* columns overwrote on every reply). */
  responses?: HelpResponse[];
}

export interface HelpResponse {
  id: string;
  userId?: string;
  userName?: string;
  note?: string;
  url?: string;
  annotationLayer?: string;
  screenshotUrl?: string;
  resolved?: boolean;
  createdAt: string;
}

function rowToHelpResponse(row: any): HelpResponse {
  return {
    id: row.id,
    userId: row.user_id ?? undefined,
    userName: row.user_name ?? undefined,
    note: row.note ?? undefined,
    url: row.url ?? undefined,
    annotationLayer: row.annotation_layer ?? undefined,
    screenshotUrl: row.screenshot_url ?? undefined,
    resolved: !!row.resolved,
    createdAt: row.created_at,
  };
}

/** Map Supabase row → HelpRequest interface */
function rowToHelpRequest(row: any): HelpRequest {
  return {
    id: row.id,
    segId: row.segment_id,
    position: (() => { try { return JSON.parse(row.position); } catch { return [0, 0, 0]; } })(),
    note: row.note ?? '',
    issueType: row.issue_type,
    createdAt: row.created_at,
    resolved: !!row.resolved,
    cellType: row.cell_type ?? undefined,
    nickname: row.nickname ?? undefined,
    dataset: row.dataset ?? undefined,
    userId: row.user_id ?? undefined,
    userName: row.user_name ?? undefined,
    resolvedByName: row.resolved_by_name ?? undefined,
    screenshotUrl: row.screenshot_url ?? undefined,
    annotationLayer: row.annotation_layer ?? undefined,
    viewUrl: row.view_url ?? undefined,
  };
}

/**
 * Two kinds of help request (Ames 2026-10-07: "help request for mentor mode,
 * including in chat, should be a different type of request than a normal
 * help request"):
 *   a normal request waits in the Help tab for a written reply;
 *   a LIVE request asks for someone to join the asker's view now (Mentor
 *   mode). It is said in chat with a Join button, and carries Join in the
 *   Help tab. It is marked by its issue type, so no new column is needed.
 */
export const LIVE_HELP = 'Live help';
/** How each kind reads in chat. Both carry the Open view button; only the
 *  live one carries Join (ChatPanel), so the usual way to help is to open
 *  the view, look, and answer in chat. */
export const HELP_CHAT_PREFIX = 'New help request';
export const LIVE_CHAT_PREFIX = '🤝 Live help wanted';
/** How a help request was worded in chat until 2026-10-13 (Ames: no SOS
 *  emoji). Messages already in chat still start with it. */
export const OLD_HELP_CHAT_PREFIX = '🆘 Help requested';
export function isLiveHelp(req: { issueType?: string | null } | null | undefined): boolean {
  return !!req && typeof req.issueType === 'string' && req.issueType.startsWith(LIVE_HELP);
}

export const useHelpRequestStore = defineStore('helpRequests', () => {
  const requests = ref<HelpRequest[]>([]);
  const pending = ref<HelpRequest[]>([]);
  let realtimeChannel: any = null;

  function refreshPending() {
    pending.value = requests.value.filter(x => !x.resolved);
  }

  /** Load all help requests from Supabase. */
  async function load() {
    try {
      const { data, error } = await supabase
        .from('help_requests')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) {
        console.warn('[helpRequests] Supabase load error:', error.message);
        loadFromLocalStorage(); // fallback
        return;
      }
      const reqs = (data ?? []).map(rowToHelpRequest);
      // Attach each request's reply thread from the help_responses child table.
      // Best-effort: if the query fails, requests still load, just without
      // their reply threads.
      try {
        const ids = reqs.map(r => r.id);
        if (ids.length) {
          const { data: resp } = await supabase
            .from('help_responses')
            .select('*')
            .in('request_id', ids)
            .order('created_at', { ascending: true });
          const byReq = new Map<string, HelpResponse[]>();
          for (const row of resp ?? []) {
            const list = byReq.get(row.request_id) ?? [];
            list.push(rowToHelpResponse(row));
            byReq.set(row.request_id, list);
          }
          for (const r of reqs) r.responses = byReq.get(r.id) ?? [];
        }
      } catch (e) {
        console.warn('[helpRequests] responses load failed:', e);
      }
      requests.value = reqs;
      refreshPending();
    } catch (e) {
      console.warn('[helpRequests] load failed, falling back to localStorage:', e);
      loadFromLocalStorage();
    }
  }

  /**
   * Add a reply to a help request as its own row in help_responses (so links /
   * screenshots accumulate). Optionally resolves the thread. Replaces the old
   * respond()/resolve() single-column writes for new replies.
   */
  async function addResponse(id: string, payload: {
    note?: string; url?: string; annotationLayer?: string;
    screenshotUrl?: string; resolve?: boolean;
  }) {
    const backend = useProofreadingBackendStore();
    const responderName = backend.chatHandle || backend.userName || backend.userEmail?.split('@')[0] || 'Anonymous';
    const row = {
      request_id: id,
      user_id: backend.userId || null,
      user_name: responderName,
      note: payload.note || null,
      url: payload.url || null,
      annotation_layer: payload.annotationLayer || null,
      screenshot_url: payload.screenshotUrl || null,
      resolved: !!payload.resolve,
    };
    const { data: inserted, error: insErr } = await supabase
      .from('help_responses').insert(row).select('*').single();
    if (insErr) { console.warn('[helpRequests] addResponse error:', insErr.message); return; }

    if (payload.resolve) {
      const { error: updErr } = await supabase.from('help_requests').update({
        resolved: true,
        resolved_at: new Date().toISOString(),
        resolved_by: backend.userId || null,
        resolved_by_name: responderName,
      }).eq('id', id);
      if (updErr) console.warn('[helpRequests] resolve-on-response error:', updErr.message);
      else dropSolvedReminder(id);
    }

    // Optimistic local update
    const r = requests.value.find(x => x.id === id);
    if (r) {
      r.responses = [...(r.responses ?? []), rowToHelpResponse(inserted)];
      if (payload.resolve) { r.resolved = true; r.resolvedByName = responderName; }
    }
    refreshPending();

    // Notify the requester that someone replied (skip self-replies).
    if (r?.userId && r.userId !== backend.userId) {
      const note = payload.note || (payload.screenshotUrl ? 'attached a screenshot' : '');
      const notePreview = note.slice(0, 120) + (note.length > 120 ? '...' : '');
      // The server builds the wording and checks the target asked for help.
      // With the request, the server also schedules tomorrow's "was it answered?".
      await secureWrite('notification.helpReply', { targetUserId: r.userId, segId: r.segId, note: notePreview, requestId: id })
        .catch((e: any) => console.warn('[helpRequests] notification error:', e?.message));
    }
  }

  /** Fallback: load from localStorage (migration period). */
  function loadFromLocalStorage() {
    try {
      const raw = localStorage.getItem('nge_help_requests_v1');
      if (raw) requests.value = JSON.parse(raw);
      refreshPending();
    } catch {}
  }

  /** Subscribe to realtime changes on help_requests table. */
  function subscribe() {
    if (realtimeChannel) return;
    realtimeChannel = supabase
      .channel('help_requests_realtime')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'help_requests',
      }, (payload: any) => {
        const req = rowToHelpRequest(payload.new);
        // Avoid duplicates (we may have already added it optimistically)
        if (!requests.value.find(r => r.id === req.id)) {
          requests.value.unshift(req);
          refreshPending();
        }
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'help_requests',
      }, (payload: any) => {
        const updated = rowToHelpRequest(payload.new);
        const idx = requests.value.findIndex(r => r.id === updated.id);
        if (idx >= 0) {
          // The row payload has no reply thread — keep the one already loaded.
          updated.responses = requests.value[idx].responses;
          requests.value[idx] = updated;
          refreshPending();
        }
      })
      .subscribe();
  }

  function unsubscribe() {
    if (realtimeChannel) {
      supabase.removeChannel(realtimeChannel);
      realtimeChannel = null;
    }
  }

  /** Add a new help request to Supabase. */
  /** A request is also said in chat (a normal one unless chat is false; a
   *  live one always), with its Open view button. Only a live one gets Join. */
  async function add(req: Omit<HelpRequest, 'id' | 'createdAt' | 'resolved'>, opts: { chat?: boolean } = {}) {
    const backend = useProofreadingBackendStore();
    // The requester's view, so helpers see exactly what they saw.
    const viewUrl = req.viewUrl ?? await mintShortStateLink().catch(() => null) ?? undefined;
    const row: Record<string, unknown> = {
      user_id: backend.userId || null,
      user_name: backend.userName || backend.userEmail?.split('@')[0] || 'Anonymous',
      segment_id: req.segId,
      position: JSON.stringify(req.position),
      note: req.note || '',
      issue_type: req.issueType,
      dataset: req.dataset || currentDatasetTag(),
      cell_type: req.cellType || null,
      nickname: req.nickname || null,
      screenshot_url: req.screenshotUrl || null,
      // Column added by supabase-help-responses-schema.sql.
      annotation_layer: req.annotationLayer || null,
    };
    if (viewUrl) row.view_url = viewUrl;

    let { data, error } = await supabase
      .from('help_requests')
      .insert(row)
      .select()
      .single();
    // Before supabase-help-view-url.sql runs the column does not exist: send
    // the request without the link rather than failing it.
    if (error && row.view_url && /view_url|column/i.test(error.message)) {
      delete row.view_url;
      ({ data, error } = await supabase.from('help_requests').insert(row).select().single());
    }

    if (error) {
      console.warn('[helpRequests] insert error:', error.message);
      // Optimistic local fallback
      requests.value.unshift({
        ...req,
        id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
        createdAt: new Date().toISOString(),
        resolved: false,
      });
    } else if (data) {
      const newReq = rowToHelpRequest(data);
      if (!requests.value.find(r => r.id === newReq.id)) {
        requests.value.unshift(newReq);
      }
      const live = isLiveHelp(req);
      if (live || opts.chat !== false) {
        try {
          const note = (req.note || '').replace(/\s+/g, ' ').trim().slice(0, 110);
          const issue = live ? req.issueType.slice(LIVE_HELP.length).replace(/^[\s·:]+/, '') : (req.issueType || '');
          const text = [live ? LIVE_CHAT_PREFIX : HELP_CHAT_PREFIX, issue ? `(${issue})` : '', note, row.view_url as string | undefined].filter(Boolean).join(' ');
          useChatStore().sendMessage(text);
        } catch (e) { console.warn('[helpRequests] could not post to chat:', e); }
      }
    }
    refreshPending();
  }

  /** Mark a help request as resolved in Supabase. Replies (with notes, links,
   *  screenshots) go through addResponse(); this only flips the thread flag. */
  /** Mark a request solved. True when the server took it. */
  async function resolve(id: string): Promise<boolean> {
    const backend = useProofreadingBackendStore();
    const resolverName = backend.userName || backend.userEmail?.split('@')[0] || 'Anonymous';

    const { error } = await supabase
      .from('help_requests')
      .update({
        resolved: true,
        resolved_at: new Date().toISOString(),
        resolved_by: backend.userId || null,
        resolved_by_name: resolverName,
      })
      .eq('id', id);

    if (error) {
      console.warn('[helpRequests] resolve error:', error.message);
    } else dropSolvedReminder(id);
    // Optimistic update
    const r = requests.value.find(x => x.id === id);
    if (r) {
      r.resolved = true;
      r.resolvedByName = resolverName;
    }
    refreshPending();
    return !error;
  }

  /** A solved request's "was it answered?" reminder, if it has not arrived
   *  yet, is deleted (functions/help-reminder.js). */
  function dropSolvedReminder(id: string) {
    secureWrite('help.dropReminder', { requestId: id }).catch(() => { /* the reminder's link says it is solved */ });
  }

  /** Remove a help request (delete from Supabase). */
  async function remove(id: string) {
    const { error } = await supabase
      .from('help_requests')
      .delete()
      .eq('id', id);

    if (error) {
      console.warn('[helpRequests] delete error:', error.message);
    }
    requests.value = requests.value.filter(x => x.id !== id);
    refreshPending();
  }

  // Initialize: load from Supabase + subscribe to realtime
  load();
  subscribe();

  /** A request the Help tab should open on and show (a reply notification's
   *  "Open in Help tab", Ames 2026-10-24). CellLibraryPanel clears it. */
  const focusId = ref<string | null>(null);
  return { requests, pending, add, resolve, addResponse, remove, refreshPending, load, subscribe, unsubscribe, focusId };
});

// ── Issue Tags (Scout tags) ────────────────────────────────────────────────
// One-click typed flags dropped at the crosshair: 'merger' (two cells wrongly
// joined) or 'missing_branch' (branch looks truncated). Scythes work the open
// queue from the Cell Library Tags tab. Backed by `issue_tags`.

export type IssueTagType = 'merger' | 'missing_branch' | 'other';

/** Merger subtypes, Amy's taxonomy. 'hairball' is real EyeWire slang. */
export type IssueTagSubtype = 'snip' | 'hairball' | 'twins' | 'debris';

export interface IssueTag {
  id: string;
  dataset?: string;
  segId?: string;
  position: number[];
  tagType: IssueTagType;
  subtype?: IssueTagSubtype;
  annotationLayer?: string;
  screenshotUrl?: string;
  note?: string;
  userId?: string;
  userName?: string;
  status: 'open' | 'resolved';
  resolvedById?: string;
  resolvedByName?: string;
  createdAt: string;
  /** When the tag was resolved (issue_tags.resolved_at). */
  resolvedAt?: string;
  /** 'human' (default) or 'model' for AI-seeded candidates. */
  source?: string;
  /** Model verify probability (0..1), set on source 'model' tags. */
  confidence?: number;
  /** Model payload for the proposed-split overlay (import-model-tags.mjs). */
  modelData?: {
    windowIdx?: number;
    batch?: string;
    /** Window center in microns (the model's native space). */
    centerUm?: number[];
    /** 25 sample points in microns relative to centerUm. */
    posRelUm?: number[][];
    /** 0/1 per sample point: which side of the proposed cut it falls on. */
    labels?: number[];
    spectralScore?: number;
    /** Future model taxonomy hook; drives the category icon in the AI tab. */
    category?: string;
  };
}

/** AI-seeded candidate (imported from the merge-error detection model). */
export function isModelTag(t: IssueTag): boolean {
  return t.source === 'model';
}

function rowToIssueTag(row: any): IssueTag {
  return {
    id: row.id,
    dataset: row.dataset ?? undefined,
    segId: row.segment_id ?? undefined,
    position: (() => { try { return JSON.parse(row.position); } catch { return [0, 0, 0]; } })(),
    tagType: row.tag_type,
    subtype: row.subtype ?? undefined,
    annotationLayer: row.annotation_layer ?? undefined,
    screenshotUrl: row.screenshot_url ?? undefined,
    note: row.note ?? undefined,
    userId: row.user_id ?? undefined,
    userName: row.user_name ?? undefined,
    status: row.status ?? 'open',
    resolvedById: row.resolved_by ?? undefined,
    resolvedByName: row.resolved_by_name ?? undefined,
    createdAt: row.created_at,
    resolvedAt: row.resolved_at ?? undefined,
    source: row.source ?? 'human',
    confidence: row.confidence ?? undefined,
    modelData: row.model_data ?? undefined,
  };
}

export const useIssueTagStore = defineStore('issueTags', () => {
  const tags = ref<IssueTag[]>([]);
  let realtimeChannel: any = null;

  const openTags = computed(() => tags.value.filter(t => t.status === 'open'));

  /** Name of the in-viewer annotation layer that mirrors open tags. */
  const TAG_LAYER_NAME = '⚑ Scout tags';
  const PIN_LAYER_NAME = '⚑ Scout pins';

  // ── Amy's Meshy 3D pin, planted at every open tag ────────────────────────
  // static/tags/pin.obj: tip at origin, 3um tall, nm units, body toward -y.
  // Instances are baked into ONE combined OBJ served as a data: URL (our
  // neuroglancer fork's parseUrl accepts data:), so the layer bar carries a
  // single "Scout pins" chip however many pins are planted.
  interface PinMesh { verts: Float32Array; faces: number[][]; colors: string[] }
  /** Parse one of our colored legacy-VTK pin assets (POINTS / POLYGONS /
   *  POINT_DATA SCALARS color float 3). Colors stay as raw text lines,
   *  they're pasted straight back out per instance. */
  function parsePinVtk(text: string): PinMesh | null {
    const lines = text.split('\n');
    const verts: number[] = [];
    const faces: number[][] = [];
    const colors: string[] = [];
    let i = 0;
    const n = lines.length;
    while (i < n && !lines[i].startsWith('POINTS')) i++;
    if (i >= n) return null;
    const nv = parseInt(lines[i].split(/\s+/)[1], 10);
    i++;
    for (let k = 0; k < nv && i < n; k++, i++) {
      const p = lines[i].trim().split(/\s+/);
      verts.push(+p[0], +p[1], +p[2]);
    }
    while (i < n && !lines[i].startsWith('POLYGONS')) i++;
    if (i >= n) return null;
    const nf = parseInt(lines[i].split(/\s+/)[1], 10);
    i++;
    for (let k = 0; k < nf && i < n; k++, i++) {
      const p = lines[i].trim().split(/\s+/);
      faces.push([+p[1], +p[2], +p[3]]);
    }
    while (i < n && !lines[i].startsWith('LOOKUP_TABLE')) i++;
    i++;
    for (let k = 0; k < nv && i < n; k++, i++) colors.push(lines[i].trim());
    if (!verts.length || !faces.length || colors.length !== nv) return null;
    return {verts: Float32Array.from(verts), faces, colors};
  }

  let pinMeshCache: Promise<{main: PinMesh; other: PinMesh | null; scythe: PinMesh | null} | null> | null = null;
  function loadPinMeshes() {
    if (!pinMeshCache) {
      pinMeshCache = Promise.all([fetch(pinVtkUrl), fetch(pinOtherVtkUrl), fetch(scytheVtkUrl)]).then(async ([r1, r2, r3]) => {
        if (!r1.ok) return null;
        const main = parsePinVtk(await r1.text());
        if (!main) return null;
        const other = r2.ok ? parsePinVtk(await r2.text()) : null;
        const scythe = r3.ok ? parsePinVtk(await r3.text()) : null;
        return {main, other, scythe};
      }).catch(() => null);
    }
    return pinMeshCache;
  }

  /** Deterministic small yaw per tag id, so a cluster of pins reads like
   *  flags planted by different hands rather than clones. */
  function pinYaw(id: string): number {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = ((h << 5) - h + id.charCodeAt(i)) | 0;
    return ((h % 628) / 628) * 2 * Math.PI;
  }

  /** Type tints: blue Cut, green Extend, purple Other (Amy's palette). */
  function pinTint(tagType?: string): [number, number, number] | null {
    if (tagType === 'merger') return [0.21, 0.71, 1.0];
    if (tagType === 'missing_branch') return [0.38, 0.88, 0.50];
    if (tagType === 'other') return [0.78, 0.59, 1.0];
    return null;
  }

  /** Colorize a mesh's baked vertex colors with a type tint: the bake's
   *  luminance keeps the sculpt's shading, the tint carries the meaning.
   *  Cached per mesh + tint. */
  const tintedColorCache = new Map<string, string[]>();
  function tintedColors(mesh: PinMesh, tint: [number, number, number] | null): string[] {
    if (!tint) return mesh.colors;
    const cacheKey = mesh.colors.length + ':' + tint.join(',');
    const hit = tintedColorCache.get(cacheKey);
    if (hit) return hit;
    const out = mesh.colors.map(line => {
      const p = line.split(/\s+/);
      const lum = 0.3 * (+p[0]) + 0.59 * (+p[1]) + 0.11 * (+p[2]);
      const k = 0.35 + 0.75 * lum;
      return `${Math.min(1, tint[0] * k).toFixed(3)} ${Math.min(1, tint[1] * k).toFixed(3)} ${Math.min(1, tint[2] * k).toFixed(3)}`;
    });
    tintedColorCache.set(cacheKey, out);
    return out;
  }

  /** The pins' up axis, chosen per rebuild: the signed world axis nearest
   *  the camera's current up vector. The pin meshes are modeled with -y as
   *  up, which is right for the cortex datasets but lay the monuments
   *  sideways in retina, whose world is rotated (Amy: "the scout tag icons
   *  are still sideways"). Snapping to an axis keeps placement deterministic
   *  and stable under small camera moves. Returns rows of a 3x3 matrix
   *  mapping pin-local coordinates to world. */
  function pinUpBasis(viewer: any): number[][] {
    const I = [[1, 0, 0], [0, 1, 0], [0, 0, 1]]; // native: up = -y
    try {
      const q = viewer?.perspectiveNavigationState?.pose?.orientation?.orientation;
      if (!q) return I;
      // world up = orientation * (0,-1,0)  (screen up under the y-down flip)
      const qx = q[0], qy = q[1], qz = q[2], qw = q[3];
      const vy = -1;
      let uvx = qy * 0 - qz * vy, uvy = qz * 0 - qx * 0, uvz = qx * vy - qy * 0;
      const uuvx = qy * uvz - qz * uvy, uuvy = qz * uvx - qx * uvz, uuvz = qx * uvy - qy * uvx;
      const ux = 0 + uvx * 2 * qw + uuvx * 2;
      const uy = vy + uvy * 2 * qw + uuvy * 2;
      const uz = 0 + uvz * 2 * qw + uuvz * 2;
      const ax = Math.abs(ux), ay = Math.abs(uy), az = Math.abs(uz);
      if (ay >= ax && ay >= az) {
        return uy <= 0 ? I : [[1, 0, 0], [0, -1, 0], [0, 0, -1]]; // 180 about x
      }
      if (ax >= az) {
        // rotate about z so local -y lands on +/-x
        return ux >= 0 ? [[0, -1, 0], [1, 0, 0], [0, 0, 1]]
                       : [[0, 1, 0], [-1, 0, 0], [0, 0, 1]];
      }
      // rotate about x so local -y lands on +/-z
      return uz >= 0 ? [[1, 0, 0], [0, 0, 1], [0, -1, 0]]
                     : [[1, 0, 0], [0, 0, -1], [0, 1, 0]];
    } catch { return I; }
  }

  /** Bake all pin instances (possibly different meshes per tag type) into
   *  one colored legacy-VTK, so the layer bar carries a single chip and the
   *  paint Meshy baked survives as per-vertex color. */
  function buildPinVtk(instances: {mesh: PinMesh; pos: number[]; yaw: number; lift: number; tint?: [number, number, number] | null}[], basis?: number[][]): string {
    const M = basis ?? [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
    let totalV = 0, totalF = 0;
    for (const inst of instances) { totalV += inst.mesh.verts.length / 3; totalF += inst.mesh.faces.length; }
    const lines: string[] = [
      '# vtk DataFile Version 3.0', 'nge scout pins', 'ASCII', 'DATASET POLYDATA',
      `POINTS ${totalV} float`,
    ];
    for (const inst of instances) {
      const {verts} = inst.mesh;
      const nv = verts.length / 3;
      const c = Math.cos(inst.yaw), s = Math.sin(inst.yaw);
      for (let i = 0; i < nv; i++) {
        const x = verts[i * 3], y = verts[i * 3 + 1], z = verts[i * 3 + 2];
        const rx = x * c + z * s, rz = -x * s + z * c;
        const ly = y - inst.lift; // lift raises along the pin's own up
        const wx = M[0][0] * rx + M[0][1] * ly + M[0][2] * rz;
        const wy = M[1][0] * rx + M[1][1] * ly + M[1][2] * rz;
        const wz = M[2][0] * rx + M[2][1] * ly + M[2][2] * rz;
        lines.push(`${(wx + inst.pos[0]).toFixed(0)} ${(wy + inst.pos[1]).toFixed(0)} ${(wz + inst.pos[2]).toFixed(0)}`);
      }
    }
    lines.push(`POLYGONS ${totalF} ${totalF * 4}`);
    let off = 0;
    for (const inst of instances) {
      for (const f of inst.mesh.faces) lines.push(`3 ${f[0] + off} ${f[1] + off} ${f[2] + off}`);
      off += inst.mesh.verts.length / 3;
    }
    lines.push(`POINT_DATA ${totalV}`, 'SCALARS color float 3', 'LOOKUP_TABLE default');
    for (const inst of instances) for (const c of tintedColors(inst.mesh, inst.tint ?? null)) lines.push(c);
    return lines.join('\n');
  }

  const MAX_PINS = 30;
  let lastPinKey = '';
  async function syncPinLayer(
      viewer: any,
      tagPoints: {id: string; point: number[]; tagType?: string}[], preview: number[] | null) {
    try {
      const removePins = () => {
        const managed = viewer.layerManager?.managedLayers?.find((l: any) => l.name === PIN_LAYER_NAME);
        if (managed) viewer.layerManager.removeManagedLayer(managed);
        lastPinKey = '';
      };
      if (!tagPoints.length && !preview) { removePins(); return; }

      // Voxel -> nm using the viewer's global coordinate space.
      const cs = viewer.coordinateSpace?.value;
      if (!cs?.scales?.length) return;
      const scaleNm = [0, 1, 2].map(i => cs.scales[i] * 1e9);
      const toNm = (p: number[]) => [p[0] * scaleNm[0], p[1] * scaleNm[1], p[2] * scaleNm[2]];

      const shown = tagPoints.slice(0, MAX_PINS);
      if (tagPoints.length > MAX_PINS) {
        console.info(`[issueTags] pin layer capped at ${MAX_PINS} of ${tagPoints.length} tags`);
      }
      const basis = pinUpBasis(viewer);
      const key = JSON.stringify(shown.map(t => [t.id, t.tagType ?? '', t.point.map(Math.round)])
        .concat(preview ? [['preview', '', preview.map(Math.round)]] : [])) + '|' + JSON.stringify(basis);
      const existing = viewer.layerManager?.managedLayers?.find((l: any) => l.name === PIN_LAYER_NAME);
      if (key === lastPinKey && existing) return;

      const meshes = await loadPinMeshes();
      if (!meshes) return;
      // Amy 2026-08-17: Cut tags plant Grim's crossed scythes in their
      // natural metal colors (the shape carries the meaning); Extend and
      // Other plant the simple location icon tinted green / purple. The
      // castle pin stays shipped for future use.
      const pinMesh = meshes.other ?? meshes.main;
      const meshFor = (tagType?: string) =>
        (tagType === 'merger' && meshes.scythe) ? meshes.scythe : pinMesh;
      const tintFor = (tagType?: string) =>
        (tagType === 'merger' && meshes.scythe) ? null : pinTint(tagType);
      const instances = shown.map(t => ({
        mesh: meshFor(t.tagType), pos: toNm(t.point), yaw: pinYaw(t.id), lift: 0,
        tint: tintFor(t.tagType),
      }));
      // The pending pin hovers half a micron above its point until Submit
      // plants it, a placed-but-not-saved tag literally hasn't landed yet.
      if (preview) instances.push({
        mesh: meshFor(previewType), pos: toNm(preview), yaw: 0, lift: 500, tint: tintFor(previewType),
      });

      const vtkText = buildPinVtk(instances, basis);
      const dataUrl = 'data:application/octet-stream;base64,' + btoa(vtkText);
      const spec = {
        type: 'mesh',
        source: 'vtk://' + dataUrl,
        name: PIN_LAYER_NAME,
        // Per-vertex baked colors from the Meshy texture.
        shader: 'void main() { emitRGB(color); }',
      };
      // Replace non-disruptively: only this layer is touched, never the
      // full viewer state (the restoreState route made segments vanish).
      if (existing) viewer.layerManager.removeManagedLayer(existing);
      const managed = makeLayer(viewer.layerSpecification, PIN_LAYER_NAME, spec);
      // NEVER serialize this layer: its source is a multi-hundred-KB data:
      // URL, and letting it into the state JSON bloated the URL hash on
      // every state change and broke state restoration (Amy's
      // localPositionValid crash). It is derived data, rebuilt from
      // Supabase on every load. A null spec means "do not serialize".
      (managed as any).toJSON = () => null;
      viewer.layerSpecification.add(managed);
      lastPinKey = key;
    } catch (e) {
      console.warn('[issueTags] pin layer sync failed:', e);
    }
  }

  /**
   * Mirror the current dataset's OPEN tags into a local annotation layer so
   * they're visible in 2D and 3D.
   *
   * The layer is CREATED once with makeLayer (only this layer, never the
   * full viewer state); every later change mutates the layer's
   * localAnnotations IN PLACE. The first version restored the whole viewer
   * state on every tag drop, which re-specced the graphene layer and made
   * the 3D segments blink out (Amy: "T+click makes the 3D seg go away").
   */
  function tagPointAnnotations() {
    const canon = currentDatasetTag();
    return openTags.value
      .filter(t => !isModelTag(t))
      // Strict: unstamped legacy tags do NOT render in every dataset.
      .filter(t => !!t.dataset && canonicalDataset(t.dataset) === canon)
      .filter(t => t.position?.length === 3)
      .map(t => ({
        id: t.id,
        point: t.position,
        tagType: t.tagType,
        description: `${t.tagType === 'merger' ? 'Cut' : t.tagType === 'missing_branch' ? 'Extend' : 'Other'}${t.note ? ': ' + t.note : ''} (${t.userName || 'anon'})`,
      }));
  }

  /** A placed-but-not-submitted point, shown in the layer so "Place by
   *  click" gives visible feedback without saving anything yet. */
  let previewPoint: number[] | null = null;
  let previewType: string | undefined;
  function setTagPreview(pos: number[] | null, tagType?: string) {
    previewPoint = pos && pos.length === 3 ? [...pos] : null;
    previewType = tagType;
    syncTagLayer();
  }

  /** True while the tag mode panel is open; the panel always shows the
   *  layer, whatever the ambient showScoutTags preference says. */
  const tagModeActive = ref(false);
  function setTagModeActive(v: boolean) {
    tagModeActive.value = v;
    syncTagLayer();
  }

  function syncTagLayer() {
    syncAiLayer();
    try {
      const viewer: any = (window as any)['viewer'];
      if (!viewer?.state) return;
      // The tag layers exist ONLY while Scout Tag mode is open (Amy
      // 2026-09-28: they were loading by default for users who had once
      // switched on the old "show tags on the map" preference). That saved
      // preference is deliberately ignored now.
      if (!tagModeActive.value) {
        for (const name of [TAG_LAYER_NAME, PIN_LAYER_NAME]) {
          const stale = viewer.layerManager?.managedLayers?.find((l: any) => l.name === name);
          if (stale) viewer.layerManager.removeManagedLayer(stale);
        }
        lastPinKey = '';
        return;
      }
      const tagPoints = tagPointAnnotations();
      void syncPinLayer(viewer, tagPoints, previewPoint);
      const points: { id: string; point: number[]; description: string }[] = [...tagPoints];
      if (previewPoint) {
        points.push({ id: 'nge-tag-preview', point: previewPoint, description: 'Pending tag, hit Submit' });
      }
      const managed = viewer.layerManager?.managedLayers?.find((l: any) => l.name === TAG_LAYER_NAME);

      // Subtle guide dots: the 3D pins are the tag's face (Amy: the big
      // blue dots were covering the cool 3D icons). Small and translucent,
      // they mark the spot in the 2D panes and whisper in the projection.
      const TAG_SHADER = 'void main() {\n' +
        '  setColor(vec4(0.21, 0.71, 1.0, 0.55));\n' +
        '  setPointMarkerSize(7.0);\n' +
        '  setPointMarkerBorderWidth(1.0);\n' +
        '  setPointMarkerBorderColor(vec4(0.85, 0.97, 1.0, 0.5));\n' +
        '}\n';

      if (!managed) {
        if (!points.length) return;
        // Add only this layer, like the pin layer. The old one-time route
        // restored the WHOLE viewer state, which re-specced every layer: on
        // MEC (hc.himc-cave.com) re-creating the graphene layer failed in the
        // middleauth credentials step ("reading 'generation'") and the view
        // went black with every layer gone (Ames 2026-09-29).
        const managed = makeLayer(viewer.layerSpecification, TAG_LAYER_NAME, {
          type: 'annotation',
          source: 'local://annotations',
          annotations: points.map(p => ({ ...p, type: 'point' })),
          annotationColor: '#35b5ff',
          shader: TAG_SHADER,
        });
        viewer.layerSpecification.add(managed);
        return;
      }

      // In-place update: clear + re-add on the layer's local source.
      const userLayer: any = managed.layer;
      // Layers created before the shader existed get it retrofitted here.
      try {
        const shaderState = userLayer?.annotationDisplayState?.shader;
        if (shaderState && shaderState.value !== TAG_SHADER) shaderState.restoreState(TAG_SHADER);
      } catch {}
      const src = userLayer?.localAnnotations;
      if (!src) return;
      src.clear();
      quietly(() => { for (const p of points) {
        src.add({ id: p.id, type: 0 /* AnnotationType.POINT */, point: Float32Array.from(p.point), properties: [], description: p.description }, true);
      } });
    } catch (e) {
      console.warn('[issueTags] tag layer sync failed:', e);
    }
  }

  // ── AI candidates (model-seeded tags) ────────────────────────────────────
  // Suspect windows from the merge-error detection model render on their own
  // layer, tinted by confidence: cool blue near the model's 0.2 threshold up
  // to hot orange at 1.0. Zoomed out the layer reads as a heat map of trouble
  // spots; each marker is still an individual candidate.

  const AI_LAYER_NAME = '🤖 AI candidates';
  // Amy's palette: cool blue (#4a9eff, the app accent) rising to golden
  // yellow (#FFD700) at full confidence. Orange is banned.
  // Hollow rings, not discs: a filled marker sits exactly on top of the
  // error it points at (Amy: "the markers are covering up the spot").
  const AI_SHADER = 'void main() {\n' +
    '  float c = clamp((prop_conf() - 0.2) / 0.8, 0.0, 1.0);\n' +
    '  vec3 cold = vec3(0.29, 0.62, 1.0);\n' +
    '  vec3 hot = vec3(1.0, 0.84, 0.0);\n' +
    '  setColor(vec4(mix(cold, hot, c), 0.08));\n' +
    '  setPointMarkerSize(11.0 + 8.0 * c);\n' +
    '  setPointMarkerBorderWidth(2.5);\n' +
    '  setPointMarkerBorderColor(vec4(mix(cold, hot, c), 0.95));\n' +
    '}\n';

  /** AI tab's layer toggle; ambient showScoutTags still gates everything. */
  const aiLayerOn = ref(true);
  function setAiLayerOn(v: boolean) {
    aiLayerOn.value = v;
    syncAiLayer();
  }

  function aiPointAnnotations() {
    const canon = currentDatasetTag();
    return openTags.value
      .filter(isModelTag)
      .filter(t => !t.dataset || canonicalDataset(t.dataset) === canon)
      .filter(t => t.position?.length === 3)
      .map(t => ({
        id: t.id,
        point: t.position,
        conf: t.confidence ?? 1,
        description: `AI candidate ${Math.round((t.confidence ?? 1) * 100)}%${t.segId ? ' on …' + t.segId.slice(-6) : ''}`,
      }));
  }

  function removeLayerByName(viewer: any, name: string) {
    const managed = viewer.layerManager?.managedLayers?.find((l: any) => l.name === name);
    if (managed) viewer.layerManager.removeManagedLayer(managed);
  }

  /** Non-disruptive annotation layer creation (same route as the pin layer:
   *  only this layer is touched, never the full viewer state). */
  function addAnnotationLayer(viewer: any, name: string, spec: any) {
    const managed = makeLayer(viewer.layerSpecification, name, spec);
    viewer.layerSpecification.add(managed);
  }

  function syncAiLayer() {
    try {
      const viewer: any = (window as any)['viewer'];
      if (!viewer?.state) return;
      // Same rule as the scout layers: only in Scout Tag mode, and only while
      // the AI tab's own toggle is on.
      const points = aiPointAnnotations();
      if (!aiLayerOn.value || !tagModeActive.value || !points.length) {
        removeLayerByName(viewer, AI_LAYER_NAME);
        removeLayerByName(viewer, AI_CRYSTAL_LAYER_NAME);
        lastCrystalKey = '';
        return;
      }
      syncAiCrystals(viewer, points);
      const managed = viewer.layerManager?.managedLayers?.find((l: any) => l.name === AI_LAYER_NAME);
      // A layer resurrected from the URL state carries whatever shader it was
      // saved with; retrofit the current one (same trick as the tag layer).
      try {
        const shaderState = (managed?.layer as any)?.annotationDisplayState?.shader;
        if (shaderState && shaderState.value !== AI_SHADER) shaderState.restoreState(AI_SHADER);
      } catch {}
      if (!managed) {
        addAnnotationLayer(viewer, AI_LAYER_NAME, {
          type: 'annotation',
          name: AI_LAYER_NAME,
          annotationProperties: [{ id: 'conf', type: 'float32', default: 1 }],
          annotations: points.map(p => ({
            id: p.id, point: p.point, type: 'point', description: p.description, props: [p.conf],
          })),
          shader: AI_SHADER,
        });
        return;
      }
      const src = (managed.layer as any)?.localAnnotations;
      if (!src) return;
      src.clear();
      quietly(() => { for (const p of points) {
        src.add({ id: p.id, type: 0 /* AnnotationType.POINT */, point: Float32Array.from(p.point), properties: [p.conf], description: p.description }, true);
      } });
    } catch (e) {
      console.warn('[issueTags] AI layer sync failed:', e);
    }
  }

  // ── Golden shards: the candidates' 3D presence ───────────────────────────
  // Circles carry the 2D cross-sections (where the color ramp reads); in the
  // 3D projection each candidate is a faceted golden shard, an elongated
  // octahedron sized by confidence, all instances baked into ONE combined
  // OBJ served as a data: URL, the same trick as the scout pins.
  const AI_CRYSTAL_LAYER_NAME = '🔶 AI shards';
  let lastCrystalKey = '';
  function syncAiCrystals(viewer: any, points: { id: string; point: number[]; conf: number }[]) {
    try {
      if (!points.length) {
        removeLayerByName(viewer, AI_CRYSTAL_LAYER_NAME);
        lastCrystalKey = '';
        return;
      }
      const cs = viewer.coordinateSpace?.value;
      if (!cs?.scales?.length) return;
      const scaleNm = [0, 1, 2].map(i => cs.scales[i] * 1e9);
      const inst = points.map(p => ({
        pos: [p.point[0] * scaleNm[0], p.point[1] * scaleNm[1], p.point[2] * scaleNm[2]],
        r: 500 + 900 * Math.max(0, Math.min(1, p.conf)),
      }));
      const key = JSON.stringify(inst.map(i =>
        [Math.round(i.pos[0]), Math.round(i.pos[1]), Math.round(i.pos[2]), Math.round(i.r)]));
      const existing = viewer.layerManager?.managedLayers?.find((l: any) => l.name === AI_CRYSTAL_LAYER_NAME);
      if (key === lastCrystalKey && existing) return;

      const lines: string[] = [];
      for (const it of inst) {
        // Hover the shard ABOVE its point like the scout pins do: centered
        // on the spot it hides exactly what it marks.
        const [x, yRaw, z] = it.pos, r = it.r;
        const y = yRaw - 2.6 * r;
        lines.push(
          `v ${(x + r).toFixed(0)} ${y.toFixed(0)} ${z.toFixed(0)}`,
          `v ${(x - r).toFixed(0)} ${y.toFixed(0)} ${z.toFixed(0)}`,
          `v ${x.toFixed(0)} ${(y + 1.7 * r).toFixed(0)} ${z.toFixed(0)}`,
          `v ${x.toFixed(0)} ${(y - 1.7 * r).toFixed(0)} ${z.toFixed(0)}`,
          `v ${x.toFixed(0)} ${y.toFixed(0)} ${(z + r).toFixed(0)}`,
          `v ${x.toFixed(0)} ${y.toFixed(0)} ${(z - r).toFixed(0)}`);
      }
      inst.forEach((_, k) => {
        const o = k * 6;
        for (const f of [[1, 3, 5], [3, 2, 5], [2, 4, 5], [4, 1, 5], [3, 1, 6], [2, 3, 6], [4, 2, 6], [1, 4, 6]]) {
          lines.push(`f ${f[0] + o} ${f[1] + o} ${f[2] + o}`);
        }
      });
      const dataUrl = 'data:application/octet-stream;base64,' + btoa(lines.join('\n'));
      // Replace non-disruptively, like the pins: only this layer is touched.
      if (existing) viewer.layerManager.removeManagedLayer(existing);
      const managed = makeLayer(viewer.layerSpecification, AI_CRYSTAL_LAYER_NAME, {
        type: 'mesh',
        source: 'obj://' + dataUrl,
        name: AI_CRYSTAL_LAYER_NAME,
        shader: 'void main() { emitRGB(vec3(1.0, 0.84, 0.25)); }',
      });
      viewer.layerSpecification.add(managed);
      lastCrystalKey = key;
    } catch (e) {
      console.warn('[issueTags] AI shard layer sync failed:', e);
    }
  }

  // ── Dense heat layer (all model windows for one neuron) ──────────────────
  // Every window the model scored, suspect or not, as a continuous heat skin
  // along the neuron. Toggled per neuron from the AI tab; data comes from the
  // model_windows table.

  const HEAT_SHADER = 'void main() {\n' +
    '  float c = clamp(prop_conf(), 0.0, 1.0);\n' +
    '  vec3 cold = vec3(0.2, 0.45, 1.0);\n' +
    '  vec3 hot = vec3(1.0, 0.84, 0.0);\n' +
    '  setColor(vec4(mix(cold, hot, c), 0.25 + 0.7 * c));\n' +
    '  setPointMarkerSize(4.0 + 9.0 * c);\n' +
    '}\n';

  function heatLayerName(rootId: string) {
    return `🔥 AI heat …${rootId.slice(-6)}`;
  }

  /** Roots whose heat layer is currently shown (drives the AI tab chips). */
  const activeHeatRoots = ref<string[]>([]);
  const heatLoadingRoot = ref<string | null>(null);

  async function toggleHeatLayer(rootId: string) {
    const viewer: any = (window as any)['viewer'];
    if (!viewer?.state) return;
    const name = heatLayerName(rootId);
    if (activeHeatRoots.value.includes(rootId)) {
      removeLayerByName(viewer, name);
      activeHeatRoots.value = activeHeatRoots.value.filter(r => r !== rootId);
      return;
    }
    heatLoadingRoot.value = rootId;
    try {
      const canon = currentDatasetTag();
      // Supabase caps a query at 1000 rows; a neuron can have several
      // thousand windows, so page through.
      const rows: any[] = [];
      for (let from = 0; ; from += 1000) {
        const { data, error } = await supabase
          .from('model_windows')
          .select('position, verify_prob, is_suspect, window_idx')
          .eq('dataset', canon)
          .eq('root_id', rootId)
          .order('window_idx', { ascending: true })
          .range(from, from + 999);
        if (error) { console.warn('[issueTags] model_windows load error:', error.message); break; }
        rows.push(...(data ?? []));
        if (!data || data.length < 1000) break;
      }
      if (!rows.length) return;
      const annotations = rows.map(r => {
        let point: number[];
        try { point = JSON.parse(r.position); } catch { return null; }
        return {
          id: `heat-${rootId}-${r.window_idx}`,
          point,
          type: 'point',
          description: `window ${r.window_idx}: ${(r.verify_prob * 100).toFixed(1)}%${r.is_suspect ? ' SUSPECT' : ''}`,
          props: [r.verify_prob],
        };
      }).filter(Boolean);
      removeLayerByName(viewer, name);
      addAnnotationLayer(viewer, name, {
        type: 'annotation',
        name,
        annotationProperties: [{ id: 'conf', type: 'float32', default: 0 }],
        annotations,
        shader: HEAT_SHADER,
      });
      activeHeatRoots.value = [...activeHeatRoots.value, rootId];
    } finally {
      heatLoadingRoot.value = null;
    }
  }

  // ── Proposed-split overlay ───────────────────────────────────────────────
  // A suspect window carries 25 sample points labeled 0/1: the model's
  // proposed partition of the neuron at that spot. Shown as a two-color
  // constellation so a Scythe sees the suggested cut before making it.

  const SPLIT_LAYER_NAME = '✂ Proposed split';
  const SPLIT_SHADER = 'void main() {\n' +
    '  vec3 a = vec3(1.0, 0.35, 0.35);\n' +
    '  vec3 b = vec3(0.3, 0.65, 1.0);\n' +
    '  setColor(vec4(mix(a, b, clamp(prop_side(), 0.0, 1.0)), 0.95));\n' +
    '  setPointMarkerSize(9.0);\n' +
    '  setPointMarkerBorderWidth(1.0);\n' +
    '  setPointMarkerBorderColor(vec4(1.0, 1.0, 1.0, 0.8));\n' +
    '}\n';

  /** Tag whose proposed split is currently overlaid (AI tab scissors chip). */
  const activeSplitTagId = ref<string | null>(null);

  function hideSplitOverlay() {
    const viewer: any = (window as any)['viewer'];
    if (viewer) removeLayerByName(viewer, SPLIT_LAYER_NAME);
    activeSplitTagId.value = null;
  }

  function toggleSplitOverlay(tag: IssueTag) {
    if (activeSplitTagId.value === tag.id) { hideSplitOverlay(); return; }
    const viewer: any = (window as any)['viewer'];
    const md = tag.modelData;
    if (!viewer?.state || !md?.posRelUm?.length || !md.labels?.length || !md.centerUm) return;
    try {
      // Micron -> voxel via the viewer's own coordinate space, like the pins.
      const cs = viewer.coordinateSpace?.value;
      if (!cs?.scales?.length) return;
      const scaleNm = [0, 1, 2].map(i => cs.scales[i] * 1e9);
      const annotations = md.posRelUm.map((rel: number[], i: number) => ({
        id: `split-${tag.id}-${i}`,
        point: [0, 1, 2].map(d => (md.centerUm![d] + rel[d]) * 1000 / scaleNm[d]),
        type: 'point',
        description: `proposed side ${md.labels![i]}`,
        props: [md.labels![i]],
      }));
      removeLayerByName(viewer, SPLIT_LAYER_NAME);
      addAnnotationLayer(viewer, SPLIT_LAYER_NAME, {
        type: 'annotation',
        name: SPLIT_LAYER_NAME,
        annotationProperties: [{ id: 'side', type: 'float32', default: 0 }],
        annotations,
        shader: SPLIT_SHADER,
      });
      activeSplitTagId.value = tag.id;
    } catch (e) {
      console.warn('[issueTags] split overlay failed:', e);
    }
  }

  /** The layers this store draws from shared data (tags, AI candidates,
   *  heat, proposed split), as opposed to a player's own markup. */
  function isTagStoreLayer(name: string): boolean {
    return [TAG_LAYER_NAME, PIN_LAYER_NAME, AI_LAYER_NAME, AI_CRYSTAL_LAYER_NAME, SPLIT_LAYER_NAME].includes(name)
      || name.startsWith('🔥 AI heat …');
  }

  async function load() {
    try {
      const { data, error } = await supabase
        .from('issue_tags')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1000);
      if (error) { console.warn('[issueTags] load error:', error.message); return; }
      tags.value = (data ?? []).map(rowToIssueTag);
      syncTagLayer();
    } catch (e) { console.warn('[issueTags] load failed:', e); }
  }

  /** Drop a tag at the given position (voxel coords for the active dataset). */
  async function add(tag: { tagType: IssueTagType; position: number[]; segId?: string; note?: string;
                            subtype?: IssueTagSubtype; annotationLayer?: string; screenshotUrl?: string }) {
    const backend = useProofreadingBackendStore();
    const row = {
      dataset: currentDatasetTag(),
      segment_id: tag.segId || null,
      position: JSON.stringify(tag.position),
      tag_type: tag.tagType,
      subtype: tag.subtype || null,
      annotation_layer: tag.annotationLayer || null,
      screenshot_url: tag.screenshotUrl || null,
      note: tag.note || null,
      user_id: backend.userId || null,
      user_name: backend.chatHandle || backend.userName || 'Anonymous',
    };
    const { data, error } = await supabase.from('issue_tags').insert(row).select().single();
    if (error) { console.warn('[issueTags] insert error:', error.message); return null; }
    const t = rowToIssueTag(data);
    if (!tags.value.find(x => x.id === t.id)) tags.value.unshift(t);
    syncTagLayer();
    return t;
  }

  /** Mark a tag fixed. The resolver's name is shown on the resolved row. */
  async function resolve(id: string) {
    const backend = useProofreadingBackendStore();
    const name = backend.chatHandle || backend.userName || 'Anonymous';
    const { error } = await supabase.from('issue_tags').update({
      status: 'resolved',
      resolved_by: backend.userId || null,
      resolved_by_name: name,
      resolved_at: new Date().toISOString(),
    }).eq('id', id);
    if (error) { console.warn('[issueTags] resolve error:', error.message); return; }
    const t = tags.value.find(x => x.id === id);
    if (t) { t.status = 'resolved'; t.resolvedByName = name; }
    syncTagLayer();
  }

  async function remove(id: string) {
    const { error } = await supabase.from('issue_tags').delete().eq('id', id);
    if (error) { console.warn('[issueTags] delete error:', error.message); }
    tags.value = tags.value.filter(x => x.id !== id);
    syncTagLayer();
  }

  function subscribe() {
    if (realtimeChannel) return;
    realtimeChannel = supabase
      .channel('issue_tags_realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'issue_tags' }, (payload: any) => {
        const t = rowToIssueTag(payload.new);
        if (!tags.value.find(x => x.id === t.id)) tags.value.unshift(t);
        syncTagLayer();
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'issue_tags' }, (payload: any) => {
        const t = rowToIssueTag(payload.new);
        const idx = tags.value.findIndex(x => x.id === t.id);
        if (idx >= 0) tags.value[idx] = t;
        syncTagLayer();
      })
      .subscribe();
  }

  load();
  subscribe();
  // The viewer boots asynchronously after the stores; without these retries
  // nothing syncs the layers on a fresh page load, so they only ever came
  // back via the URL state (stale shaders, no shards).
  for (const ms of [2000, 5000, 10000]) setTimeout(syncTagLayer, ms);
  // Saved views and shared links carry these layers in their state, and the
  // viewer can finish loading after the timed syncs above. Watch the layer
  // list itself, so a tag layer that appears outside tag mode is removed as
  // soon as it shows up, however late.
  const TAG_MODE_ONLY = [TAG_LAYER_NAME, PIN_LAYER_NAME, AI_LAYER_NAME, AI_CRYSTAL_LAYER_NAME];
  (function watchLayers(tries = 60) {
    const lm = (window as any)['viewer']?.layerManager;
    if (!lm?.layersChanged?.add) {
      if (tries > 0) setTimeout(() => watchLayers(tries - 1), 1000);
      return;
    }
    lm.layersChanged.add(() => {
      if (tagModeActive.value) return;
      if (lm.managedLayers?.some((l: any) => TAG_MODE_ONLY.includes(l.name))) syncTagLayer();
    });
    syncTagLayer();
  })();

  return { tags, openTags, load, add, resolve, remove, syncTagLayer, setTagPreview, tagModeActive, setTagModeActive,
           aiLayerOn, setAiLayerOn, syncAiLayer,
           activeHeatRoots, heatLoadingRoot, toggleHeatLayer,
           activeSplitTagId, toggleSplitOverlay, hideSplitOverlay, isTagStoreLayer };
});

// ── Working Links ─────────────────────────────────────────────────────────
// Saved viewer-state URLs (snapshots). Backed by Supabase `working_links`.

export interface WorkingLink {
  id: string;
  userId: string;
  userName?: string;
  title: string;
  note: string;
  url: string;
  starred: boolean;
  dataset?: string;
  position?: [number, number, number];
  visibleSegments: string[];
  isPublic: boolean;
  sharedGroupId?: number;
  createdAt: string;
  updatedAt: string;
  /** Optional picture of the view (working_links.screenshot_url), uploaded
   *  through ewSecureUpload; the gateway rejects any other host. */
  screenshotUrl?: string;
}

function rowToWorkingLink(row: any): WorkingLink {
  return {
    id: row.id,
    userId: row.user_id,
    userName: row.user_name ?? undefined,
    title: row.title ?? 'Untitled',
    note: row.note ?? '',
    url: row.url ?? '',
    starred: !!row.starred,
    dataset: row.dataset ?? undefined,
    position: (row.position_x != null && row.position_y != null && row.position_z != null)
      ? [row.position_x, row.position_y, row.position_z] : undefined,
    visibleSegments: Array.isArray(row.visible_segments) ? row.visible_segments : [],
    isPublic: !!row.is_public,
    sharedGroupId: row.shared_group_id ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    screenshotUrl: row.screenshot_url ?? undefined,
  };
}

export const useWorkingLinksStore = defineStore('workingLinks', () => {
  const links = ref<WorkingLink[]>([]);
  let realtimeChannel: any = null;

  /** Load all links visible to this user (own + public + shared-to-group). */
  async function load() {
    try {
      const { data, error } = await supabase
        .from('working_links')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) {
        console.warn('[workingLinks] load error:', error.message);
        return;
      }
      links.value = (data ?? []).map(rowToWorkingLink);
    } catch (e) {
      console.warn('[workingLinks] load failed:', e);
    }
  }

  function subscribe() {
    if (realtimeChannel) return;
    realtimeChannel = supabase
      .channel('working_links_realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'working_links' }, (payload: any) => {
        const link = rowToWorkingLink(payload.new);
        if (!links.value.find(l => l.id === link.id)) links.value.unshift(link);
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'working_links' }, (payload: any) => {
        const updated = rowToWorkingLink(payload.new);
        const idx = links.value.findIndex(l => l.id === updated.id);
        if (idx >= 0) links.value[idx] = updated;
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'working_links' }, (payload: any) => {
        const id = payload.old?.id;
        if (id) links.value = links.value.filter(l => l.id !== id);
      })
      .subscribe();
  }

  function unsubscribe() {
    if (realtimeChannel) {
      supabase.removeChannel(realtimeChannel);
      realtimeChannel = null;
    }
  }

  /** Save a new working link. Returns the inserted row's id, or null on failure. */
  async function add(input: {
    title: string;
    note?: string;
    url: string;
    dataset?: string;
    position?: [number, number, number];
    visibleSegments?: string[];
    starred?: boolean;
    isPublic?: boolean;
    sharedGroupId?: number | null;
    screenshotUrl?: string | null;
  }): Promise<string | null> {
    const backend = useProofreadingBackendStore();
    if (!backend.userId) {
      console.warn('[workingLinks] cannot save without login');
      return null;
    }
    const row: any = {
      user_id: backend.userId,
      title: input.title || 'Untitled',
      note: input.note ?? '',
      url: input.url,
      starred: !!input.starred,
      dataset: input.dataset ?? null,
      position_x: input.position?.[0] ?? null,
      position_y: input.position?.[1] ?? null,
      position_z: input.position?.[2] ?? null,
      visible_segments: input.visibleSegments ?? [],
      is_public: !!input.isPublic,
      shared_group_id: input.sharedGroupId ?? null,
      ...(input.screenshotUrl ? { screenshot_url: input.screenshotUrl } : {}),
    };
    try {
      const { data, error } = await supabase
        .from('working_links')
        .insert(row)
        .select()
        .single();
      if (error) {
        console.warn('[workingLinks] insert error:', error.message);
        return null;
      }
      const link = rowToWorkingLink(data);
      // Optimistic local insert (realtime will dedupe)
      if (!links.value.find(l => l.id === link.id)) links.value.unshift(link);
      return link.id;
    } catch (e) {
      console.warn('[workingLinks] insert failed:', e);
      return null;
    }
  }

  async function update(id: string, patch: Partial<{
    title: string;
    note: string;
    starred: boolean;
    isPublic: boolean;
    sharedGroupId: number | null;
  }>): Promise<boolean> {
    const row: any = { updated_at: new Date().toISOString() };
    if (patch.title !== undefined) row.title = patch.title;
    if (patch.note !== undefined) row.note = patch.note;
    if (patch.starred !== undefined) row.starred = patch.starred;
    if (patch.isPublic !== undefined) row.is_public = patch.isPublic;
    if (patch.sharedGroupId !== undefined) row.shared_group_id = patch.sharedGroupId;
    try {
      const { error } = await supabase.from('working_links').update(row).eq('id', id);
      if (error) {
        console.warn('[workingLinks] update error:', error.message);
        return false;
      }
      // Optimistic local update
      const idx = links.value.findIndex(l => l.id === id);
      if (idx >= 0) links.value[idx] = { ...links.value[idx], ...patch, updatedAt: row.updated_at } as WorkingLink;
      return true;
    } catch (e) {
      console.warn('[workingLinks] update failed:', e);
      return false;
    }
  }

  async function remove(id: string): Promise<boolean> {
    try {
      const { error } = await supabase.from('working_links').delete().eq('id', id);
      if (error) {
        console.warn('[workingLinks] delete error:', error.message);
        return false;
      }
      links.value = links.value.filter(l => l.id !== id);
      return true;
    } catch (e) {
      console.warn('[workingLinks] delete failed:', e);
      return false;
    }
  }

  async function toggleStar(id: string): Promise<boolean> {
    const link = links.value.find(l => l.id === id);
    if (!link) return false;
    return update(id, { starred: !link.starred });
  }

  // Initialize
  load();
  subscribe();

  return { links, load, subscribe, unsubscribe, add, update, remove, toggleStar };
});

// ── Proofreading Queue ─────────────────────────────────────────────────────
// Loads a queue of segments to review from a published Google Sheet (CSV).
// Users cycle through items, mark them reviewed, and track session progress.

const QUEUE_SHEET_KEY    = 'nge_queue_sheet_url_v1';

/** Hash a sheet URL into a short suffix so each sheet gets its own storage keys. */
function sheetKeyHash(url: string): string {
  if (!url) return '_default';
  let h = 0;
  for (let i = 0; i < url.length; i++) h = ((h << 5) - h + url.charCodeAt(i)) | 0;
  return '_' + Math.abs(h).toString(36);
}

export interface QueueItem {
  segId: string;
  index: string;              // e.g. "B1", "B2"
  nucCoords: string;          // nucleus coordinates "x, y, z"
  somaCoords: string;         // soma coordinates "x, y, z"
  finalSegId: string;         // final segment ID after proofreading
  finalNucId: string;         // final nucleus ID
  notes: string;
  dataset: string;            // dataset the cell belongs to (tagged at load time from its sheet)
  sheetStatus?: string;       // status text from the sheet (Stroeh: 'Status' column); '' if none
  assignee?: string;          // who's on it, from the sheet (Stroeh: 'Proofreader'); '' if none
  startLink?: string;         // curated starting view (Stroeh: 'Start link', column E); opened on Claim
  aiType?: string;            // the type the lab's AI predicts (Stroeh: 'AI-predicted BC type'); '' if none
}

export const useProofreadingQueueStore = defineStore('proofreadingQueue', () => {
  const items       = ref<QueueItem[]>([]);
  const currentIdx  = ref(0);
  const proofread   = ref<Set<string>>(new Set());   // formerly "reviewed"
  const loading     = ref(false);
  const error       = ref('');
  const sheetUrl    = ref('');
  const sessionStart = Date.now();

  // Per-item local edits (soma coords, final seg id, annotation) keyed by segId
  const localEdits = ref<Record<string, { somaCoords?: string; finalSegId?: string; annotation?: string }>>(
    {});

  // Persist proofread set, sheet URL, and local edits — keyed per-sheet
  // so different datasets (pinky vs minnie65) don't clobber each other.
  function reviewedKey() { return 'nge_queue_reviewed' + sheetKeyHash(sheetUrl.value); }
  function editsKey()    { return 'nge_queue_edits' + sheetKeyHash(sheetUrl.value); }

  function loadLocal() {
    try {
      const url = localStorage.getItem(QUEUE_SHEET_KEY);
      if (url) sheetUrl.value = url;
    } catch {}
    try {
      const raw = localStorage.getItem(reviewedKey());
      if (raw) proofread.value = new Set(JSON.parse(raw));
    } catch {}
    try {
      const edits = localStorage.getItem(editsKey());
      if (edits) localEdits.value = JSON.parse(edits);
    } catch {}
  }

  function persistProofread() {
    localStorage.setItem(reviewedKey(), JSON.stringify([...proofread.value]));
  }

  function persistEdits() {
    localStorage.setItem(editsKey(), JSON.stringify(localEdits.value));
  }

  function persistSheetUrl() {
    localStorage.setItem(QUEUE_SHEET_KEY, sheetUrl.value);
  }

  /** Get local edits for a segment, merged with sheet data. */
  function getEdits(segId: string) {
    return localEdits.value[segId] || {};
  }

  /** Save local edit for a specific field. */
  function setEdit(segId: string, field: 'somaCoords' | 'finalSegId' | 'annotation', value: string) {
    if (!localEdits.value[segId]) localEdits.value[segId] = {};
    localEdits.value[segId][field] = value;
    persistEdits();
  }

  /** Check if item has soma coords (from sheet or local edit) — i.e. "claimed". */
  function isClaimed(item: QueueItem): boolean {
    const coords = item.somaCoords || getEdits(item.segId).somaCoords || '';
    return coords.trim().length > 0;
  }

  /** Check if item can be marked proofread (has soma + final seg id). */
  function canComplete(item: QueueItem): boolean {
    const edits = getEdits(item.segId);
    const hasSoma = (item.somaCoords || edits.somaCoords || '').trim().length > 0;
    const hasFinal = (item.finalSegId || edits.finalSegId || '').trim().length > 0;
    return hasSoma && hasFinal;
  }

  /** Parse CSV text into rows (handles quoted fields). */
  function parseCsv(text: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (inQuotes) {
        if (ch === '"' && text[i + 1] === '"') { field += '"'; i++; }
        else if (ch === '"') { inQuotes = false; }
        else { field += ch; }
      } else {
        if (ch === '"') { inQuotes = true; }
        else if (ch === ',') { row.push(field.trim()); field = ''; }
        else if (ch === '\n' || ch === '\r') {
          if (ch === '\r' && text[i + 1] === '\n') i++;
          row.push(field.trim()); field = '';
          if (row.some(f => f)) rows.push(row);
          row = [];
        } else { field += ch; }
      }
    }
    row.push(field.trim());
    if (row.some(f => f)) rows.push(row);
    return rows;
  }

  /**
   * Fetch and parse a published Google Sheet CSV.
   * Finds the header row dynamically (looks for "Segment ID" in any row).
   * Expected columns: Index, Nuc Coords, Soma Coords, Segment ID,
   *                   Final SegID, Final NucID, Notes
   */
  async function loadFromSheet(url?: string, datasetName?: string) {
    const sheetSource = url || sheetUrl.value;
    if (!sheetSource) { error.value = 'No sheet URL configured'; return; }

    loading.value = true;
    error.value = '';

    try {
      // Convert share URL to CSV export URL
      let csvUrl = sheetSource;
      const match = sheetSource.match(/\/d\/([a-zA-Z0-9_-]+)/);
      if (match) {
        // Extract gid if present (for multi-tab sheets)
        const gidMatch = sheetSource.match(/gid=(\d+)/);
        const gid = gidMatch ? gidMatch[1] : '0';
        csvUrl = `https://docs.google.com/spreadsheets/d/${match[1]}/export?format=csv&gid=${gid}`;
      }

      const res = await fetch(csvUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      const rows = parseCsv(text);
      if (rows.length < 2) { error.value = 'Sheet is empty'; loading.value = false; return; }

      // Find header row dynamically — look for a row containing "segment"
      let headerIdx = 0;
      for (let i = 0; i < Math.min(rows.length, 10); i++) {
        const lower = rows[i].map(c => c.toLowerCase());
        if (lower.some(c => c.includes('segment'))) { headerIdx = i; break; }
      }

      const header = rows[headerIdx].map(h => h.toLowerCase().replace(/[^a-z0-9]/g, ''));
      const col = (name: string) =>
        header.findIndex(h => h.includes(name));

      // First matching column index from a list of patterns (in priority order).
      const firstCol = (...names: string[]) => {
        for (const n of names) { const i = col(n); if (i >= 0) return i; }
        return -1;
      };

      const iIndex    = col('index');
      const iNuc      = col('nuccoord') >= 0 ? col('nuccoord') : col('nuc');
      const iSoma     = firstCol('somacoord', 'somaorstem', 'soma');
      // 'startseg' (Stroeh "Start SegID") before generic — avoids matching "Final SegID".
      const iSeg      = firstCol('startseg', 'segmentid', 'segment');
      const iFinal    = col('finalseg');
      const iFinalNuc = col('finalnuc');
      const iNotes    = col('note');
      const iDataset  = col('dataset');
      const iStatus   = col('status');
      const iAssignee = firstCol('proofreader', 'claimedby', 'assignee');
      const iStartLink = col('startlink');
      const iAiType    = firstCol('aipredicted', 'predictedtype');

      if (iSeg < 0) { error.value = 'Could not find a Segment ID column'; loading.value = false; return; }

      const parsed: QueueItem[] = [];
      for (let r = headerIdx + 1; r < rows.length; r++) {
        const row = rows[r];
        const segId = (row[iSeg] || '').trim();
        if (!segId || !/^\d+$/.test(segId)) continue;

        parsed.push({
          segId,
          index:      (iIndex >= 0 ? row[iIndex] : '') || '',
          nucCoords:  cleanCoord(iNuc >= 0 ? row[iNuc] : ''),
          somaCoords: cleanCoord(iSoma >= 0 ? row[iSoma] : ''),
          finalSegId: (iFinal >= 0 ? row[iFinal] : '') || '',
          finalNucId: (iFinalNuc >= 0 ? row[iFinalNuc] : '') || '',
          notes:      (iNotes >= 0 ? row[iNotes] : '') || '',
          // Tag with the dataset this sheet was loaded for; fall back to a
          // 'dataset' column if the sheet has one, else empty.
          dataset:    (datasetName || (iDataset >= 0 ? (row[iDataset] || '').trim() : '')) || '',
          sheetStatus: (iStatus >= 0 ? (row[iStatus] || '').trim() : '') || '',
          assignee:    (iAssignee >= 0 ? (row[iAssignee] || '').trim() : '') || '',
          startLink:   (iStartLink >= 0 ? (row[iStartLink] || '').trim() : '') || '',
          aiType:      (iAiType >= 0 ? (row[iAiType] || '').trim().slice(0, 40) : '') || '',
        });
      }

      items.value = parsed;
      if (url && url !== sheetUrl.value) {
        // Switching to a different sheet — save current, then load new sheet's data
        sheetUrl.value = url;
        persistSheetUrl();
        // Load proofread/edits for the NEW sheet (keyed by URL hash)
        try {
          const raw = localStorage.getItem(reviewedKey());
          proofread.value = raw ? new Set(JSON.parse(raw)) : new Set();
        } catch { proofread.value = new Set(); }
        try {
          const edits = localStorage.getItem(editsKey());
          localEdits.value = edits ? JSON.parse(edits) : {};
        } catch { localEdits.value = {}; }
        currentIdx.value = 0;
      }
    } catch (e: any) {
      error.value = e.message || 'Failed to load sheet';
    }
    loading.value = false;
  }

  /** Clean a coordinate string — strip dashes and whitespace-only values. */
  function cleanCoord(s: string): string {
    const trimmed = s.trim();
    return (trimmed === '-' || trimmed === '') ? '' : trimmed;
  }

  // ── Navigation ──────────────────────────────────────────────────────────
  function currentItem(): QueueItem | null {
    return items.value[currentIdx.value] ?? null;
  }

  function next() {
    if (items.value.length === 0) return;
    currentIdx.value = (currentIdx.value + 1) % items.value.length;
    navigateToCurrentItem();
  }

  function prev() {
    if (items.value.length === 0) return;
    currentIdx.value = (currentIdx.value - 1 + items.value.length) % items.value.length;
    navigateToCurrentItem();
  }

  function jumpToIndex(idx: number) {
    if (idx >= 0 && idx < items.value.length) {
      currentIdx.value = idx;
      navigateToCurrentItem();
    }
  }

  function nextUnproofread() {
    if (items.value.length === 0) return;
    for (let i = 1; i <= items.value.length; i++) {
      const idx = (currentIdx.value + i) % items.value.length;
      if (!proofread.value.has(items.value[idx].segId)) {
        currentIdx.value = idx;
        navigateToCurrentItem();
        return;
      }
    }
  }

  function navigateToCurrentItem() {
    const item = currentItem();
    if (!item) return;
    const edits = getEdits(item.segId);
    // Prefer local edits soma, then sheet soma, then nuc coords
    const coordStr = edits.somaCoords || item.somaCoords || item.nucCoords || '';
    const pos = parseCoordString(coordStr);
    const historyStore = useCellHistoryStore();
    // Always jump to segment (selects it), position is optional
    historyStore.jumpToCell(item.segId, pos[0] || pos[1] || pos[2] ? pos : undefined);
  }

  /** Parse "x, y, z" coordinate string into a tuple. */
  function parseCoordString(s: string): [number, number, number] {
    if (!s) return [0, 0, 0];
    const parts = s.split(',').map(p => parseInt(p.trim(), 10) || 0);
    return [parts[0] || 0, parts[1] || 0, parts[2] || 0];
  }

  function markProofread(segId?: string) {
    const id = segId || currentItem()?.segId;
    if (id) { proofread.value.add(id); persistProofread(); }
  }

  function unmarkProofread(segId: string) {
    proofread.value.delete(segId);
    persistProofread();
  }

  function clearProofread() {
    proofread.value.clear();
    persistProofread();
  }

  /** Clear all local edits (soma coords, final seg ID, annotations). */
  function clearLocalEdits() {
    localEdits.value = {};
    persistEdits();
  }

  /** Full reset: clear proofread set + local edits, jump to start. */
  function resetAll() {
    proofread.value.clear();
    localEdits.value = {};
    persistProofread();
    persistEdits();
    currentIdx.value = 0;
  }

  // ── Google Sheets write-back ─────────────────────────────────────────

  /** Sync the exact segment row through the authenticated Sheets service. */
  async function writeSomaCoordsToSheet(segId: string, coords: string) {
    return syncCellToSheet('coordinates', segId, coords, items.value.find(item => item.segId === segId)?.dataset);
  }

  // ── Computed-like helpers ───────────────────────────────────────────────
  function proofreadCount(): number { return proofread.value.size; }
  function totalCount(): number { return items.value.length; }
  function pendingCount(): number {
    return items.value.filter(i => !proofread.value.has(i.segId)).length;
  }
  function sessionMinutes(): number {
    return Math.floor((Date.now() - sessionStart) / 60000);
  }

  loadLocal();
  // Auto-load if we have a saved sheet URL
  if (sheetUrl.value) loadFromSheet();

  return {
    items, currentIdx, proofread, loading, error, sheetUrl, localEdits,
    loadFromSheet, currentItem, next, prev, jumpToIndex, nextUnproofread,
    navigateToCurrentItem, markProofread, unmarkProofread, clearProofread, clearLocalEdits, resetAll,
    proofreadCount, totalCount, pendingCount, sessionMinutes,
    getEdits, setEdit, isClaimed, canComplete, writeSomaCoordsToSheet,
  };
});

/* ───────── Split / Merge Overlay Store ───────── */

export const useSplitMergeOverlayStore = defineStore('splitMergeOverlay', () => {
  const toolActive = ref<'multicut' | 'merge' | null>(null);
  const activeGroup = ref<'red' | 'blue'>('red');
  const redPointCount = ref(0);
  const bluePointCount = ref(0);
  const mergeSubmissionCount = ref(0);
  /** Scraped merge pairs: each entry is [sinkId, sourceId] or [sinkId] if incomplete */
  const mergeSegments = ref<string[][]>([]);
  /** One per queued merge, in step with mergeSegments: where it stands
   *  ('submitting', 'done', an error, or '' while it waits) and whether it
   *  can still be removed (not once it is on its way to the server). For a
   *  failed one, `why` is what the server said. */
  const mergeRows = ref<{ status: string; removable: boolean; why?: string }[]>([]);
  /** Whether NG's auto-submit checkbox is checked */
  const autoSubmit = ref(false);
  const submitting = ref(false);
  /** Status message scraped from neuroglancer's tool UI */
  const statusMessage = ref('');
  /** Result flash: 'success' | 'error' | '' */
  const resultFlash = ref<'success' | 'error' | ''>('');
  const resultText = ref('');
  /** When true, bar holds visible in success/error state before closing */
  const pendingClose = ref(false);
  /** Which tool was active before pendingClose (for display) */
  const closingTool = ref<'multicut' | 'merge' | null>(null);
  /** Timestamp of the last "Clear points" click. The DOM scanner uses this to
   *  distinguish a Clear (points reset to 0, no submit) from an actual split
   *  submit (points also reset to 0, but the operation is processing). */
  const clearedAt = ref(0);
  /** "Keep open after a cut" (Nseraf 2026-10-12). Off by default: the cut
   *  tool closes after a cut. On, it stays for the next one. Kept in this
   *  browser; the cut tool itself reads the same key at each submit. */
  const keepCutOpen = ref(ngeKeepCutOpen());
  function setKeepCutOpen(on: boolean) {
    keepCutOpen.value = on;
    try { localStorage.setItem(NGE_KEEP_CUT_OPEN_KEY, on ? '1' : '0'); } catch { /* a private window: it holds for this page only */ }
  }
  let flashTimer: ReturnType<typeof setTimeout> | null = null;
  let closeTimer: ReturnType<typeof setTimeout> | null = null;
  /** Points left on a cell that is not shown, cleared as cut mode opened
   *  (util/stale_cut_points.ts): how many, while the bar offers Undo. */
  const staleCutCleared = ref<number | null>(null);
  let staleCutTimer: ReturnType<typeof setTimeout> | null = null;
  function showStaleCutCleared(count: number | null) {
    if (staleCutTimer) { clearTimeout(staleCutTimer); staleCutTimer = null; }
    staleCutCleared.value = count;
    if (count !== null) staleCutTimer = setTimeout(() => { staleCutCleared.value = null; staleCutTimer = null; }, 8000);
  }

  /** Record that the user just cleared the placed points. */
  function markCleared() {
    clearedAt.value = Date.now();
    submitting.value = false;
    statusMessage.value = '';
  }

  function setToolState(tool: 'multicut' | 'merge' | null) {
    if (pendingClose.value) return; // don't interfere during close animation
    if (tool !== toolActive.value) {
      toolActive.value = tool;
      if (tool === null) {
        redPointCount.value = 0;
        bluePointCount.value = 0;
        mergeSubmissionCount.value = 0;
        mergeSegments.value = [];
        mergeRows.value = [];
        autoSubmit.value = false;
        submitting.value = false;
        statusMessage.value = '';
        resultFlash.value = '';
        resultText.value = '';
        if (flashTimer) { clearTimeout(flashTimer); flashTimer = null; }
        showStaleCutCleared(null);
      }
    }
  }

  function setActiveGroup(group: 'red' | 'blue') {
    activeGroup.value = group;
  }

  function updatePointCounts(red: number, blue: number) {
    redPointCount.value = red;
    bluePointCount.value = blue;
  }

  function setStatusMessage(msg: string) {
    statusMessage.value = msg;
  }

  /** Show inline result on the bar (merge tool stays active after) */
  function showResult(type: 'success' | 'error', text: string, durationMs = 4000) {
    resultFlash.value = type;
    resultText.value = text;
    submitting.value = false;
    statusMessage.value = '';
    if (flashTimer) clearTimeout(flashTimer);
    flashTimer = setTimeout(() => {
      resultFlash.value = '';
      resultText.value = '';
    }, durationMs);
  }

  /** Close the result flash now. Also presses Dismiss on neuroglancer's own
   *  error status: main.ts re-raises the flash from it while it is shown. */
  function dismissResult() {
    const text = resultText.value;
    if (flashTimer) { clearTimeout(flashTimer); flashTimer = null; }
    resultFlash.value = '';
    resultText.value = '';
    const statusContainer = document.getElementById('statusContainer');
    if (!statusContainer || !text) return;
    for (const el of Array.from(statusContainer.children)) {
      const elText = (el.textContent || '').trim().replace(/\s*dismiss\s*$/i, '').trim();
      if (elText !== text) continue;
      (el.querySelector('button') as HTMLElement | null)?.click();
    }
  }

  /** Hold bar in success state for 2.5s, then holographic exit */
  function beginSuccessClose(text: string) {
    closingTool.value = toolActive.value;
    pendingClose.value = true;
    resultFlash.value = 'success';
    resultText.value = text;
    submitting.value = false;
    statusMessage.value = '';
    if (closeTimer) clearTimeout(closeTimer);
    closeTimer = setTimeout(() => {
      pendingClose.value = false;
      toolActive.value = null;
      closingTool.value = null;
      resultFlash.value = '';
      resultText.value = '';
      redPointCount.value = 0;
      bluePointCount.value = 0;
      mergeSubmissionCount.value = 0;
    }, 2500);
  }

  function removeMergeSegment(index: number) {
    // Remove it in neuroglancer, which owns the queue.
    try {
      const mergeEl = document.querySelector('.graphene-merge-segments');
      const sub = mergeEl?.querySelectorAll('.graphene-merge-segments-submission')[index];
      const deleteBtn = sub?.querySelector('button[title*="delete" i], button[title*="remove" i], button[title*="cancel" i], .neuroglancer-icon[title*="delete" i]') as HTMLElement | null;
      // No delete control means the merge is on its way to the server and
      // cannot be taken back. Taking the row off the screen anyway only made
      // it come straight back (Nik 2026-10-08: "they just re-add on their own").
      if (!deleteBtn) return;
      deleteBtn.click();
    } catch (e) {
      console.warn('[smo] removeMergeSegment DOM error:', e);
      return;
    }
    // Also update local state immediately for UI responsiveness
    mergeSegments.value.splice(index, 1);
    if (mergeSubmissionCount.value > 0) mergeSubmissionCount.value--;
  }

  return {
    toolActive, activeGroup, redPointCount, bluePointCount,
    mergeSubmissionCount, mergeSegments, mergeRows, autoSubmit, submitting, statusMessage, resultFlash, resultText,
    pendingClose, closingTool, clearedAt, keepCutOpen, staleCutCleared,
    setToolState, setActiveGroup, updatePointCounts, setStatusMessage,
    showResult, dismissResult, beginSuccessClose, removeMergeSegment, markCleared, setKeepCutOpen, showStaleCutCleared,
  };
});

/* ───────── Proofreading Backend Store (Supabase) ───────── */

/** Spatial coordinate anchor for point-in-space claims. */
export type ClaimPoint = [number, number, number];

export interface ProofreadingTask {
  id: number;
  segment_id: string;  // cached/resolved root ID from PCG (may be stale)
  dataset: string;
  nucleus_coords: string | null;
  soma_coords: string | null;
  status: 'pending' | 'assigned' | 'in_progress' | 'completed' | 'skipped';
  assigned_to: string | null;
  priority: number;
  final_segment_id: string | null;
  final_nucleus_id: string | null;
  notes: string | null;
  source_sheet_url: string | null;
  created_at: string;
  updated_at: string;
  // Point-in-space claim anchor
  claim_point_x: number | null;
  claim_point_y: number | null;
  claim_point_z: number | null;
  /** Immutable supervoxel ID at claim point — used to resolve current root after edits/splits. */
  supervoxel_id: string | null;
  /** The claimer's saved view of this cell, annotation layers included. */
  working_link?: string | null;
  /** 'interneuron': a long term quest (supabase-interneuron-quests.sql). One
   *  at a time, never expires, not counted against the claim limit. */
  kind?: 'cell' | 'interneuron' | null;
  /** A Super Quest's category, such as "Pyramidal Target" (supabase-super-quest-categories.sql). */
  quest_category?: string | null;
  /** When a Super Quest claim ends, 2 months after it was claimed (supabase-super-quest-two-months.sql). */
  quest_ends_at?: string | null;
}

/** An interneuron quest: a claim of its own kind, listed apart in the Cell Library. */
export const isQuestTask = (t: { kind?: string | null } | null | undefined) => t?.kind === 'interneuron';

export interface EditLogEntry {
  task_id?: number | null;
  user_id?: string | null;
  operation: string;
  segment_before?: string | null;
  segment_after?: string | null;
  coordinates?: string | null;
  metadata?: Record<string, any> | null;
  dataset?: string;
  success?: boolean;
}

export interface ActivityFeedItem {
  id: number;
  user_id: string | null;
  user_name: string | null;
  action: string;
  segment_id: string | null;
  timestamp: string;
}

export const useProofreadingBackendStore = defineStore('proofreadingBackend', () => {
  const userId = ref<string | null>(null);
  // The annotations counter follows whoever is signed in.
  watch(userId, id => setAnnotationCounterUser(id, currentDatasetTag), { immediate: true });
  const userEmail = ref<string>('');
  const userName = ref<string>('');
  /**
   * Unique handle used for chat display and @mentions.
   *
   * Display names ("Amy S.") are neither unique nor single-token, so mentions
   * couldn't tell two people apart or capture a name containing a space. A
   * username is unique and space-free, making a mention unambiguous by
   * construction. Empty until the user picks one; chat falls back to the
   * display name in that case.
   */
  const username = ref<string>('');

  /** Name to show in chat and match mentions against. */
  const chatHandle = computed(() => username.value || userName.value || 'Anonymous');

  const USERNAME_RE = /^[A-Za-z0-9_]{3,20}$/;

  /** Validate a candidate username. Returns '' when acceptable. */
  function validateUsername(name: string): string {
    const n = (name || '').trim();
    if (!n) return 'Pick a username.';
    if (!USERNAME_RE.test(n)) {
      return 'Use 3-20 characters: letters, numbers or underscore, no spaces.';
    }
    return '';
  }

  /** True when the username is free (case-insensitively), ignoring our own row. */
  async function isUsernameAvailable(name: string): Promise<boolean> {
    const n = (name || '').trim();
    if (!n) return false;
    const { data, error } = await supabase
      .from('users')
      .select('id')
      .ilike('username', n)     // case-insensitive, matching the unique index
      .limit(1);
    if (error) { console.warn('[username] availability check failed:', error.message); return false; }
    if (!data || data.length === 0) return true;
    return data[0].id === userId.value;   // our own row doesn't count as taken
  }

  /**
   * Suggest a free username derived from the user's display name.
   *
   * Existing users all have NULL, so asking them to invent a handle from
   * nothing is friction for no reason. "Amy R. Sterling" -> "amy_r_sterling",
   * with a numeric suffix if that's taken, so the prompt is usually one click.
   */
  async function suggestUsername(): Promise<string> {
    const base = (userName.value || userEmail.value.split('@')[0] || 'user')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')   // spaces and punctuation become separators
      .replace(/^_+|_+$/g, '')       // no leading/trailing separators
      .slice(0, 20);
    let candidate = base.length >= 3 ? base : `${base}_user`.slice(0, 20);
    for (let i = 0; i < 20; i++) {
      // Only offer something we've confirmed is actually free.
      if (await isUsernameAvailable(candidate)) return candidate;
      const suffix = String(i + 2);
      candidate = `${base.slice(0, 20 - suffix.length)}${suffix}`;
    }
    return candidate;
  }

  /** Persist the username. Throws with a friendly message on conflict. */
  async function saveUsername(name: string) {
    const n = (name || '').trim();
    const invalid = validateUsername(n);
    if (invalid) throw new Error(invalid);
    if (!userId.value) throw new Error('Not signed in.');
    const { error } = await supabase
      .from('users')
      .update({ username: n, updated_at: new Date().toISOString() })
      .eq('id', userId.value);
    if (error) {
      // 23505 = unique violation, i.e. someone already has it.
      if ((error as any).code === '23505' || /duplicate|unique/i.test(error.message)) {
        throw new Error('That username is taken.');
      }
      throw new Error(error.message);
    }
    username.value = n;
  }
  const tasks = ref<ProofreadingTask[]>([]);
  const activeTaskId = ref<number | null>(null);
  const activityFeed = ref<ActivityFeedItem[]>([]);
  const loading = ref(false);
  const error = ref('');

  let heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  let feedSubscription: any = null;

  // ── User sync ─────────────────────────────────────────────────────────
  /** Upsert user from middleauth identity. Call after login. */
  async function syncUser(email: string, displayName: string) {
    userEmail.value = email;
    userName.value = displayName;
    console.info('[backend] syncUser called for', email);
    try {
      // Find the player's row by email. A failed read (server busy, too many
      // requests, a network blip) used to fall through to "create a new
      // user", which failed too, and left the whole visit without a profile:
      // chat said "Log in to chat" to a signed in player (Ames 2026-10-25).
      // Now a failed read is tried again, and only a real "no row" creates one.
      let existing: { id: string; username?: string | null } | null = null;
      for (let attempt = 0; ; attempt++) {
        const { data, error: selectErr } = await supabase
          .from('users')
          .select('id, username')
          .eq('middleauth_email', email)
          .single();
        // PGRST116 = "no rows returned" (expected for new users)
        if (!selectErr || selectErr.code === 'PGRST116') { existing = data as any; break; }
        console.warn(`[backend] syncUser SELECT error (try ${attempt + 1}):`, selectErr.message);
        if (attempt >= 5) throw new Error('Could not read your profile: ' + selectErr.message);
        await new Promise(r => setTimeout(r, [2000, 5000, 10000, 20000, 40000][attempt]));
        if (userEmail.value !== email) return;   // signed in as someone else meanwhile
      }

      if (existing) {
        userId.value = existing.id;
        username.value = existing.username || '';
        // Mirrored to a global so error reporting can attribute a crash without
        // importing this store (it may not exist yet during a bootstrap failure).
        (window as any).__ngeUserId = existing.id;
        console.info('[backend] syncUser: found existing user', existing.id);
        // Update display name if changed
        await supabase
          .from('users')
          .update({ display_name: displayName, updated_at: new Date().toISOString() })
          .eq('id', existing.id);
      } else {
        // Create new user
        console.info('[backend] syncUser: creating new user for', email);
        const { data: newUser, error: insertErr } = await supabase
          .from('users')
          .insert({ middleauth_email: email, display_name: displayName })
          .select('id')
          .single();
        if (insertErr) throw insertErr;
        userId.value = newUser?.id ?? null;
        (window as any).__ngeUserId = newUser?.id ?? null;
        console.info('[backend] syncUser: created user', userId.value);
      }
      // Check admin status + load special badges + favorite badge after user is synced
      await checkAdmin();
      void checkBlogAuthor();
      await loadMySpecialBadges();
      await loadMyBadgeAwards();
      await loadFavoriteBadge();
    } catch (e: any) {
      console.warn('[backend] User sync failed:', e.message);
    }
  }

  // ── CAVE user-id capture (Phase B leaderboard groundwork) ───────────────
  /**
   * Read the current CAVE bearer token from localStorage, call CAVE's
   * /auth/api/v1/user/me, and persist the returned numeric `id` to
   * users.cave_user_id. Used so the leaderboard can join cell-completion
   * counts (written by AnnotationEngine into bound_tag_user tables, keyed
   * on a numeric CAVE user_id) against this Supabase users row.
   *
   * Idempotent and silent on failure — if there's no token, the endpoint
   * is unreachable, or the user already has a cave_user_id stored, this
   * just returns. Safe to call after every middleauthlogin event.
   */
  async function captureCaveUserId(): Promise<void> {
    if (!userId.value) {
      console.info('[backend] captureCaveUserId: no userId yet, skipping');
      return;
    }
    // CAVE's sticky_auth realm — same one lightbulb_service.ts reads from.
    const STICKY_AUTH_URL = 'https://global.daf-apis.com/sticky_auth';
    let token: string | null = null;
    try {
      const raw = window.localStorage.getItem(`auth_token_v2_${STICKY_AUTH_URL}`);
      if (raw) token = JSON.parse(raw).accessToken ?? null;
    } catch { /* ignore */ }
    if (!token) {
      console.info('[backend] captureCaveUserId: no CAVE token in localStorage, skipping');
      return;
    }

    // Skip if we already have an id stored (idempotency — avoids a network
    // round-trip on every login).
    try {
      const { data: existing } = await supabase
        .from('users')
        .select('cave_user_id')
        .eq('id', userId.value)
        .single();
      if (existing?.cave_user_id) {
        console.info('[backend] captureCaveUserId: already stored', existing.cave_user_id);
        return;
      }
    } catch { /* fall through to lookup */ }

    // Try the global daf-apis auth-me endpoint first, then minnie as a
    // fallback. Both are sticky_auth-backed; one of them should answer.
    const endpoints = [
      'https://global.daf-apis.com/auth/api/v1/user/me',
      'https://minnie.microns-daf.com/auth/api/v1/user/me',
    ];
    let caveId: number | null = null;
    for (const url of endpoints) {
      try {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) {
          console.info(`[backend] captureCaveUserId: ${url} → ${res.status}`);
          continue;
        }
        const me = await res.json();
        // CAVE returns { id, email, name, ... }. The id is a Postgres
        // bigint — JS Number is fine here (real CAVE ids are well under 2^53).
        if (me && typeof me.id === 'number') {
          caveId = me.id;
          console.info(`[backend] captureCaveUserId: ${url} →`, me.id, me.email);
          break;
        }
      } catch (e: any) {
        console.info(`[backend] captureCaveUserId: ${url} error`, e?.message);
      }
    }
    if (caveId === null) return;

    try {
      await supabase
        .from('users')
        .update({ cave_user_id: caveId, updated_at: new Date().toISOString() })
        .eq('id', userId.value);
      console.info('[backend] captureCaveUserId: persisted', caveId, 'for user', userId.value);
    } catch (e: any) {
      console.warn('[backend] captureCaveUserId: update failed:', e?.message);
    }
  }

  // ── Task management ───────────────────────────────────────────────────
  /** Load tasks for a dataset, with optional status filter. */
  // Incremental sync (Amy 2026-09-28: "don't go through thousands of rows each
  // time"). After the first load of a dataset only rows whose updated_at moved
  // are fetched and merged in. Every claim, release, completion and expiry
  // bumps updated_at (pilot_task_action, expire_stale_assignments), so a claim
  // or release costs one tiny query.
  //
  // A dataset of more than WHOLE_LIST_MAX tasks is paged (Ames 2026-10-25):
  // Retina's 16,000+ rows (17 reads, 10 MB) were pulled by every player who
  // opened the Cell Library while its database sat at full CPU. Such a dataset
  // loads the player's own tasks in full, the first TASK_PAGE available ones,
  // and the tab counts from count queries. Load more, search, tab switches and
  // findTasks fetch the rest as they are needed.
  let tasksSyncedDataset = '';
  let tasksSyncedAt = '';

  /** A task row names a cell. One with a blank id showed as an empty first
   *  entry in MEC's Cell Library (Krzysztof Kruk, report 223), and Claim a
   *  new cell could pick it. */
  const hasCellId = (t: ProofreadingTask) => /^[1-9]\d*$/.test(String(t.segment_id ?? '').trim());
  /** hasCellId, as a server filter. */
  const CELL_ID_RE = '^ *[1-9][0-9]* *$';
  const TASK_PAGE = 100;
  const WHOLE_LIST_MAX = 1000;
  /** Load more asks again for a few rows it already has, so a row that left
   *  the tab meanwhile does not push one past the page edge unseen. */
  const PAGE_OVERLAP = 10;
  type TaskTab = 'available' | 'claimed' | 'completed' | 'all';
  const TAB_STATUSES: Record<TaskTab, string[] | null> = {
    available: ['pending'], claimed: ['assigned', 'in_progress'], completed: ['completed'], all: null,
  };
  const freshPages = (): Record<TaskTab, { next: number; done: boolean }> => ({
    available: { next: 0, done: false }, claimed: { next: 0, done: false },
    completed: { next: 0, done: false }, all: { next: 0, done: false },
  });
  /** True when `tasks` holds only part of the dataset (see above). */
  const tasksPaged = ref(false);
  /** Server counts per tab while paged, else null. */
  const taskCounts = ref<Record<TaskTab, number> | null>(null);
  /** How far each tab has been read from the server while paged. */
  const taskPages = ref(freshPages());
  const loadingMore = ref<TaskTab | null>(null);
  /** The player whose own tasks are loaded in full. */
  let tasksMineFor = '';

  /** List order. A paged list puts the sheet's cells first, as the sheet
   *  backed Cell Library does, so its first page holds cells from the sheet. */
  const sheetRank = (t: ProofreadingTask) => (t.source_sheet_url ? 0 : 1);
  function taskOrder(a: ProofreadingTask, b: ProofreadingTask): number {
    return (tasksPaged.value ? sheetRank(a) - sheetRank(b) || String(a.source_sheet_url ?? '').localeCompare(String(b.source_sheet_url ?? '')) : 0)
      || (b.priority ?? 0) - (a.priority ?? 0)
      || String(a.created_at).localeCompare(String(b.created_at)) || a.id - b.id;
  }
  function orderedQuery(q: any) {
    return q.order('source_sheet_url', { ascending: true, nullsFirst: false })
      .order('priority', { ascending: false })
      .order('created_at', { ascending: true })
      .order('id', { ascending: true });
  }
  /** One tab's rows of a dataset, cells with an ID only. */
  function tabQuery(dataset: string, tab: TaskTab, select = '*', opts?: { count?: 'exact'; head?: boolean }) {
    let q: any = supabase.from('proofreading_tasks').select(select, opts)
      .eq('dataset', dataset).filter('segment_id', 'match', CELL_ID_RE);
    const st = TAB_STATUSES[tab];
    if (st) q = st.length === 1 ? q.eq('status', st[0]) : q.in('status', st);
    return q;
  }
  async function countTab(dataset: string, tab: TaskTab): Promise<number> {
    const { count, error: e } = await tabQuery(dataset, tab, 'id', { count: 'exact', head: true });
    if (e) throw e;
    return count ?? 0;
  }
  async function refreshTaskCounts(dataset: string) {
    const tabs: TaskTab[] = ['available', 'claimed', 'completed', 'all'];
    const got = await Promise.all(tabs.map(t => countTab(dataset, t)));
    if (dataset !== tasksSyncedDataset || !tasksPaged.value) return;
    taskCounts.value = Object.fromEntries(tabs.map((t, i) => [t, got[i]])) as Record<TaskTab, number>;
  }
  /** Merge rows into `tasks`: a known row is replaced, a new one added in order. */
  function mergeTasks(rows: ProofreadingTask[]) {
    const ok = rows.filter(hasCellId);
    if (!ok.length) return;
    const byId = new Map(tasks.value.map((t, i) => [t.id, i] as [number, number]));
    const next = tasks.value.slice();
    let added = false;
    for (const r of ok) {
      const i = byId.get(r.id);
      if (i === undefined) { byId.set(r.id, next.length); next.push(r); added = true; } else next[i] = r;
    }
    if (added) next.sort(taskOrder);
    tasks.value = next;
  }
  /** The player's own tasks in a dataset (claimed, completed), all of them. */
  async function fetchMyTasks(dataset: string): Promise<ProofreadingTask[]> {
    const out: ProofreadingTask[] = [];
    for (let from = 0; from <= 20_000; from += 1000) {
      const { data, error: e } = await supabase.from('proofreading_tasks').select('*')
        .eq('dataset', dataset).eq('assigned_to', userId.value).order('id', { ascending: true })
        .range(from, from + 999);
      if (e) throw e;
      out.push(...((data ?? []) as ProofreadingTask[]));
      if ((data ?? []).length < 1000) break;
    }
    return out;
  }
  /** Every row of a dataset, 1,000 a read (the API's cap), AT_ONCE reads at once. */
  async function fetchWholeList(dataset: string, statusFilter?: string, AT_ONCE = 4): Promise<ProofreadingTask[]> {
    const PAGE = 1000;
    const page = async (from: number) => {
      let query = supabase
        .from('proofreading_tasks')
        .select('*')
        .eq('dataset', dataset)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: true })
        .order('id', { ascending: true })
        .range(from, from + PAGE - 1);
      if (statusFilter) query = query.eq('status', statusFilter);
      const { data, error: fetchErr } = await query;
      if (fetchErr) throw fetchErr;
      return (data ?? []) as ProofreadingTask[];
    };
    const all: ProofreadingTask[] = [];
    for (let from = 0; from <= 50_000; from += PAGE * AT_ONCE) {
      const got = await Promise.all(Array.from({ length: AT_ONCE }, (_, i) => page(from + i * PAGE)));
      for (const g of got) all.push(...g);
      if (got.some(g => g.length < PAGE)) break;
    }
    return all;
  }

  async function loadTasks(dataset: string = currentDatasetTag(), statusFilter?: string, opts: { full?: boolean } = {}) {
    if (!statusFilter && !opts.full && dataset === tasksSyncedDataset && tasksSyncedAt) {
      try {
        // Signed in since the list loaded: bring in the player's own cells.
        if (tasksPaged.value && userId.value && tasksMineFor !== userId.value) {
          mergeTasks(await fetchMyTasks(dataset));
          tasksMineFor = userId.value;
        }
        const { data, error: fetchErr } = await supabase
          .from('proofreading_tasks')
          .select('*')
          .eq('dataset', dataset)
          .gte('updated_at', tasksSyncedAt)
          .order('updated_at', { ascending: true })
          .limit(1000);
        if (fetchErr) throw fetchErr;
        const fetched = (data ?? []) as ProofreadingTask[];
        if (fetched.length < 1000) {
          const since = tasksSyncedAt;
          for (const r of fetched) if (r.updated_at > tasksSyncedAt) tasksSyncedAt = r.updated_at;
          // A paged list takes changes to rows it holds, and rows that are
          // now the player's. Other rows stay unloaded; the counts follow.
          const known = new Set(tasks.value.map(t => t.id));
          mergeTasks(tasksPaged.value
            ? fetched.filter(r => known.has(r.id) || (!!userId.value && r.assigned_to === userId.value))
            : fetched);
          // The read starts at the newest change already seen, so that row
          // comes back every time: only a later one changes the counts.
          if (tasksPaged.value && fetched.some(r => r.updated_at > since)) {
            await refreshTaskCounts(dataset).catch((e: any) => console.warn('[backend] task counts not refreshed:', e?.message));
          }
          return;
        }
        // Too much changed since the last sync: fall through to a full load.
      } catch (e: any) {
        console.warn('[backend] loadTasks delta failed, doing a full load:', e.message);
      }
    }
    loading.value = true;
    error.value = '';
    try {
      if (statusFilter) {
        tasks.value = (await fetchWholeList(dataset, statusFilter)).filter(hasCellId);
      } else {
        const total = await countTab(dataset, 'all');
        if (total <= WHOLE_LIST_MAX) {
          const all = await fetchWholeList(dataset, undefined, 1);
          tasksPaged.value = false;
          taskCounts.value = null;
          tasks.value = all.filter(hasCellId);
          tasksSyncedAt = all.reduce((m, t) => (t.updated_at > m ? t.updated_at : m), '');
        } else {
          // The newest change before anything is read: the next sync starts there.
          const { data: newest, error: markErr } = await supabase.from('proofreading_tasks').select('updated_at')
            .eq('dataset', dataset).order('updated_at', { ascending: false }).limit(1);
          if (markErr) throw markErr;
          const me = userId.value;
          const [mine, firstPage, claimed, completed] = await Promise.all([
            me ? fetchMyTasks(dataset) : Promise.resolve([] as ProofreadingTask[]),
            orderedQuery(tabQuery(dataset, 'available', '*', { count: 'exact' })).range(0, TASK_PAGE - 1),
            countTab(dataset, 'claimed'),
            countTab(dataset, 'completed'),
          ]);
          if (firstPage.error) throw firstPage.error;
          const rows = (firstPage.data ?? []) as ProofreadingTask[];
          tasksPaged.value = true;
          taskPages.value = { ...freshPages(), available: { next: rows.length, done: rows.length < TASK_PAGE } };
          taskCounts.value = { available: firstPage.count ?? rows.length, claimed, completed, all: total };
          tasks.value = [];
          mergeTasks([...mine, ...rows]);
          tasksMineFor = me;
          tasksSyncedAt = String(newest?.[0]?.updated_at ?? '');
        }
        tasksSyncedDataset = dataset;
      }
    } catch (e: any) {
      error.value = e.message || 'Failed to load tasks';
      console.warn('[backend] loadTasks error:', e.message);
    }
    loading.value = false;
  }

  /** Paged list: read the next page of one tab. Returns how many rows were new. */
  async function loadMoreTasks(tab: TaskTab, dataset: string = currentDatasetTag()): Promise<number> {
    if (!tasksPaged.value || dataset !== tasksSyncedDataset || loadingMore.value) return 0;
    const p = taskPages.value[tab];
    if (p.done) return 0;
    loadingMore.value = tab;
    try {
      const from = Math.max(0, p.next - PAGE_OVERLAP);
      const want = p.next - from + TASK_PAGE;
      const { data, error: e } = await orderedQuery(tabQuery(dataset, tab)).range(from, from + want - 1);
      if (e) throw e;
      const rows = (data ?? []) as ProofreadingTask[];
      const before = tasks.value.length;
      mergeTasks(rows);
      taskPages.value = { ...taskPages.value, [tab]: { next: from + rows.length, done: rows.length < want } };
      return tasks.value.length - before;
    } catch (e: any) {
      console.warn('[backend] loadMoreTasks error:', e?.message);
      return 0;
    } finally {
      loadingMore.value = null;
    }
  }

  /** Paged list: fetch the tasks of these cells, and with `text`, the tasks
   *  whose cell ID, final ID or notes contain it (the Cell Library search). */
  async function findTasks(dataset: string, segIds: string[], text = ''): Promise<void> {
    if (!tasksPaged.value || dataset !== tasksSyncedDataset) return;
    const known = new Set(tasks.value.map(t => String(t.segment_id)));
    const want = [...new Set(segIds.map(s => String(s || '').trim()))].filter(s => /^\d+$/.test(s) && !known.has(s)).slice(0, 300);
    const reads: PromiseLike<any>[] = [];
    for (let i = 0; i < want.length; i += 100) {
      reads.push(supabase.from('proofreading_tasks').select('*').eq('dataset', dataset).in('segment_id', want.slice(i, i + 100)));
    }
    const term = text.replace(/[^\w #.-]/g, '').trim();
    if (term.length >= 3) {
      const like = `"*${term}*"`;
      reads.push(orderedQuery(tabQuery(dataset, 'all'))
        .or(`segment_id.ilike.${like},final_segment_id.ilike.${like},notes.ilike.${like}`).limit(TASK_PAGE));
    }
    if (!reads.length) return;
    const got = await Promise.all(reads);
    const rows: ProofreadingTask[] = [];
    for (const g of got) {
      if (g.error) { console.warn('[backend] findTasks error:', g.error.message); continue; }
      rows.push(...((g.data ?? []) as ProofreadingTask[]));
    }
    mergeTasks(rows);
  }

  /** Paged list: the available cells that follow `task` in list order (Claim
   *  this + next N from a search). */
  async function loadAvailableAfter(task: ProofreadingTask, n: number): Promise<void> {
    if (!tasksPaged.value || task.dataset !== tasksSyncedDataset) return;
    let q: any = orderedQuery(tabQuery(task.dataset, 'available'))
      .eq('priority', task.priority ?? 0).gte('created_at', task.created_at);
    q = task.source_sheet_url ? q.eq('source_sheet_url', task.source_sheet_url) : q.is('source_sheet_url', null);
    const { data, error: e } = await q.limit(n + 1);
    if (e) { console.warn('[backend] loadAvailableAfter error:', e.message); return; }
    mergeTasks((data ?? []) as ProofreadingTask[]);
  }

  /** Player scripts (window.eyewire) hear about claims, releases and completions. */
  function tellScripts(event: 'claim' | 'release' | 'complete', taskId: number, finalSegId?: string) {
    const t = tasks.value.find(x => x.id === taskId);
    emitScriptEvent(event, { taskId, cellId: String(finalSegId || t?.final_segment_id || t?.segment_id || ''), dataset: t?.dataset || currentDatasetTag() });
  }

  /** Claim a task — insert assignment, update task status, start heartbeat. */
  async function claimTask(taskId: number) {
    if (!userId.value) { error.value = 'Not logged in'; return false; }
    try {
      // Check if already claimed by someone else
      const task = tasks.value.find(t => t.id === taskId);
      if (task && (task.status === 'assigned' || task.status === 'in_progress') && task.assigned_to && task.assigned_to !== userId.value) {
        error.value = 'This cell is already claimed by another user';
        return false;
      }
      await taskAction('claim', { id: taskId });

      activeTaskId.value = taskId;
      startHeartbeat();
      tellScripts('claim', taskId);

      // Log to edit_log and the activity feed without holding the button.
      void Promise.resolve(logEdit({ operation: 'claim_task', task_id: taskId })).catch(() => {});
      const t = tasks.value.find(x => x.id === taskId);
      const label = t?.segment_id ? `...${t.segment_id.slice(-4)}` : `#${taskId}`;
      void Promise.resolve(postActivity(`claimed cell ${label}`)).catch(() => {});

      return true;
    } catch (e: any) {
      error.value = e.message || 'Failed to claim task';
      console.warn('[backend] claimTask error:', e.message);
      return false;
    }
  }

  /** Release the currently active task (give up / skip). */
  async function releaseTask() {
    if (!activeTaskId.value || !userId.value) return;
    const taskId = activeTaskId.value;
    try {
      // Take the player's name off the cell's row in the sheet first, while
      // the claim is still theirs (the server only writes a row for the
      // player who holds it). A sheet that cannot be reached must not stop
      // the release: the claim is let go either way.
      const mine = tasks.value.find(t => t.id === taskId);
      if (mine?.segment_id) {
        await syncCellToSheet('release', String(mine.segment_id), undefined, (mine as any).dataset)
          .catch((e: any) => console.warn('[backend] sheet not updated on release:', e?.message));
      }
      // A server that is busy or down is asked again: a failed release used
      // to leave the cell claimed while the menu said it was released.
      for (let attempt = 0; ; attempt++) {
        try { await trackSave(taskAction('release', { id: taskId })); break; }
        catch (e: any) {
          if (attempt >= 3 || !serverMayRecover(e) || Number(e?.status) === 401) throw e;
          await new Promise(r => setTimeout(r, 1000 * 2 ** attempt + Math.random() * 500));
        }
      }
      tellScripts('release', taskId);

      await logEdit({ operation: 'release_task', task_id: taskId });

      activeTaskId.value = null;
      stopHeartbeat();
      return true;
    } catch (e: any) {
      error.value = e.message || 'Could not release this claim';
      console.warn('[backend] releaseTask error:', e.message);
      return false;
    }
  }

  /** Mark a task as completed with final segment/soma data. */
  async function completeTask(taskId: number, finalSegId?: string, somaCoords?: string) {
    if (!userId.value) return;
    try {
      await taskAction('complete', { id: taskId, final_segment_id: finalSegId, soma_coords: somaCoords });
      tellScripts('complete', taskId, finalSegId);
      noteClaimFinished(taskId, tasks.value.find(t => t.id === taskId)?.kind);

      await logEdit({
        operation: 'complete_task',
        task_id: taskId,
        metadata: { final_segment_id: finalSegId, soma_coords: somaCoords },
      });

      // Find segment_id for the feed
      const task = tasks.value.find(t => t.id === taskId);
      const segLabel = task?.segment_id ? `...${task.segment_id.slice(-4)}` : `#${taskId}`;
      await postActivity(`completed ${segLabel}`);

      if (activeTaskId.value === taskId) {
        activeTaskId.value = null;
        stopHeartbeat();
      }

      // Update user stats timestamp
      await supabase
        .from('users')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', userId.value);
    } catch (e: any) {
      console.warn('[backend] completeTask error:', e.message);
      error.value = e.message || 'Could not complete this cell';
      throw e;
    }
  }

  // ── Heartbeat — extends assignment expiry every 10 min ────────────────
  function startHeartbeat() {
    stopHeartbeat();
    heartbeatInterval = setInterval(async () => {
      if (!activeTaskId.value || !userId.value) return;
      try {
        await taskAction('heartbeat', { id: activeTaskId.value });
      } catch { /* heartbeat failure is non-fatal */ }
    }, 10 * 60 * 1000); // every 10 minutes
  }

  function stopHeartbeat() {
    if (heartbeatInterval) { clearInterval(heartbeatInterval); heartbeatInterval = null; }
  }

  // ── Edit logging ──────────────────────────────────────────────────────
  /**
   * Report one thing the player did. The SERVER records it and moves the
   * counters (edits, cells, streak) in one step, and answers with the
   * counters as they now stand; the browser never writes a total
   * (leaderboard audit, 2026-10-05). A split or merge carries the graph
   * server's operation id, which the server looks up before counting.
   *
   * Two older arrangements are still handled, so the app and the server can
   * be updated in either order: a server from before this change (no
   * 'activity.log' action), and a database without the counting function
   * yet (the server answers counted: false). In both, the counters are
   * updated from here as they always were.
   */
  /** Send one activity row, waiting out the server's "Please wait". */
  async function sendActivity(row: Record<string, any>): Promise<any> {
    for (let attempt = 0; ; attempt++) {
      try { return await secureWrite('activity.log', { row }); }
      catch (e: any) {
        if (attempt >= 3 || !/please wait/i.test(e?.message ?? '')) throw e;
        await new Promise(r => setTimeout(r, 1500 * (attempt + 1) + Math.random() * 1000));
      }
    }
  }

  /** The server recorded a row and answered with the counters: show them. */
  function applyServerCounts(result: any) {
    // The server's numbers are the numbers.
    useUserStatsStore().setStats({
      editsAllTime: result.total_edits ?? 0,
      mergesAllTime: result.total_merges ?? 0,
      splitsAllTime: result.total_splits ?? 0,
      cellsSubmitted: result.cells_completed ?? 0,
      currentStreak: Math.max(result.current_streak ?? 0, streakFloor),
      longestStreak: result.longest_streak ?? 0,
      ...(result.last_edit_date ? { lastEditDate: result.last_edit_date } : {}),
      ...(typeof result.total_days === 'number' ? { totalDays: result.total_days } : {}),
    });
    if (result.recorded) {
      celebrateDays(result.days_before, result.total_days, false);
      if ((result.current_streak ?? 0) !== (result.streak_before ?? 0)) sendStreakMilestone(result.current_streak).catch(() => {});
      if ((result.edits_before ?? 0) < 3 && (result.total_edits ?? 0) >= 3) sendThirdEditThanks().catch(() => {});
    }
  }

  // Rows the server did not take wait in this browser and go out later,
  // only under the sign in of the player who made them.
  startActivityOutbox({
    currentUser: () => userId.value,
    send: row => sendActivity(row),
    onRecorded: (result, row) => {
      if (!result?.counted || !userId.value) return;
      document.dispatchEvent(new CustomEvent('nge:edit-logged', { detail: { operation: row.operation } }));
      applyServerCounts(result);
    },
  });

  async function logEdit(entry: EditLogEntry) {
    try {
      const uid = entry.user_id ?? userId.value;
      if (!uid) {
        console.warn('[backend] logEdit skipped — no userId set (syncUser may not have run yet)');
        return;
      }
      const logRow = {
        task_id: entry.task_id ?? activeTaskId.value,
        operation: entry.operation,
        segment_before: entry.segment_before ?? null,
        segment_after: entry.segment_after ?? null,
        coordinates: entry.coordinates ?? null,
        metadata: entry.metadata ?? null,
        dataset: entry.dataset ?? currentDatasetTag(),
        success: entry.success ?? true,
        // The server records a row once per key, so this row can be sent
        // again after a failure without counting twice. A split or merge is
        // keyed by its graph operation id instead (activity-policy.js).
        ...(entry.operation === 'split' || entry.operation === 'merge' ? {} : { op_key: newOpKey() }),
      };
      let result: any = null;
      try {
        // The server turns a record away with "Please wait" when many land in
        // the same moment (a batch of up to 20 merges finishing together). It
        // was refused before anything was written, so sending it again cannot
        // count an edit twice. Without this the edit was simply never counted.
        result = await trackSave(sendActivity(logRow));
      } catch (e: any) {
        if (!/unknown action/i.test(e?.message ?? '')) {
          // The server is down or did not answer: the edit waits in this
          // browser and is sent again (activity_outbox.ts). It used to be
          // dropped here with only this console line, so the split or merge
          // was in CAVE but never counted (save failures audit, 2026-10-23).
          if (serverMayRecover(e) && queueActivity(uid, logRow)) {
            console.warn('[backend] activity not recorded yet, will retry:', e?.message);
          } else {
            console.warn('[backend] activity not recorded:', e?.message);
          }
          return;
        }
        // A server from before this change: the plain insert it expects.
        const { op_key: _key, ...plain } = logRow as any;
        supabase.from('edit_log').insert({ ...plain, user_id: uid }).then(
          ({ error }) => { if (error) console.warn('[backend] edit_log insert failed:', error.message); },
          () => {},
        );
      }

      if (!userId.value || !(entry.success ?? true)) return;
      const op = entry.operation;
      // Top bar staging listens: a first edit on your own brings icons in.
      document.dispatchEvent(new CustomEvent('nge:edit-logged', { detail: { operation: op } }));

      if (result?.counted) {
        applyServerCounts(result);
        return;
      }

      // ── Counting function not installed yet: as before ──
      const diff = 1;

      // Single SELECT to get all stats we need
      const { data: row } = await supabase.from('users')
        .select('total_edits, total_merges, total_splits, cells_completed, current_streak, longest_streak, last_edit_date')
        .eq('id', userId.value).single();
      if (!row) return;

      const today = new Date().toISOString().slice(0, 10);
      const updates: Record<string, any> = {
        updated_at: new Date().toISOString(),
        last_edit_date: today,
      };

      // Increment operation-specific counters
      if (op === 'merge') {
        updates.total_edits = (row.total_edits || 0) + diff;
        updates.total_merges = (row.total_merges || 0) + diff;
      } else if (op === 'split') {
        updates.total_edits = (row.total_edits || 0) + diff;
        updates.total_splits = (row.total_splits || 0) + diff;
      } else if (op === 'mark_complete') {
        updates.cells_completed = (row.cells_completed || 0) + 1;
      } else if (op === 'unmark_complete') {
        updates.cells_completed = Math.max(0, (row.cells_completed || 0) - 1);
      }

      // Calculate streak: consecutive calendar days with any activity (edits or completions)
      const lastDate = row.last_edit_date;
      let streak = row.current_streak || 0;
      let longest = row.longest_streak || 0;
      if (lastDate !== today) {
        // Different day — check if it's consecutive
        const lastMs = lastDate ? new Date(lastDate + 'T00:00:00').getTime() : 0;
        const todayMs = new Date(today + 'T00:00:00').getTime();
        const dayGap = Math.round((todayMs - lastMs) / 86400000);
        if (dayGap === 1) {
          streak += 1; // consecutive day
        } else if (dayGap > 1 || !lastDate) {
          streak = 1; // streak broken, restart
        }
        longest = Math.max(longest, streak);
        updates.current_streak = streak;
        updates.longest_streak = longest;
      }

      await supabase.from('users').update(updates).eq('id', userId.value);

      // The day's edit carried the streak onto a milestone: a note in the
      // bell, not a popup (Ames 2026-10-05). After the save, so the server
      // sees the new streak when it checks.
      if (updates.current_streak !== undefined && updates.current_streak !== (row.current_streak || 0)) {
        sendStreakMilestone(updates.current_streak).catch(() => {});
      }

      // Third edit: a thank you from Nurro, once, at the moment of crossing.
      if ((row.total_edits || 0) < 3 && (updates.total_edits || 0) >= 3) {
        sendThirdEditThanks().catch(() => {});
      }

      // Immediately update local stats store for instant UI feedback
      const statsStore = useUserStatsStore();
      const localUpdates: Record<string, any> = {};
      if (updates.total_edits !== undefined) localUpdates.editsAllTime = updates.total_edits;
      if (updates.total_merges !== undefined) localUpdates.mergesAllTime = updates.total_merges;
      if (updates.total_splits !== undefined) localUpdates.splitsAllTime = updates.total_splits;
      if (updates.cells_completed !== undefined) localUpdates.cellsSubmitted = updates.cells_completed;
      if (updates.current_streak !== undefined) localUpdates.currentStreak = updates.current_streak;
      if (updates.longest_streak !== undefined) localUpdates.longestStreak = updates.longest_streak;
      localUpdates.lastEditDate = today;
      statsStore.setStats(localUpdates);
    } catch (e: any) {
      console.warn('[backend] logEdit error:', e.message);
    }
  }

  /** A streak milestone, as a notification to yourself. The server allows
   *  only these titles, only when the saved streak has reached the number,
   *  and never the same one twice within six days. */
  async function sendStreakMilestone(days: number) {
    // The chip agrees with the note (lalonso: "shows 1 when my notification
    // says 2"): a slower answer read before the streak grew can't undo it.
    streakFloor = Math.max(streakFloor, days);
    const stats = useUserStatsStore();
    if (stats.stats.currentStreak < days) stats.setStats({ currentStreak: days });
    if (!isStreakMilestone(days) || !userId.value) return;
    const next = nextStreakMilestone(days);
    const art = 'https://raw.githubusercontent.com/seung-lab/ng-extend/eyewire-ii-community/static/nurro';
    await secureWrite('notification.self', {
      title: `🔥 ${days}-Day Streak!`,
      body: `${streakLine(days)} Next milestone: ${next} days. Come back tomorrow to keep the flame going.`,
      // Stand-in art until the streak's own Nurro arrives.
      image_url: `${art}/nurro-dance.png`,
      thumbnail_url: `${art}/nurro-dance.png`,
    });
    loadNotifications().catch(() => {});
  }

  /**
   * The Days track (Ames 2026-10-06). When the total of days shown up passes
   * a step of its ladder, a note lands in the bell; every 100 days and every
   * full year also get the big centre celebration. A first count of old days
   * is a correction, not a day earned just now, so it celebrates nothing.
   */
  const DAY_NOTES_OFF = true;
  function celebrateDays(before: unknown, now: unknown, recounted: boolean) {
    if (recounted || typeof before !== 'number' || typeof now !== 'number' || now <= before) return;
    // Loyalty achievements listen for this (AchievementToast.vue); the note is
    // kept for a listener that is not mounted yet.
    try {
      (window as any).__ngeLoyaltyDays = { before, now };
      document.dispatchEvent(new CustomEvent('nge:loyalty-days', { detail: { before, now } }));
    } catch { /* no document */ }
    // The Loyalty Achievements (badge_definitions.ts) now mark these same days
    // with their own note, art and celebration, so the older "Day 60 at
    // EyeWire II" note doubled up (Ames 2026-10-28: "is this right? two
    // notifications?"). It is no longer sent; the profile still counts the
    // days to the next step.
    if (DAY_NOTES_OFF) return;
    const reached = dayMilestonesReached(before, now);
    const days = reached[reached.length - 1];
    if (!days || !userId.value) return;
    const years = wholeYears(days);
    const big = isBigDayMilestone(days);
    const art = 'https://raw.githubusercontent.com/seung-lab/ng-extend/eyewire-ii-community/static/nurro';
    const title = `📅 Day ${days} at EyeWire II`;
    const body = years
      ? `${years} of days spent mapping the brain with us. There are no words. Thank you! Next milestone: day ${nextDayMilestone(days)}.`
      : big
        ? `${days} days of showing up for science. That is extraordinary. Thank you! Next milestone: day ${nextDayMilestone(days)}.`
        : `You have shown up on ${days} different days. Thank you for coming back! Next milestone: day ${nextDayMilestone(days)}.`;
    secureWrite('notification.self', { title, body, image_url: `${art}/nurro-dance.png`, thumbnail_url: `${art}/nurro-dance.png` })
      .then(() => loadNotifications().catch(() => {}), () => {});
    if (big) pendingBadgeCelebration.value = { title: years ? `${years} at EyeWire II` : `Day ${days} at EyeWire II`, body, imageUrl: `${art}/nurro-dance.png` };
  }

  /** Total days shown up for any player, or null when it can not be read
   *  (before supabase-days-track.sql is run the column does not exist). */
  async function loadDayTotal(id: string): Promise<number | null> {
    try {
      const { data, error } = await supabase.from('users').select('total_days').eq('id', id).single();
      return error || !data ? null : Number((data as any).total_days) || 0;
    } catch { return null; }
  }

  /** When a player was given edit access to each dataset, from the chat line
   *  each grant gets (functions/access-request.js, rows keyed access:<id>,
   *  grants since 2026-10-30). {datasetId: ISO time}; {} when unreadable. */
  async function loadAccessGrants(id: string): Promise<Record<string, string>> {
    try {
      const { data, error } = await supabase.from('chat_messages').select('dataset,created_at')
        .eq('user_id', id).like('dataset', 'access:%').limit(20);
      if (error || !data) return {};
      const out: Record<string, string> = {};
      for (const r of data as any[]) {
        const ds = String(r.dataset || '').slice('access:'.length);
        if (ds && (!out[ds] || r.created_at < out[ds])) out[ds] = r.created_at;
      }
      return out;
    } catch { return {}; }
  }

  /** A personal "thank you, for science" notification after someone's third
   *  edit. Guarded so a double count or a second tab can never send two. */
  async function sendThirdEditThanks() {
    const uid = userId.value;
    if (!uid) return;
    const key = `nge-thanks-3-edits-${uid}`;
    try { if (localStorage.getItem(key)) return; localStorage.setItem(key, '1'); } catch {}
    const title = '💙 Thank you, for science!';
    const { data: already } = await supabase.from('notifications')
      .select('id').eq('target_type', 'user').eq('target_id', uid).eq('title', title).limit(1);
    if (already?.length) return;
    const art = 'https://raw.githubusercontent.com/seung-lab/ng-extend/eyewire-ii-community/static/nurro';
    // The server checks the edit count and that this goes to the caller only.
    await secureWrite('notification.self', {
      title,
      body: "You've made 3 edits. Every one of them helps map the brain, and real scientists will use the cells you fix. Thank you for giving your time to science!",
      thumbnail_url: 'https://javthknksdcrlhiaaptj.supabase.co/storage/v1/object/public/admin-uploads/nurro/guide-avatar.png',
      image_url: `${art}/nurro-thank-you-science.jpg`,
    });
    loadNotifications().catch(() => {});
  }

  // ── Activity feed ─────────────────────────────────────────────────────
  /** Post an action to the activity feed. */
  async function postActivity(action: string, segmentId?: string) {
    try {
      await supabase.from('activity_feed').insert({
        user_id: userId.value,
        user_name: userName.value || userEmail.value?.split('@')[0] || 'Anonymous',
        action,
        segment_id: segmentId ?? null,
      });
    } catch (e: any) {
      console.warn('[backend] postActivity error:', e.message);
    }
  }

  /** Subscribe to realtime activity feed updates. */
  function subscribeToFeed() {
    if (feedSubscription) return; // already subscribed
    // Load recent feed items
    supabase
      .from('activity_feed')
      .select('*')
      .order('timestamp', { ascending: false })
      .limit(50)
      .then(({ data }) => {
        if (data) activityFeed.value = data.reverse();
      });

    // Subscribe to new inserts
    feedSubscription = supabase
      .channel('activity_feed_realtime')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'activity_feed',
      }, (payload: any) => {
        activityFeed.value.push(payload.new as ActivityFeedItem);
        // Keep only last 100 items in memory
        if (activityFeed.value.length > 100) {
          activityFeed.value = activityFeed.value.slice(-100);
        }
      })
      .subscribe();
  }

  /** Unsubscribe from realtime feed. */
  function unsubscribeFromFeed() {
    if (feedSubscription) {
      supabase.removeChannel(feedSubscription);
      feedSubscription = null;
    }
  }

  // ── Import from Google Sheet (reuses parseCsv from queue store) ─────
  /** Batch-import tasks from a Google Sheet CSV into Supabase. */
  async function importFromGoogleSheet(url: string, dataset: string = currentDatasetTag()) {
    loading.value = true;
    error.value = '';
    try {
      // Convert to CSV export URL
      let csvUrl = url;
      const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
      if (match) {
        const gidMatch = url.match(/gid=(\d+)/);
        const gid = gidMatch ? gidMatch[1] : '0';
        csvUrl = `https://docs.google.com/spreadsheets/d/${match[1]}/export?format=csv&gid=${gid}`;
      }

      const res = await fetch(csvUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();

      // Parse CSV lines (simple split — Google Sheets CSV export is well-formed)
      const lines = text.split('\n').map(l => l.trim()).filter(l => l);
      if (lines.length < 2) { error.value = 'Sheet is empty'; loading.value = false; return; }

      // Find header
      let headerIdx = 0;
      for (let i = 0; i < Math.min(lines.length, 10); i++) {
        if (lines[i].toLowerCase().includes('segment')) { headerIdx = i; break; }
      }

      const header = lines[headerIdx].split(',').map(h => h.toLowerCase().replace(/[^a-z0-9]/g, ''));
      const col = (name: string) => header.findIndex(h => h.includes(name));
      const iSeg = col('segmentid') >= 0 ? col('segmentid') : col('segment');
      const iNuc = col('nuccoord') >= 0 ? col('nuccoord') : col('nuc');
      const iSoma = col('somacoord') >= 0 ? col('somacoord') : col('soma');
      const iNotes = col('note');

      if (iSeg < 0) { error.value = 'No "Segment ID" column found'; loading.value = false; return; }

      const toInsert: any[] = [];
      for (let r = headerIdx + 1; r < lines.length; r++) {
        const fields = lines[r].split(',').map(f => f.trim());
        const segId = fields[iSeg] || '';
        if (!segId || !/^\d+$/.test(segId)) continue;

        // Parse nucleus coords into claim point anchor
        const nucRaw = iNuc >= 0 ? fields[iNuc] || '' : '';
        const nucParts = nucRaw.split(',').map(p => parseFloat(p.trim()));
        const hasNuc = nucParts.length >= 3 && nucParts.every(n => !isNaN(n));

        toInsert.push({
          segment_id: segId,
          dataset,
          nucleus_coords: nucRaw || null,
          soma_coords: iSoma >= 0 ? fields[iSoma] || null : null,
          notes: iNotes >= 0 ? fields[iNotes] || null : null,
          source_sheet_url: url,
          // Point-in-space anchor from nucleus coordinates
          claim_point_x: hasNuc ? Math.round(nucParts[0]) : null,
          claim_point_y: hasNuc ? Math.round(nucParts[1]) : null,
          claim_point_z: hasNuc ? Math.round(nucParts[2]) : null,
        });
      }

      if (toInsert.length === 0) { error.value = 'No valid segments found'; loading.value = false; return; }

      // Batch insert (Supabase handles up to 1000 rows per insert)
      const batchSize = 100;
      for (let i = 0; i < toInsert.length; i += batchSize) {
        const batch = toInsert.slice(i, i + batchSize);
        const { error: insertErr } = await supabase.from('proofreading_tasks').insert(batch);
        if (insertErr) throw insertErr;
      }

      console.info(`[backend] Imported ${toInsert.length} tasks from Google Sheet`);
      // Reload tasks
      await loadTasks(dataset);
    } catch (e: any) {
      error.value = e.message || 'Failed to import sheet';
      console.warn('[backend] importFromGoogleSheet error:', e.message);
    }
    loading.value = false;
  }

  // ── A visit counts as a day of the streak (Ames 2026-10-06) ──────────
  // Once per local day, the server is told the player opened the game and in
  // which time zone; it moves the streak on the player's own calendar
  // (supabase-streak-local-days.sql). Days used to be UTC days, which end at
  // 8 pm in New York, so an evening session broke or skipped a day.
  const VISIT_KEY = 'nge-streak-visit';
  let streakFloor = 0;
  let visitStreak: { currentStreak: number; longestStreak: number; lastEditDate: string; totalDays?: number } | null = null;
  let visiting = false;
  async function recordVisit() {
    const uid = userId.value;
    if (!uid || visiting) return;
    let tz = '';
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { /* very old browser */ }
    const now = new Date();
    const day = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const key = `${uid}|${day}|${tz}`;
    try { if (localStorage.getItem(VISIT_KEY) === key) return; } catch { /* private mode: ask each time */ }
    visiting = true;
    try {
      const r: any = await secureWrite('activity.visit', { tz });
      if (!r || typeof r.current_streak !== 'number') return;   // SQL not installed yet
      if (userId.value !== uid) return;
      visitStreak = { currentStreak: r.current_streak, longestStreak: r.longest_streak ?? 0, lastEditDate: r.last_edit_date || '',
        ...(typeof r.total_days === 'number' ? { totalDays: r.total_days } : {}) };
      useUserStatsStore().setStats(visitStreak);
      try { localStorage.setItem(VISIT_KEY, key); } catch { /* */ }
      // A recount of old days is a correction, not a day earned just now.
      if (!r.recounted && r.current_streak !== (r.streak_before ?? 0)) sendStreakMilestone(r.current_streak).catch(() => {});
      celebrateDays(r.days_before, r.total_days, !!r.days_recounted);
    } catch (e: any) {
      console.warn('[backend] visit not counted:', e?.message);
    } finally {
      visiting = false;
    }
  }
  watch(userId, id => { visitStreak = null; if (id) void recordVisit(); }, { immediate: true });
  // A tab left open overnight: the next day counts when the player returns to it.
  document.addEventListener('visibilitychange', () => { if (!document.hidden) void recordVisit(); });
  setInterval(() => { if (!document.hidden) void recordVisit(); }, 10 * 60 * 1000);

  // ── Load user stats from Supabase → local store ──────────────────────
  /** Hydrate local stats store from Supabase users table. Call after login. */
  async function loadUserStats() {
    if (!userId.value) return;
    try {
      const { data } = await supabase
        .from('users')
        .select('total_edits, total_merges, total_splits, cells_completed, current_streak, longest_streak, last_edit_date')
        .eq('id', userId.value)
        .single();
      if (data) {
        const statsStore = useUserStatsStore();
        statsStore.setStats({
          editsAllTime: data.total_edits || 0,
          mergesAllTime: data.total_merges || 0,
          splitsAllTime: data.total_splits || 0,
          cellsSubmitted: data.cells_completed || 0,
          currentStreak: Math.max(data.current_streak || 0, streakFloor),
          longestStreak: data.longest_streak || 0,
          lastEditDate: data.last_edit_date || '',
        });
        // Today's visit may have been counted while this read was on its way.
        if (visitStreak) statsStore.setStats(visitStreak);
        // Total days has its own read: it is absent until its SQL is run.
        else void loadDayTotal(userId.value).then(n => { if (n != null && n > 0) statsStore.setStats({ totalDays: n }); });
        console.info('[backend] Loaded user stats from Supabase');
      }
    } catch (e: any) {
      console.warn('[backend] loadUserStats error:', e.message);
    }
  }

  // ── Load another user's profile data ────────────────────────────────
  async function loadUserProfile(targetUserId: string): Promise<any | null> {
    try {
      const { data: user } = await supabase
        .from('users')
        // NOTE: middleauth_email is deliberately NOT selected. This loads OTHER
        // users' profiles, and fetching their address shipped it to the client
        // (visible in devtools) even once it stopped being rendered.
        .select('id, display_name, username, flag, total_edits, total_merges, total_splits, cells_completed, current_streak, longest_streak, last_edit_date, favorite_badge')
        .eq('id', targetUserId)
        .single();
      if (!user) return null;

      // Load their special badges
      const { data: awards } = await supabase
        .from('special_badge_awards')
        .select('*, badge:special_badges(*)')
        .eq('user_id', targetUserId);

      return { ...user, specialBadges: awards || [] };
    } catch (e: any) {
      console.warn('[backend] loadUserProfile error:', e.message);
      return null;
    }
  }

  // ── Leaderboard: top users from Supabase ────────────────────────────
  const leaderboard: Ref<any[]> = ref([]);
  /** The signed in player's true place in each ranking, by view column
   *  (1 = first). The board shows the top 50 of a ranking; a player below
   *  that still sees their own row with this rank (Ames 2026-10-05). */
  const leaderboardMyRanks: Ref<Record<string, number>> = ref({});
  /** What the board can show right now (leaderboard audit, 2026-10-05).
   *  'ok': every ranking was read. 'alltime-only': the windowed view could
   *  not be read, so only the saved all-time totals are known; the 24 hour
   *  and 7 day boards say so instead of showing zeros. 'unavailable':
   *  nothing could be read. */
  const leaderboardState: Ref<'loading' | 'ok' | 'alltime-only' | 'unavailable'> = ref('loading');
  /** When the numbers on the board were read (ms). */
  const leaderboardAt: Ref<number> = ref(0);

  /** The rest of one player's row (all-time totals, streaks) for the
   *  board's card: read when the card opens, not with every refresh. */
  const DETAIL_COLS = 'id, total_edits, total_merges, total_splits, cells_completed, longest_streak';
  async function loadLeaderboardDetail(id: string): Promise<boolean> {
    const { data, error } = await supabase.from('user_edit_counts').select(DETAIL_COLS).eq('id', id).maybeSingle();
    if (error || !data) return false;
    const i = leaderboard.value.findIndex((u: any) => u.id === id);
    if (i >= 0) leaderboard.value[i] = { ...leaderboard.value[i], ...(data as any) };
    return i >= 0;
  }

  async function loadLeaderboard() {
    // Pull from the `user_edit_counts` view so we get distinct edits_24h,
    // edits_week, and edits_alltime per user (used by the leaderboard
    // panel's three tabs). Falls back to the `users` table if the view
    // hasn't been deployed yet.
    try {
      // The board has six rankings (Edits or Cells, over 24 hours, 7 days or
      // all time). Fetching only the top 50 by all-time edits left out anyone
      // strong on Cells or in a recent window but lower on all-time edits
      // (Ames 2026-10-05). Fetch the top 50 of EACH ranking and merge them.
      // Only what the board draws (the rows show the current streak). A
      // player's all-time totals and longest streak are read when their card
      // opens (loadLeaderboardDetail). Six selects
      // of 16 columns, every minute, in every tab with the board open, were
      // most of Pyr's Supabase egress (2026-10-15).
      const COLS = 'id, display_name, flag, current_streak, edits_24h, edits_week, edits_alltime, completions_24h, completions_week, completions_alltime';
      const RANKINGS = ['edits_alltime', 'edits_week', 'edits_24h', 'completions_alltime', 'completions_week', 'completions_24h'];
      const me = userId.value;
      // One read instead of six: leaderboard_board holds the union of the
      // six top 50s (supabase-leaderboard-board.sql). Until that view is
      // installed, the six selects.
      const [mine, board] = await Promise.all([
        me ? supabase.from('user_edit_counts').select(COLS).eq('id', me).maybeSingle() : Promise.resolve({ data: null, error: null } as any),
        supabase.from('leaderboard_board').select(COLS),
      ]);
      const results = !board.error ? [board] : await Promise.all(
        // Ties in a fixed order (by id), so the same 50 come back every time.
        RANKINGS.map(col => supabase.from('user_edit_counts').select(COLS).order(col, { ascending: false }).order('id', { ascending: true }).limit(50)));
      // One ranking that could not be read is the board not being readable:
      // merging the rest would quietly leave its players out.
      const error = results.find(r => r.error)?.error ?? null;
      const data = error ? null : (() => {
        const byId = new Map<string, any>();
        for (const r of results) for (const u of (r.data ?? []) as any[]) if (!byId.has(u.id)) byId.set(u.id, u);
        if (mine?.data && !byId.has(mine.data.id)) byId.set(mine.data.id, mine.data);
        return [...byId.values()];
      })();
      // My place in each ranking: one more than the number of players ahead.
      if (!error && mine?.data) {
        const row: any = mine.data;
        void Promise.all(RANKINGS.map(col =>
          supabase.from('user_edit_counts').select('id', { count: 'exact', head: true }).gt(col, row[col] ?? 0)))
          .then(counts => {
            const ranks: Record<string, number> = {};
            counts.forEach((c: any, i) => { if (!c.error && typeof c.count === 'number') ranks[RANKINGS[i]] = c.count + 1; });
            leaderboardMyRanks.value = ranks;
          }, () => {});
      }
      if (!error && data) {
        leaderboard.value = data;
        leaderboardState.value = 'ok';
        leaderboardAt.value = Date.now();
        return;
      }
      if (error) {
        console.warn('[backend] user_edit_counts view query failed, falling back to users:', error.message);
      }
    } catch (e: any) {
      console.warn('[backend] loadLeaderboard view error:', e.message);
    }
    // The windowed view could not be read. The saved all-time totals can
    // still be shown, but the recent windows are NOT KNOWN: they are left
    // null, never zero, and the board says so.
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, display_name, flag, total_edits, total_merges, total_splits, cells_completed, current_streak, longest_streak')
        .order('total_edits', { ascending: false }).order('id', { ascending: true })
        .limit(50);
      if (!error && data) {
        leaderboard.value = data.map((u: any) => ({
          ...u,
          edits_24h: null,
          edits_week: null,
          edits_alltime: u.total_edits ?? 0,
          completions_24h: null,
          completions_week: null,
          completions_alltime: u.cells_completed ?? 0,
        }));
        leaderboardState.value = 'alltime-only';
        leaderboardAt.value = Date.now();
        return;
      }
    } catch (e: any) {
      console.warn('[backend] loadLeaderboard fallback error:', e.message);
    }
    // Keep whatever was on the board; just say it could not be refreshed.
    leaderboardState.value = 'unavailable';
  }

  // ── Weekly podium counts: how many times a user finished top-3 ──────
  /** Result shape: { gold, silver, bronze } counts for one user.
   *  metric defaults to 'edits' for backward compatibility. Pass 'completions'
   *  for the cell-completions podium. The weekly_winners table now carries
   *  a `metric` column (Phase B) so both podiums coexist. */
  async function loadWeeklyPodium(
      targetUserId: string,
      metric: 'edits' | 'completions' = 'edits',
  ): Promise<{ gold: number; silver: number; bronze: number }> {
    const empty = { gold: 0, silver: 0, bronze: 0 };
    if (!targetUserId) return empty;
    try {
      // Tolerant query: select metric so legacy rows (no column) and migrated
      // rows ('edits' / 'completions') both work. We filter client-side so
      // pre-migration rows (where metric IS NULL) are treated as 'edits'.
      const { data, error } = await supabase
        .from('weekly_winners')
        .select('rank, metric')
        .eq('user_id', targetUserId);
      if (error) {
        console.warn('[backend] loadWeeklyPodium error:', error.message);
        return empty;
      }
      const counts = { gold: 0, silver: 0, bronze: 0 };
      for (const row of (data ?? [])) {
        const rowMetric = (row as any).metric ?? 'edits';
        if (rowMetric !== metric) continue;
        if (row.rank === 1) counts.gold++;
        else if (row.rank === 2) counts.silver++;
        else if (row.rank === 3) counts.bronze++;
      }
      return counts;
    } catch (e: any) {
      console.warn('[backend] loadWeeklyPodium failed:', e.message);
      return empty;
    }
  }

  /**
   * Persist the profile fields a user edits in Settings (flag, bio).
   *
   * Settings previously only wrote to localStorage via useUserPreferencesStore,
   * so a saved flag/bio never reached Supabase — the profile and leaderboard
   * read `users.flag`, so the change appeared not to save at all. `syncStats`
   * would have written these, but nothing ever called it.
   *
   * Deliberately NARROW: it must not push edit/merge/split counts. Those are
   * CAVE-derived, and having the client overwrite them with locally-inferred
   * numbers is exactly the bug we're removing.
   */
  async function saveProfileFields(fields: { flag?: string; bio?: string }) {
    if (!userId.value) return;
    const update: Record<string, any> = { updated_at: new Date().toISOString() };
    if (fields.flag !== undefined) update.flag = fields.flag;
    if (fields.bio !== undefined) update.bio = fields.bio;
    const { error } = await supabase.from('users').update(update).eq('id', userId.value);
    if (error) console.warn('[settings] saveProfileFields failed:', error.message);
  }

  // ── Sync user stats to Supabase ───────────────────────────────────────
  /** Push local stats to the users table. Call periodically or on significant events.
   *  NOTE: currently unreferenced. Do not wire this up for stats — see the
   *  CAVE-sourced stats work; use saveProfileFields for profile edits. */
  async function syncStats() {
    if (!userId.value) return;
    const statsStore = useUserStatsStore();
    const prefsStore = useUserPreferencesStore();
    try {
      await supabase
        .from('users')
        .update({
          total_edits: statsStore.stats.editsAllTime,
          total_merges: statsStore.stats.mergesAllTime,
          total_splits: statsStore.stats.splitsAllTime,
          current_streak: statsStore.stats.currentStreak,
          longest_streak: statsStore.stats.longestStreak,
          last_edit_date: statsStore.stats.lastEditDate || null,
          flag: prefsStore.prefs.flag,
          bio: prefsStore.prefs.bio,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId.value);
    } catch (e: any) {
      console.warn('[backend] syncStats error:', e.message);
    }
  }

  // ── Point-in-space claim helpers ──────────────────────────────────────
  const MAX_CLAIMS = 8;  // must match pilot_task_action in supabase-pilot-access.sql

  function pointKey(pt: ClaimPoint): string { return `${pt[0]},${pt[1]},${pt[2]}`; }

  /** The current root of every claim that carries a supervoxel, by task id,
   *  so a claimed cell is still recognised after edits (isTaskCell). */
  const currentRoots = ref<Record<number, string>>({});

  /** Look up the current root of every claim that carries a supervoxel.
   *
   *  It used to WRITE that root into the claim's segment_id, here and in the
   *  database. But segment_id is how a claim is matched to its row in the
   *  cell list and the sheet, and since 10-05 Cell Library claims carry a
   *  supervoxel too, so the first edit to a claimed cell broke the match:
   *  completing it said the cell was not in the sheet, and the claim showed as
   *  a second cell (annkri, report 150, 2026-10-16). The root now lives beside
   *  the claim instead; repairListedIds puts back the IDs already rewritten. */
  async function refreshSegmentIds(): Promise<void> {
    const tasksWithSv = tasks.value.filter(t => t.supervoxel_id);
    if (!tasksWithSv.length) return;

    const svIds = tasksWithSv.map(t => t.supervoxel_id!);
    const resolved = await getRootsFromSupervoxels(svIds);
    const next = { ...currentRoots.value };
    for (const task of tasksWithSv) {
      const root = resolved.get(task.supervoxel_id!);
      // An anchor on another cell (an old cell-menu claim took whatever was
      // selected) must not make that cell this claim.
      if (root && await anchorFits(task, root)) next[task.id] = root;
      else delete next[task.id];
    }
    currentRoots.value = next;
  }

  /** Later versions of a listed cell, asked once per ID per visit. */
  const descendantsSeen = new Map<string, Promise<string[] | null>>();
  /** True when `root` (where a claim's anchor is now) is the claim's cell: its
   *  listed ID or a later version of it. Unknown (the lookup failed) counts as
   *  true, as before. */
  async function anchorFits(t: ProofreadingTask, root: string): Promise<boolean> {
    const listed = String(t.segment_id || '');
    if (!listed || root === listed) return true;
    let ends = descendantsSeen.get(listed);
    if (!ends) { ends = latestDescendants(listed).catch(() => null); descendantsSeen.set(listed, ends); }
    const found = await ends;
    return !found || found.includes(root);
  }

  /** True when this claim is the cell `segId`: the ID it was listed under, or
   *  the cell's ID now, after edits. */
  function isTaskCell(t: ProofreadingTask, segId: string): boolean {
    return t.segment_id === segId || currentRoots.value[t.id] === segId || liveRoots.value[t.id] === segId;
  }

  /** Put back the listed ID of my open claims whose segment_id was rewritten
   *  to a later root (see refreshSegmentIds). `listed`: the cell list's
   *  segment IDs. A claim is changed only when exactly one listed ID is in
   *  its edit history and no other claim of mine already holds that ID; a
   *  claim on a cell that is not in the list is left alone. Returns how many
   *  were put back. */
  async function repairListedIds(listed: string[]): Promise<number> {
    const ids = [...new Set(listed.filter(id => /^\d+$/.test(id)))];
    if (!ids.length || !userId.value) return 0;
    const known = new Set(ids);
    let fixed = 0;
    for (const t of myActiveClaims()) {
      const now = String(t.segment_id || '');
      if (!/^\d+$/.test(now) || known.has(now)) continue;
      const hit = await ancestorAmong(now, ids).catch(() => undefined);
      if (!hit) continue;
      if (tasks.value.some(o => o !== t && o.segment_id === hit && o.assigned_to === userId.value
          && (o.status === 'assigned' || o.status === 'in_progress'))) continue;
      const { error: upErr } = await supabase.from('proofreading_tasks').update({ segment_id: hit }).eq('id', t.id);
      if (upErr) { console.warn('[backend] could not put back the listed ID of claim', t.id, upErr.message); continue; }
      console.info(`[backend] claim ${t.id}: listed ID ${hit} put back (it had been rewritten to ${now})`);
      currentRoots.value = { ...currentRoots.value, [t.id]: now };
      t.segment_id = hit;
      fixed++;
    }
    return fixed;
  }

  // ── What each of my claims is NOW ─────────────────────────────────────
  // A merge or split gives the cell a new segment ID, but the claim kept
  // showing, and jumping to, the ID it was listed under (Celia's report of
  // 2026-08-13). task.segment_id stays as it is: it is how a claim is matched
  // to its row in the cell list. The current ID is kept beside it, by task.
  //
  // How it is found: a claim made from the Delta menu carries a supervoxel
  // (a fixed point of the cell that edits never change) and its root is
  // simply looked up. A claim made from the Cell Library has none, so its
  // edit history is followed forward from the listed ID. When splits left
  // more than one live piece, the history alone cannot say which piece is the
  // cell: that is settled the first time you jump to the cell, by reading the
  // volume at its nucleus (anchorClaimAt), and remembered in this browser.
  const liveRoots = ref<Record<number, string>>({});
  const ANCHOR_KEY = 'nge_claim_anchors_v1';
  const anchors: Record<string, string> = (() => {
    try { return JSON.parse(localStorage.getItem(ANCHOR_KEY) || '{}') || {}; } catch { return {}; }
  })();
  const anchorOf = (t: ProofreadingTask): string | null => t.supervoxel_id || anchors[String(t.id)] || null;
  const myActiveClaims = () => tasks.value.filter(t => t.assigned_to === userId.value
    && (t.status === 'assigned' || t.status === 'in_progress'));

  /** The claim's current root, or null when it cannot be decided yet. */
  async function resolveLive(t: ProofreadingTask): Promise<string | null> {
    const sv = anchorOf(t);
    if (sv) {
      const root = await getRootFromSupervoxel(sv);
      if (root && await anchorFits(t, root)) return root;
    }
    const ends = await latestDescendants(String(t.segment_id));
    if (!ends) return liveRoots.value[t.id] ?? null;      // lookup failed: keep what we had
    if (ends.length === 1) return ends[0];
    const known = liveRoots.value[t.id];
    return known && ends.includes(known) ? known : null;  // several pieces, no anchor yet
  }
  async function refreshLiveRoots(): Promise<void> {
    const mine = myActiveClaims();
    if (!mine.length) return;
    // A claim still without an anchor gets one whenever its soma happens to
    // be loaded (one look, no waiting); one kept only in this browser is
    // sent to the server.
    for (const t of mine) {
      if (t.supervoxel_id) continue;
      if (anchors[String(t.id)]) { void saveAnchor(t, anchors[String(t.id)]); continue; }
      const at = claimPointOf(t);
      const sv = at && supervoxelAt(at);
      if (sv) await anchorWith(t, sv).catch(() => null);
    }
    const found = await Promise.all(mine.map(t => resolveLive(t).catch(() => null)));
    const next = { ...liveRoots.value };
    mine.forEach((t, i) => { if (found[i]) next[t.id] = found[i]!; else delete next[t.id]; });
    liveRoots.value = next;
  }
  /** The supervoxel the loaded segmentation holds at a point, if any. */
  function supervoxelAt(point: [number, number, number]): string | null {
    try {
      const v: any = (window as any)['viewer'];
      const p = Float32Array.from(point);
      for (const ml of v?.layerManager?.managedLayers ?? []) {
        if (ml.archived || !ml.layer?.displayState?.segmentSelectionState) continue;
        for (const rl of ml.layer.renderLayers ?? []) {
          const got = rl.getValueAt?.(p);
          const sv = got == null ? '' : String(Array.isArray(got) ? got[0] : got);
          if (sv && sv !== '0' && Math.floor(Number(sv) / 2 ** 56) === 1) return sv;
        }
      }
    } catch { /* viewer not ready */ }
    return null;
  }
  /** The claim point: for a Cell Library claim, the nucleus coordinates of
   *  its row in the cell list, so a spot in the soma. */
  function claimPointOf(t: ProofreadingTask): [number, number, number] | null {
    const p = [t.claim_point_x, t.claim_point_y, t.claim_point_z];
    return p.every(n => typeof n === 'number' && Number.isFinite(n)) && p.some(n => n !== 0) ? p as [number, number, number] : null;
  }
  /** Keep the anchor with the claim on the server, so it is there in every
   *  browser and every session (large cells take many). Tried once a visit. */
  const anchorSent = new Set<number>();
  async function saveAnchor(t: ProofreadingTask, sv: string): Promise<void> {
    if (t.supervoxel_id || anchorSent.has(t.id)) return;
    anchorSent.add(t.id);
    try {
      await taskAction('set_anchor', { id: t.id, supervoxel_id: sv });
      t.supervoxel_id = sv;
    } catch (e: any) {
      // Before supabase-claim-anchor.sql is run this fails; this browser's copy still works.
      console.warn('[backend] anchor not saved on the server:', e?.message);
    }
  }
  /** Accept a supervoxel as this claim's anchor only when it really is this
   *  claim's cell: its listed ID, or a later version of it. Returns the
   *  cell's current root. */
  async function anchorWith(t: ProofreadingTask, sv: string): Promise<string | null> {
    const root = await getRootFromSupervoxel(sv);
    if (!root) return null;
    if (root !== String(t.segment_id)) {
      const ends = await latestDescendants(String(t.segment_id));
      if (!ends || !ends.includes(root)) return null;   // that spot is some other cell
    }
    anchors[String(t.id)] = sv;
    try { localStorage.setItem(ANCHOR_KEY, JSON.stringify(anchors)); } catch { /* remembered for this visit only */ }
    liveRoots.value = { ...liveRoots.value, [t.id]: root };
    void saveAnchor(t, sv);
    return root;
  }
  /**
   * Give one of my claims its anchor: wait a few seconds for the volume at
   * its soma to load (after a claim, or a jump to the cell) and read it.
   * point defaults to the claim point. Returns the cell's current root, or
   * null when nothing could be read (it is tried again on later visits).
   */
  async function anchorClaimAt(taskId: number, point?: [number, number, number]): Promise<string | null> {
    const t = tasks.value.find(x => x.id === taskId);
    if (!t) return null;
    if (anchorOf(t)) return liveRoots.value[taskId] ?? null;
    const at = point ?? claimPointOf(t);
    if (!at) return null;
    let sv: string | null = null;
    for (let i = 0; i < 14 && !(sv = supervoxelAt(at)); i++) await new Promise(r => setTimeout(r, 500));
    return sv ? anchorWith(t, sv) : null;
  }
  // Active time on each claim (src/util/claim_time.ts): the player's open
  // claims, each with its listed ID and its IDs after edits.
  /** See refreshLaterRoots. */
  const laterRoots = ref<Record<number, string[]>>({});
  const timedClaims = computed<TimedClaim[]>(() => myActiveClaims().filter(countsTime).map(t => ({
    id: t.id, dataset: t.dataset, kind: t.kind,
    roots: [...new Set([t.segment_id, currentRoots.value[t.id], liveRoots.value[t.id], ...(laterRoots.value[t.id] ?? [])]
      .filter((r): r is string => !!r))],
  })));
  /** Each timed claim's cell as it is now, from the server's edit history of
   *  its listed ID: every merge and cut since, whoever made it, with or
   *  without an anchor, and after a reload (Ames 2026-11-02: active time "is
   *  not accurately tracking segs after merge and cuts"). Pieces cut off are
   *  in it too; time spent on one is still time on the claim. */
  async function refreshLaterRoots(): Promise<void> {
    const ds = canonicalDataset(currentDatasetTag());
    const mine = myActiveClaims().filter(t => countsTime(t) && canonicalDataset(t.dataset || '') === ds && /^\d+$/.test(String(t.segment_id || '')));
    const next: Record<number, string[]> = {};
    await Promise.all(mine.map(async t => {
      const listed = String(t.segment_id);
      let ends = descendantsSeen.get(listed);
      if (!ends) { ends = latestDescendants(listed).catch(() => null); descendantsSeen.set(listed, ends); }
      const found = await ends;
      // A failed lookup keeps what was known.
      if (found) next[t.id] = found.slice(0, 50); else if (laterRoots.value[t.id]) next[t.id] = laterRoots.value[t.id];
    }));
    laterRoots.value = next;
  }
  setTimeout(() => { refreshLaterRoots().catch(() => {}); }, 8000);
  setInterval(() => { if (!document.hidden) refreshLaterRoots().catch(() => {}); }, 3 * 60_000);
  watch(() => timedClaims.value.map(c => c.id).join(','), () => { refreshLaterRoots().catch(() => {}); });
  startClaimTimer({ claims: () => timedClaims.value, signedIn: () => !!userId.value });

  let liveRootTimer: ReturnType<typeof setTimeout> | null = null;
  window.addEventListener('nge-graph-edit', () => {
    // Its later versions changed with this edit: ask again (AzureJay, report
    // 344: active time stopped counting after a split).
    descendantsSeen.clear();
    // A burst of edits is one lookup, shortly after the last of them.
    if (liveRootTimer) clearTimeout(liveRootTimer);
    liveRootTimer = setTimeout(() => { refreshLiveRoots().catch(() => {}); refreshLaterRoots().catch(() => {}); }, 1500);
  });

  function _findTaskByPoint(point: ClaimPoint): ProofreadingTask | undefined {
    return tasks.value.find(
      t => t.claim_point_x === point[0] && t.claim_point_y === point[1] && t.claim_point_z === point[2],
    );
  }

  function _findActiveTaskByPoint(point: ClaimPoint): ProofreadingTask | undefined {
    return tasks.value.find(
      t => t.claim_point_x === point[0] && t.claim_point_y === point[1] && t.claim_point_z === point[2] &&
           (t.status === 'assigned' || t.status === 'in_progress'),
    );
  }

  /** Claims allowed per dataset (Amy 2026-09-30): 10 on Retina, else 8.
   *  Must match pilot_task_action in supabase-pilot-access.sql. */
  const CLAIM_LIMITS: Record<string, number> = { stroeh_mouse_retina: 10 };
  function claimLimitFor(dataset: string = currentDatasetTag()): number {
    return CLAIM_LIMITS[canonicalDataset(dataset)] ?? MAX_CLAIMS;
  }

  /** How many active claims does the current user have in this dataset?
   *  An interneuron quest is not counted (it has a limit of its own). */
  function myActiveClaimCount(): number {
    if (!userId.value) return 0;
    return tasks.value.filter(
      t => t.assigned_to === userId.value && (t.status === 'assigned' || t.status === 'in_progress') && !isQuestTask(t),
    ).length;
  }

  /** Is a specific point claimed by anyone? */
  function isClaimedPoint(point: ClaimPoint): {claimed: boolean; byMe: boolean; byName?: string} {
    const task = _findActiveTaskByPoint(point);
    if (!task) return { claimed: false, byMe: false };
    return {
      claimed: true,
      byMe: task.assigned_to === userId.value,
      byName: task.assigned_to && task.assigned_to !== userId.value ? task.assigned_to : undefined,
    };
  }

  /** Is a segment claimed? By the ID a claim was listed under, or its ID now. */
  function isClaimedSegment(segId: string): {claimed: boolean; byMe: boolean; byName?: string} {
    const task = tasks.value.find(
      t => isTaskCell(t, segId) && (t.status === 'assigned' || t.status === 'in_progress'),
    );
    if (!task) return { claimed: false, byMe: false };
    return {
      claimed: true,
      byMe: task.assigned_to === userId.value,
      byName: task.assigned_to && task.assigned_to !== userId.value ? task.assigned_to : undefined,
    };
  }

  /** Claim a cell by spatial coordinate + current root ID + supervoxel ID.
   *  The point is the stable navigation anchor; supervoxel_id is the immutable
   *  segment used to resolve current root after edits/splits. */
  async function claimCell(point: ClaimPoint, currentSegId?: string, supervoxelId?: string): Promise<{ok: boolean; reason?: string}> {
    if (!userId.value) return { ok: false, reason: 'Not logged in' };
    if (myActiveClaimCount() >= claimLimitFor()) return { ok: false, reason: `Max ${claimLimitFor()} claims reached` };

    // Check if already claimed at this point
    const existing = isClaimedPoint(point);
    if (existing.claimed) return { ok: false, reason: existing.byMe ? 'Already claimed by you' : 'Claimed by another player' };

    // Also check by segment ID if provided (in case the same cell was claimed at a different point)
    if (currentSegId) {
      const segClaim = isClaimedSegment(currentSegId);
      if (segClaim.claimed) return { ok: false, reason: segClaim.byMe ? 'Already claimed by you' : 'Claimed by another player' };
    }

    try {
      const task = await taskAction('claim_cell', { dataset: currentDatasetTag(), segment_id: currentSegId, point, supervoxel_id: supervoxelId });
      activeTaskId.value = task.id;
      startHeartbeat();
      emitScriptEvent('claim', { taskId: task.id, cellId: String(task.segment_id || currentSegId || ''), dataset: currentDatasetTag() });
      // The claim is made; logging and the activity feed need not hold the
      // button (Amy 2026-09-28: Claim felt slow). Only the task sync waits.
      void Promise.resolve(logEdit({ operation: 'claim_task', task_id: task.id })).catch(() => {});
      void Promise.resolve(postActivity(`claimed cell ${currentSegId ? `...${currentSegId.slice(-4)}` : pointKey(point)}`)).catch(() => {});
      await loadTasks();
      return { ok: true };
    } catch (e: any) {
      console.warn('[backend] claimCell error:', e.message);
      return { ok: false, reason: e.message };
    }
  }

  /** Release a claim by spatial coordinate. */
  async function releaseCell(point: ClaimPoint): Promise<boolean> {
    const task = tasks.value.find(
      t => t.claim_point_x === point[0] && t.claim_point_y === point[1] && t.claim_point_z === point[2] &&
           t.assigned_to === userId.value &&
           (t.status === 'assigned' || t.status === 'in_progress'),
    );
    if (!task) return false;
    const prevActive = activeTaskId.value;
    activeTaskId.value = task.id;
    const released = await releaseTask();
    activeTaskId.value = prevActive;
    if (!released) return false;
    await loadTasks();
    return true;
  }

  /** Release a claim by its task id: works for claims made on a point, which
   *  have no segment id (Release used to bail out silently on those). */
  /** My active claims in EVERY dataset. The server's 3-claim limit spans
   *  datasets, but `tasks` holds only the dataset on screen, so a claim left on
   *  MEC silently blocked every Retina claim (Amy 2026-09-28). */
  async function loadMyActiveClaims(): Promise<ProofreadingTask[]> {
    if (!userId.value) return [];
    const { data, error: e } = await supabase
      .from('proofreading_tasks')
      .select('*')
      .eq('assigned_to', userId.value)
      .in('status', ['assigned', 'in_progress'])
      .order('updated_at', { ascending: true });
    if (e) { console.warn('[backend] loadMyActiveClaims error:', e.message); return []; }
    return (data ?? []) as ProofreadingTask[];
  }

  /** Save a view (short link) as this claim's work in progress, so coming
   *  back to the cell restores its annotation layers (Amy 2026-09-28). */
  async function saveWorkingLink(taskId: number, link: string): Promise<boolean> {
    try {
      await taskAction('save_link', { id: taskId, link });
      const t = tasks.value.find(x => x.id === taskId);
      if (t) t.working_link = link;
      noteClaimSave(taskId);
      return true;
    } catch (e: any) {
      // The claim has ended (completed or released, from any menu): nothing is
      // left to save to, so that is no failure to warn a player about (Nseraf,
      // reports 339 and 341).
      if (/no longer active/i.test(String(e?.message))) return true;
      console.warn('[backend] saveWorkingLink error:', e.message);
      return false;
    }
  }

  /** Take back a cell you completed as an open claim (Ames 2026-10-10:
   *  "uncomplete reopens the cell"). Unmarking it in the Delta menu took the
   *  mark off in CAVE but left the claim completed, so the Cell Library still
   *  showed it done. The sheet row goes back to WIP; its date, final ID and
   *  link stay until the cell is completed again. Returns '' when it worked,
   *  else why not, in plain words. */
  async function reopenTask(taskId: number): Promise<string> {
    const mine = tasks.value.find(t => t.id === taskId);
    try {
      await taskAction('reopen', { id: taskId });
    } catch (e: any) {
      const msg = String(e?.message || '');
      if (/unknown pilot action|pilot_task_reopen|could not find the function/i.test(msg)) return 'Reopening is not switched on yet.';
      return msg || 'Could not reopen this cell.';
    }
    if (mine?.segment_id) {
      await syncCellToSheet('reopen', String(mine.segment_id), undefined, (mine as any).dataset)
        .catch((e: any) => console.warn('[backend] sheet not updated on reopen:', e?.message));
    }
    await loadTasks();
    return '';
  }

  async function releaseTaskById(taskId: number): Promise<boolean> {
    const prevActive = activeTaskId.value;
    activeTaskId.value = taskId;
    const released = await releaseTask();
    activeTaskId.value = prevActive === taskId ? null : prevActive;
    if (!released) return false;
    await loadTasks();
    return true;
  }

  /** Release a claim by segment ID (finds matching task by cached segment_id). */
  async function releaseBySegment(segId: string): Promise<boolean> {
    const task = tasks.value.find(
      t => isTaskCell(t, segId) && t.assigned_to === userId.value &&
           (t.status === 'assigned' || t.status === 'in_progress'),
    );
    if (!task) return false;
    const prevActive = activeTaskId.value;
    activeTaskId.value = task.id;
    const released = await releaseTask();
    activeTaskId.value = prevActive;
    if (!released) {
      // The answer may have been lost on the way back after the release went
      // through: ask what the cell's state is now.
      try { await loadTasks(); } catch { return false; }
      const stillMine = tasks.value.some(t => t.id === task.id && t.assigned_to === userId.value &&
        (t.status === 'assigned' || t.status === 'in_progress'));
      if (!stillMine) error.value = '';
      return !stillMine;
    }
    await loadTasks();
    return true;
  }

  // ── Admin Hub ──────────────────────────────────────────────────────────

  const isAdmin = ref(false);

  /** Listed in blog_authors: may write for connectome.quest/blog (the Blog
   *  tab of your profile). The server enforces it; this only shows the tab. */
  const isBlogAuthor = ref(false);
  async function checkBlogAuthor() {
    if (!userId.value) { isBlogAuthor.value = false; return; }
    try {
      const { data } = await supabase.from('blog_authors').select('user_id').eq('user_id', userId.value).maybeSingle();
      isBlogAuthor.value = !!data;
    } catch { isBlogAuthor.value = false; }
  }

  // A failed read used to count as "not an admin", so the Admin part of
  // settings went missing on some refreshes (Celia #195). A failed read now
  // keeps what this browser last knew for that email and tries again.
  async function checkAdmin(tries = 4): Promise<void> {
    if (!userEmail.value) { isAdmin.value = false; return; }
    const email = userEmail.value;
    const known = `nge-admin-${email}`;
    try { if (localStorage.getItem(known) === '1') isAdmin.value = true; } catch { /* storage blocked */ }
    const { data, error } = await supabase
      .from('admins')
      .select('id')
      .eq('email', email)
      .maybeSingle();
    if (error) {
      console.warn('[backend] admin check failed, will retry:', error.message);
      if (tries > 1) setTimeout(() => { if (userEmail.value === email) void checkAdmin(tries - 1); }, 2500);
      return;
    }
    isAdmin.value = !!data;
    try { if (isAdmin.value) localStorage.setItem(known, '1'); else localStorage.removeItem(known); } catch { /* storage blocked */ }
    if (isAdmin.value) console.info('[backend] Admin access granted for', email);
  }

  /** Player view (Ames 2026-10-25): an admin sees the game as a player does.
   *  Display only: isAdmin, and everything the server allows, stay the same.
   *  Interface that only admins see asks showAdminUi instead of isAdmin.
   *  Remembered per browser. */
  const PLAYER_VIEW_KEY = 'nge-player-view';
  const playerView = ref((() => { try { return localStorage.getItem(PLAYER_VIEW_KEY) === '1'; } catch { return false; } })());
  function setPlayerView(on: boolean) {
    playerView.value = on;
    try { if (on) localStorage.setItem(PLAYER_VIEW_KEY, '1'); else localStorage.removeItem(PLAYER_VIEW_KEY); } catch { /* storage blocked */ }
  }
  const showAdminUi = computed(() => isAdmin.value && !playerView.value);

  // ── Notifications ─────────────────────────────────────────────────────

  interface Notification {
    id: number;
    title: string;
    body: string;
    image_url: string | null;
    thumbnail_url: string | null;
    send_at: string;
    expires_at: string | null;
    target_type: string;
    target_id: string | null;
    post_to_chat: boolean;
    created_at: string;
    created_by: string | null;
    // A reply was asked for (supabase-report-replies.sql). Absent until that
    // SQL is run, and empty on every notification that did not ask.
    reply_triage_id?: string | null;
    reply_text?: string | null;
    replied_at?: string | null;
    reply_screenshot_url?: string | null;
    reply_view_url?: string | null;
  }

  const notifications = ref<Notification[]>([]);
  const notificationReads = ref<Set<number>>(new Set());
  /**
   * Dismissed notification ids for the current user.
   *
   * Previously this lived in localStorage and hid a notification FOREVER, per
   * browser, with no undo — one stray click on the × (which sits next to the
   * admin 🗑) wiped it from the feed permanently. It now lives in Supabase on
   * `notification_reads.dismissed`, so it is per-user rather than per-browser,
   * survives a machine change, and is separable from read state: you can mark
   * everything read without deleting it, or delete all without reading.
   */
  const notificationDismissals = ref<Set<number>>(new Set());

  // One-time cleanup: drop the old browser-local dismissal list so anything
  // hidden by an accidental click under the previous behaviour comes back.
  try { localStorage.removeItem('nge-notification-dismissals'); } catch { /* private mode */ }
  let notifSubscription: any = null;

  /** Trigger badge celebration from notification click — AchievementToast watches this */
  const pendingBadgeCelebration = ref<{ title: string; body: string; imageUrl: string } | null>(null);
  const pendingCellCelebration = ref<{ totalCells: number; imageUrl: string; batchCount?: number; segId?: string } | null>(null);

  /** Read, or a Pyr Update (sent to everyone) more than 5 days old: those
   *  count as read, so the daily updates do not pile up as unread (Ames
   *  2026-10-25). Nothing is saved for them. */
  const UPDATE_STALE_MS = 5 * 24 * 60 * 60 * 1000;
  function notifRead(n: Notification): boolean {
    if (notificationReads.value.has(n.id)) return true;
    if (n.target_type === 'user') return false;
    const at = Date.parse(String((n as any).send_at || (n as any).created_at || ''));
    return Number.isFinite(at) && Date.now() - at > UPDATE_STALE_MS;
  }
  /** Whether Hunt reports count right now: only during a Hunt or its
   *  preparation (NotificationFeedPanel keeps this in step with the Hunts;
   *  Ames 2026-10-27). Off, the bell's counts leave them out. */
  const huntReportsShown = ref(true);
  const counted = (n: { title?: string | null }) => huntReportsShown.value || notifKind(n.title, isAdmin.value) !== 'hunt';
  const unreadNotificationCount = computed(() =>
    notifications.value.filter(n => !notifRead(n) && counted(n)).length
  );
  /** Unread in the bell's Admin tab (util/notif_kinds.ts): its own red count
   *  on the bell (Ames 2026-10-21). Always 0 for players. */
  const unreadAdminNotificationCount = computed(() =>
    notifications.value.filter(n => !notifRead(n) && ['admin', 'access'].includes(notifKind(n.title, isAdmin.value))).length
  );
  /** Unread in the bell's Admin and Triage tabs together: the dot on the
   *  Player view chip, so admin alerts still show while those tabs are hidden. */
  const unreadStaffNotificationCount = computed(() =>
    notifications.value.filter(n => !notifRead(n) && notifKind(n.title, isAdmin.value) !== 'main' && counted(n)).length
  );

  // The bell used to ask for the 50 newest notifications in full, twice a
  // minute, in every open tab, with every read mark the player has: after
  // the leaderboard, the biggest share of Pyr's Supabase egress (2026-10-15).
  // A check now asks only for what is new since the last check (newOnly):
  // rows written since (id above the highest seen) and rows that became due
  // since (send_at after the last check's own clock reading). The first
  // version compared send_at with the newest send_at seen, which missed a
  // row written later with an earlier send_at, e.g. by a writer whose clock
  // runs behind (review by Astra, 2026-10-15). The read marks and group
  // memberships come with a full load: at sign in, when the bell opens, and
  // every tenth check, which also brings edits and other tabs' dismissals.
  let notifGroupIds: string[] = [];
  let notifMaxId = 0;
  let notifCheckedAt = '';
  // The feed loads the newest NOTIF_PAGE and asks for more on "Show more"
  // (Ames 2026-10-20: "maybe it should load some # and then make me click
  // 'more' in order to load more? might save on querying"); it was 50 every
  // time.
  const NOTIF_PAGE = 20;
  const notifLimit = ref(NOTIF_PAGE);
  /** True when the last full load filled its page: there may be older ones. */
  const notificationsHaveMore = ref(false);
  async function loadMoreNotifications() {
    notifLimit.value += NOTIF_PAGE;
    await loadNotifications();
  }
  let grantLinesAsked = false;
  /** What the leader tab read on its minute check, for the other tabs
   *  (util/tabs.ts). reads and dismissed come with a full check only. */
  interface SharedNotifs { user: string; at: string; rows: any[]; groupIds: string[]; reads?: number[]; dismissed?: number[] }
  /** shared: rows another tab's check read. They are merged as a check of
   *  this tab's own would be, without asking the server. share: hand what
   *  this check read to the other tabs. */
  async function loadNotifications(opts: { newOnly?: boolean; share?: boolean; shared?: SharedNotifs } = {}) {
    if (!userId.value) return;
    const s = opts.shared;
    if (s && s.user !== userId.value) return;
    const now = s ? s.at : new Date().toISOString();
    const newOnly = s ? true : !!opts.newOnly && !!notifCheckedAt;

    if (!newOnly) {
      // Get user's group IDs for group-targeted notifications
      const { data: memberships } = await supabase
        .from('user_group_members')
        .select('group_id')
        .eq('user_id', userId.value);
      notifGroupIds = (memberships || []).map((m: any) => String(m.group_id));
    }

    // Fetch active notifications: all of them, or only what is new since the last check.
    let query = supabase
      .from('notifications')
      .select('*')
      .lte('send_at', now)
      .order('send_at', { ascending: false })
      .limit(newOnly ? 50 : notifLimit.value);
    if (newOnly) query = query.or(`id.gt.${notifMaxId},send_at.gt.${notifCheckedAt}`);

    const allNotifs = s ? s.rows : (await query).data;
    if (!allNotifs) return;
    // A grant is said in chat the next time the player comes in, not when the
    // admin approves it (Ames 2026-10-30): only the first load of a visit
    // looks, so a grant that lands mid visit waits for the next one.
    if (!grantLinesAsked) {
      grantLinesAsked = true;
      if ((allNotifs as any[]).some(n => n.target_type === 'user' && String(n.send_at) >= '2026-10-30' && (n.title === '🎉 Congratulations! Your edit access has been granted!' || String(n.title || '').startsWith('🔓 You have access to ')))) {
        void secureWrite('access.grantLines').catch(() => {});
      }
    }
    if (!newOnly) notificationsHaveMore.value = allNotifs.length >= notifLimit.value;
    for (const n of allNotifs as any[]) if (Number(n.id) > notifMaxId) notifMaxId = Number(n.id);
    if (!s || now > notifCheckedAt) notifCheckedAt = now;
    if (s) {
      // What the leader knows of groups, reads and dismissals, added to what
      // this tab knows (a read or dismissal never goes back).
      notifGroupIds = s.groupIds;
      if (s.reads) notificationReads.value = new Set([...notificationReads.value, ...s.reads]);
      if (s.dismissed) notificationDismissals.value = new Set([...notificationDismissals.value, ...s.dismissed]);
    }
    if (newOnly && !allNotifs.length && !s?.dismissed?.length) return;
    // The server already picks what is meant for this player. A group
    // notification for a group joined since the last full load: read the
    // memberships again so it is not filtered out here.
    if (!s && newOnly && (allNotifs as any[]).some(n => n.target_type === 'group' && !notifGroupIds.includes(String(n.target_id)))) {
      const { data: memberships } = await supabase
        .from('user_group_members')
        .select('group_id')
        .eq('user_id', userId.value);
      if (memberships) notifGroupIds = memberships.map((m: any) => String(m.group_id));
    }

    if (!newOnly) {
      // Read + dismissed status together (both live on notification_reads),
      // for just the notifications this feed can show: the page just read,
      // and the ones delivered before (kept below). It used to read every
      // row a player had, and the gateway sends at most 500, so a player with
      // more lost the rest to "unread" (report 212).
      const wanted = [...new Set([...(allNotifs as any[]).map(n => Number(n.id)),
        ...(useUserPreferencesStore().prefs.notifDelivered || [])])].filter(Number.isInteger).slice(0, 450);
      const reads: any[] = [];
      let readsFailed = false;
      if (wanted.length) {
        const { data, error } = await supabase
          .from('notification_reads')
          .select('notification_id, dismissed')
          .eq('user_id', userId.value)
          .in('notification_id', wanted);
        if (error || !data) readsFailed = true;
        else reads.push(...data);
      }
      // A read that failed (the server busy for a moment) used to empty the
      // list, and everything already read came back as unread (toknow,
      // report 212: the reads were all saved on the server). Keep what this
      // tab knows and add what did come back.
      const ids = reads.map((r: any) => r.notification_id);
      const gone = reads.filter((r: any) => r.dismissed).map((r: any) => r.notification_id);
      notificationReads.value = readsFailed ? new Set([...notificationReads.value, ...ids]) : new Set(ids);
      notificationDismissals.value = readsFailed ? new Set([...notificationDismissals.value, ...gone]) : new Set(gone);
      if (opts.share) shareResult('notifications', { user: userId.value, at: now, rows: allNotifs, groupIds: notifGroupIds, reads: ids, dismissed: gone } as SharedNotifs);
    } else if (opts.share && !s) {
      shareResult('notifications', { user: userId.value, at: now, rows: allNotifs, groupIds: notifGroupIds } as SharedNotifs);
    }

    // "Stop sending at" is a deadline for DELIVERY, not for the notification
    // itself (Ames 2026-10-10: it was vanishing from everyone's feed). Past
    // the deadline it reaches nobody new; a player who was already given it,
    // or read it, keeps it. Delivered = it has been in this player's feed
    // before, remembered with their settings so it follows them across
    // computers.
    const prefsStore = useUserPreferencesStore();
    const delivered = new Set<number>([...(prefsStore.prefs.notifDelivered || []), ...notificationReads.value]);
    const groupIds = notifGroupIds;
    // Settings, "Show Today in Pyr" off: the daily update stays out of the bell.
    const hideDigest = prefsStore.prefs.showDailyDigest === false;
    const mine = (n: Notification) => !notificationDismissals.value.has(n.id)
      && !(hideDigest && n.target_type !== 'user' && String(n.title || '').startsWith('Today in Pyr')) && (
      n.target_type === 'all'
      || (n.target_type === 'user' && n.target_id === userId.value)
      || (n.target_type === 'group' && groupIds.includes(n.target_id || '')));
    const stopped = (n: Notification) => !!n.expires_at && new Date(n.expires_at) < new Date();
    const due = (allNotifs as Notification[]).filter(n => mine(n) && (!stopped(n) || delivered.has(n.id)));
    // New ones join the feed in front of what is already there.
    const current = newOnly
      ? [...due.filter(n => !notifications.value.some(o => o.id === n.id)), ...notifications.value]
          .sort((a: any, b: any) => String(b.send_at || b.created_at).localeCompare(String(a.send_at || a.created_at)))
      : due;

    // Delivered ones that have since stopped: the server leaves those out of
    // the list above, so ask for them by id.
    const have = new Set(current.map(n => n.id));
    // Only those as new as the page reaches: older ones come with "Show more".
    const oldestShown = notificationsHaveMore.value && current.length ? Math.min(...current.map(n => n.id)) : 0;
    const kept = [...delivered].filter(id => id > oldestShown && !have.has(id) && !notificationDismissals.value.has(id)).sort((a, b) => b - a).slice(0, 100);
    if (kept.length && !newOnly) {
      const { data: older } = await supabase.from('notifications').select('*').in('id', kept).lte('send_at', now);
      for (const n of (older || []) as Notification[]) if (mine(n)) current.push(n);
      current.sort((a: any, b: any) => String(b.send_at || b.created_at).localeCompare(String(a.send_at || a.created_at)));
    }
    notifications.value = current;

    // Everything in the feed now counts as delivered from here on.
    const before = prefsStore.prefs.notifDelivered || [];
    const fresh = current.map(n => n.id).filter(id => !before.includes(id));
    if (fresh.length) prefsStore.save({ notifDelivered: [...new Set([...fresh, ...before])].sort((a, b) => b - a).slice(0, 300) });

    // Dismissed notifications are hidden from the feed. Done after the read
    // query so the freshly-loaded dismissal set is applied to this batch.
    if (notificationDismissals.value.size) {
      notifications.value = notifications.value.filter(
        n => !notificationDismissals.value.has(n.id));
    }
  }

  /**
   * The admin's view of notifications — ALL of them, not just the ones a user
   * would see.
   *
   * loadNotifications() filters to `send_at <= now` and the current user's
   * targeting, so an admin can't see what's scheduled, what's expired, or
   * anything aimed at someone else. This is the management list instead.
   *
   * Paginated deliberately: a project that has been running a while
   * accumulates notifications, and loading every one to render a settings
   * panel gets slow for no benefit. Newest first, a page at a time.
   * Ordering by send_at DESC also means future-dated (scheduled) rows sort to
   * the top, so the queue is always on the first page.
   */
  const adminNotifications = ref<Notification[]>([]);
  const adminNotifHasMore = ref(false);
  const ADMIN_NOTIF_PAGE = 15;

  async function loadAdminNotifications(append = false) {
    if (!isAdmin.value) return;
    const offset = append ? adminNotifications.value.length : 0;
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .order('send_at', { ascending: false })
      .range(offset, offset + ADMIN_NOTIF_PAGE - 1);
    if (error) { console.warn('[admin] loadAdminNotifications failed:', error.message); return; }
    const rows = data || [];
    // A full page back implies there may be more; a short page means the end.
    adminNotifHasMore.value = rows.length === ADMIN_NOTIF_PAGE;
    adminNotifications.value = append ? [...adminNotifications.value, ...rows] : rows;
  }

  /**
   * Edit an existing notification.
   *
   * Callers must not offer this for expired notifications — editing something
   * nobody can see any more is misleading. Enforced in the UI (the edit button
   * is hidden) rather than here, so an admin script could still repair a row.
   */
  async function updateNotification(id: number, fields: {
    title?: string; body?: string;
    image_url?: string | null; thumbnail_url?: string | null;
    send_at?: string; expires_at?: string | null;
    target_type?: string; target_id?: string | null;
    post_to_chat?: boolean;
  }) {
    if (!isAdmin.value) return;
    try { await secureWrite('notification.update', { id, fields }); }
    catch (error: any) { console.warn('[admin] updateNotification failed:', error?.message); throw error; }
    // Reflect locally without a full refetch.
    const patch = (list: Notification[]) => {
      const i = list.findIndex(n => n.id === id);
      if (i >= 0) list[i] = { ...list[i], ...fields } as Notification;
    };
    patch(adminNotifications.value);
    patch(notifications.value);
  }

  // Read in one tab is read in the others (report 212: with two Pyr tabs
  // open, the other tab kept showing what had been read as unread).
  const readsChannel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('pyr-notif-reads') : null;
  if (readsChannel) readsChannel.onmessage = (e: MessageEvent) => {
    const m = e.data || {};
    if (m.user !== userId.value || !Array.isArray(m.ids)) return;
    const set = m.dismissed ? notificationDismissals : notificationReads;
    for (const id of m.ids) if (typeof id === 'number') set.value.add(id);
    if (m.dismissed) notifications.value = notifications.value.filter(n => !m.ids.includes(n.id));
  };
  function tellOtherTabs(ids: number[], dismissed = false) {
    if (ids.length) try { readsChannel?.postMessage({ user: userId.value, ids, dismissed }); } catch { /* closed */ }
  }

  async function markNotificationRead(notifId: number) {
    if (!userId.value) return;
    notificationReads.value.add(notifId);
    tellOtherTabs([notifId]);
    await supabase.from('notification_reads').insert({
      notification_id: notifId,
      user_id: userId.value,
    }).then(() => {}, () => {}); // ignore duplicate errors
  }

  /**
   * Moving a triage card to In progress or Done reads its bell notes (Ames
   * 2026-10-27): a note that names the report (a reply, a tag, newer alerts),
   * or a 🗂 alert that quotes it. Works on the triage board's own page too,
   * where the game never signed in: the account is looked up by email only,
   * so nothing else (a visit, a streak) starts. Other Pyr tabs hear at once.
   */
  let reportReaderId: string | null = null;
  async function markReportNotesRead(reportId: string, excerpt: string | null) {
    return markReportsNotesRead([{ id: reportId, excerpt }]);
  }
  /** Several reports at once: the triage board, on load, reads the notes of
   *  every card already In progress or Done (Ames 2026-11-02: "Triage and
   *  Notifs should be in sync"), whoever moved it and from wherever. */
  async function markReportsNotesRead(reports: { id: string; excerpt: string | null }[]) {
    let uid = userId.value || reportReaderId;
    if (!uid && userEmail.value) {
      const { data } = await supabase.from('users').select('id').eq('middleauth_email', userEmail.value).single();
      uid = reportReaderId = (data as any)?.id ?? null;
    }
    const wanted = reports.filter(r => r && r.id);
    if (!uid || !wanted.length) return 0;
    const flat = (t: any) => String(t || '').replace(/\s+/g, ' ').trim();
    const quotes = wanted.map(r => flat(r.excerpt).slice(0, 60)).filter(Boolean);
    const { data: recent } = await supabase.from('notifications').select('id,title,body')
      .lte('send_at', new Date().toISOString()).order('send_at', { ascending: false }).limit(150);
    const hits = ((recent || []) as any[]).filter(n => {
      const body = String(n.body || '');
      if (wanted.some(r => body.includes(r.id))) return true;
      if (!String(n.title || '').startsWith('🗂')) return false;
      const b = flat(body);
      return quotes.some(q => b.startsWith('"' + q));
    }).map(n => Number(n.id));
    if (!hits.length) return 0;
    // The gateway takes at most 100 values in one in().
    const seen = new Set<number>();
    for (let i = 0; i < hits.length; i += 90) {
      const { data: had } = await supabase.from('notification_reads').select('notification_id').eq('user_id', uid).in('notification_id', hits.slice(i, i + 90));
      for (const r of (had || []) as any[]) seen.add(Number(r.notification_id));
    }
    const fresh = hits.filter(id => !seen.has(id));
    if (!fresh.length) return 0;
    const { error } = await supabase.from('notification_reads').upsert(fresh.map(id => ({ notification_id: id, user_id: uid })), { onConflict: 'notification_id,user_id' });
    if (error) return 0;
    for (const id of fresh) notificationReads.value.add(id);
    try { readsChannel?.postMessage({ user: uid, ids: fresh }); } catch { /* closed */ }
    return fresh.length;
  }

  /** only: mark just these (one tab of the feed) instead of everything. */
  async function markAllNotificationsRead(only?: number[]) {
    if (!userId.value) return;
    const pick = only ? new Set(only) : null;
    const unread = notifications.value.filter(n => !notificationReads.value.has(n.id) && (!pick || pick.has(n.id)));
    for (const n of unread) {
      notificationReads.value.add(n.id);
    }
    tellOtherTabs(unread.map(n => n.id));
    // Batch insert (ignore conflicts)
    const rows = unread.map(n => ({ notification_id: n.id, user_id: userId.value }));
    if (rows.length > 0) {
      await supabase.from('notification_reads').upsert(rows, { onConflict: 'notification_id,user_id' });
    }
  }

  async function createNotification(data: {
    title: string; body: string;
    image_url?: string; thumbnail_url?: string;
    send_at?: string; expires_at?: string;
    target_type?: string; target_id?: string;
    post_to_chat?: boolean;
  }) {
    if (!isAdmin.value || !userId.value) return;
    const row = {
      title: data.title,
      body: data.body,
      image_url: data.image_url || null,
      thumbnail_url: data.thumbnail_url || null,
      send_at: data.send_at || new Date().toISOString(),
      expires_at: data.expires_at || null,
      target_type: data.target_type || 'all',
      target_id: data.target_id || null,
      post_to_chat: data.post_to_chat || false,
      created_by: userId.value,
    };
    // Select the id back so the chat announcement can link to this exact
    // notification rather than telling people to go and find it.
    let created: any = null;
    try { created = await secureWrite('notification.insert', { row }); }
    catch (err: any) { console.warn('[admin] createNotification error:', err?.message); }
    const newNotifId: number | null = created?.id ?? null;

    // Post to chat as a system message if requested — but ONLY for
    // notifications going out now.
    //
    // This used to fire on every create, so scheduling a notification for next
    // Tuesday still announced it in chat the instant you hit Send, pointing
    // everyone at something that `send_at` keeps hidden until Tuesday.
    //
    // The `post_to_chat` flag is persisted on the row; the scheduled
    // announcement is posted at its send time by postDueChatAnnouncements()
    // below, which the notification poller runs on every client. Exactly one
    // client posts because the claim is an atomic conditional UPDATE on
    // chat_posted_at (a naive "every client posts" would duplicate — that's why
    // the claim matters). A cron (chat-announcements.yml) was the original plan
    // but it lives on this branch, and GitHub only runs schedules from the
    // default branch, so it never fired — hence the client-side poster.
    // "Scheduled" means the admin explicitly picked a future send time. Post to
    // chat instantly ONLY for send-now notifications — either no send_at, or one
    // whose time has already arrived. Key off `data.send_at` (undefined for a
    // send-now) rather than `row.send_at` (which defaults to now), and use only
    // a small jitter skew.
    //
    // The old guard allowed a 60-second grace: anything less than a minute ahead
    // counted as "now" and fired an instant post. But scheduling for the current
    // or next minute — exactly how this feature gets tested — lands inside that
    // window, so a "scheduled" notification announced itself in chat the instant
    // it was created (e.g. one set for 20:44 posted at 20:43:48).
    const scheduledMs = data.send_at ? new Date(data.send_at).getTime() : NaN;
    const isScheduledForLater = Number.isFinite(scheduledMs) && scheduledMs > Date.now() + 2_000;
    if (data.post_to_chat && !isScheduledForLater) {
      try {
        const chatStore = useChatStore();
        if ((chatStore.connected || chatStore.polling) && newNotifId != null) {
          // Record that the announcement already went out, so the every-10-min
          // cron (post-due-chat-announcements.mjs) doesn't post a second copy.
          // Only mark it when it was really posted: if chat was offline or the
          // server refused it, leave chat_posted_at null for a later try.
          if (await chatStore.postAnnouncement(data.title, newNotifId)) {
            await secureWrite('notification.update', { id: newNotifId, fields: { chat_posted_at: new Date().toISOString() } })
              .catch(() => { /* the cron's copy check is the fallback */ });
          }
        }
      } catch (e) { console.warn('[admin] post_to_chat failed:', e); }
    }
    await loadNotifications();
  }

  /**
   * Post to chat any notifications whose scheduled send time has arrived, that
   * asked to post to chat, and that haven't been posted yet. Runs on every
   * connected client via the notification poller, but posts each announcement
   * exactly once: the claim is an atomic conditional UPDATE (`.is('chat_posted_at',
   * null)`), so only the client whose UPDATE actually flips the column wins and
   * posts. This is what makes scheduled "also post to chat" announcements
   * actually appear (the intended cron never ran — see createNotification).
   */
  async function postDueChatAnnouncements() {
    // Only an admin's game may post one (the server refuses anyone else), so
    // nobody else asks: every other open game was being told "Admins only"
    // once a minute while an announcement waited (seen 2026-10-11).
    if (!isAdmin.value) return;
    const chatStore = useChatStore();
    // Need a live channel to broadcast to other users. If no one is connected
    // yet, leave the rows unposted; the next connected poller picks them up.
    if (!chatStore.connected && !chatStore.polling) return;
    const now = new Date().toISOString();
    const { data: due, error: dueErr } = await supabase
      .from('notifications')
      .select('id,title')
      .eq('post_to_chat', true)
      // Only public ones: the server refuses a chat post for any other, and a
      // refused claim is let go below, so it would be tried again forever.
      .eq('target_type', 'all')
      .is('chat_posted_at', null)
      .lte('send_at', now)
      // Monthly Champions is the job's: Nurro and each winner's award line
      // (scripts/post-due-chat-announcements.mjs), not one announcement here.
      .not('title', 'like', '🏆 Monthly Champions,%')
      // Don't announce something that already expired — this also skips a stale
      // backlog of expired test notifications when this poster first ships.
      .or(`expires_at.is.null,expires_at.gt.${now}`)
      .order('send_at', { ascending: true })
      .limit(10);
    if (dueErr || !due?.length) return;
    for (const n of due) {
      // Atomic claim: only the row still NULL gets updated, and only one client's
      // UPDATE can win that race, so exactly one client proceeds to post.
      let claimed = false;
      try { claimed = (await secureWrite<{ claimed: boolean }>('notification.claimChatPost', { id: n.id })).claimed; }
      catch (claimErr: any) { console.warn('[admin] claim chat announcement failed:', claimErr?.message); continue; }
      if (!claimed) continue;
      // Posted, or the claim is let go so a later round tries again (it was
      // set before the post, so a refused or failed post used to stay "done"
      // for good: Ames 2026-10-27, an edit that added Post to chat).
      if (!(await chatStore.postAnnouncement(n.title, n.id))) {
        await secureWrite('notification.update', { id: n.id, fields: { chat_posted_at: null } }).catch(() => {});
      }
    }
  }

  /** Create a self-targeted notification (no admin required). */
  /** Ask the server to announce an achievement in chat. It checks the
   *  achievement is really earned and posts the line once; a refusal or a
   *  failure is silent (the achievement itself is unaffected). */
  function announceAchievement(track: string, badgeId: number, forUserId?: string) {
    if (!userId.value) return;
    // forUserId: a special award an admin has just given to someone else.
    if (forUserId || track === 'special') {
      secureWrite('chat.achievement', { track, badgeId, ...(forUserId ? { userId: forUserId } : {}) }).catch(() => { /* not announced */ });
      return;
    }
    // The game counts an edit or a cell a moment before the server has, so
    // the server can answer "Not earned yet" to an achievement that is
    // earned (Nseraf's 6,000 edits, 2026-10-11: saved, never announced). It
    // is kept and asked again, across a reload too, until the server agrees.
    const wait = waitingAnnouncements();
    if (!wait.some(w => w.track === track && w.badgeId === badgeId)) wait.push({ track, badgeId, tries: 0 });
    keepAnnouncements(wait);
    // Asked 3 seconds after it is earned (Ames 2026-10-11), which gives the
    // server time to count the edit or cell; asked again if it still says no.
    setTimeout(() => { void sendAnnouncements(); }, 3000);
  }
  const ANNOUNCE_KEY = 'nge_announce_waiting_v1';
  type WaitingAnnouncement = { track: string; badgeId: number; tries: number };
  function waitingAnnouncements(): WaitingAnnouncement[] {
    try {
      const all = JSON.parse(localStorage.getItem(ANNOUNCE_KEY) || '{}') || {};
      const mine = all[userId.value || ''];
      return Array.isArray(mine) ? mine.filter(w => w && typeof w.track === 'string' && Number.isFinite(w.badgeId)) : [];
    } catch { return []; }
  }
  function keepAnnouncements(list: WaitingAnnouncement[]) {
    if (!userId.value) return;
    try { localStorage.setItem(ANNOUNCE_KEY, JSON.stringify({ [userId.value]: list })); } catch { /* not remembered */ }
  }
  let announceTimer: ReturnType<typeof setTimeout> | null = null;
  let announcing = false;
  async function sendAnnouncements() {
    if (announcing || !userId.value) return;
    announcing = true;
    if (announceTimer) { clearTimeout(announceTimer); announceTimer = null; }
    try {
      const still: WaitingAnnouncement[] = [];
      for (const w of waitingAnnouncements()) {
        try { await secureWrite('chat.achievement', { track: w.track, badgeId: w.badgeId }); }   // posted, or already there
        catch (e: any) {
          // Only "not yet" is worth asking again, and not for ever (8 tries, about 15 minutes).
          if (/not earned yet/i.test(String(e?.message || '')) && w.tries < 7) still.push({ ...w, tries: w.tries + 1 });
        }
      }
      keepAnnouncements(still);
      if (still.length) announceTimer = setTimeout(() => { void sendAnnouncements(); }, Math.min(240000, 20000 * 2 ** Math.min(still[0].tries - 1, 4)));
    } finally { announcing = false; }
  }
  // Anything still waiting from before a reload is asked again once signed in.
  watch(userId, id => { if (id && waitingAnnouncements().length) setTimeout(() => { void sendAnnouncements(); }, 8000); }, { immediate: true });

  async function createSelfNotification(data: {
    title: string; body: string;
    image_url?: string; thumbnail_url?: string;
  }) {
    if (!userId.value) return;
    const row = {
      title: data.title,
      body: data.body,
      image_url: data.image_url || null,
      thumbnail_url: data.thumbnail_url || null,
      send_at: new Date().toISOString(),
      expires_at: null,
      target_type: 'user',
      target_id: userId.value,
      post_to_chat: false,
      created_by: userId.value,
    };
    await secureWrite('notification.self', { title: row.title, body: row.body, image_url: row.image_url, thumbnail_url: row.thumbnail_url })
      .catch((err: any) => console.warn('[notifications] self-notification error:', err?.message));
    await loadNotifications();
  }

  async function deleteNotification(notifId: number) {
    if (!isAdmin.value) return;
    try { await secureWrite('notification.delete', { id: notifId }); }
    catch (error: any) { console.warn('[admin] deleteNotification failed:', error?.message); throw error; }
    notifications.value = notifications.value.filter(n => n.id !== notifId);
    // Also drop it from the scheduled queue — deleting a pending notification
    // is how an admin cancels it.
    adminNotifications.value = adminNotifications.value.filter(n => n.id !== notifId);
  }

  /** Delete (permanently hide) one notification for the current user. */
  async function dismissNotification(notifId: number) {
    notificationDismissals.value.add(notifId);
    notifications.value = notifications.value.filter(n => n.id !== notifId);
    if (!userId.value) return;
    tellOtherTabs([notifId], true);
    const { error } = await supabase.from('notification_reads').upsert({
      notification_id: notifId,
      user_id: userId.value,
      dismissed: true,
      dismissed_at: new Date().toISOString(),
    }, { onConflict: 'notification_id,user_id' });
    if (error) console.warn('[notifications] dismiss failed:', error.message);
  }

  /**
   * Delete all of the current user's visible notifications.
   *
   * Deliberately separate from "mark all read": read state controls the unread
   * pip, this clears the feed. Neither one deletes the underlying
   * `notifications` row (that stays admin-only) — it only hides them for this
   * user, so one person clearing their feed never affects anyone else's.
   */
  async function dismissAllNotifications(only?: number[]) {
    if (!userId.value) return;
    // `only`: just these ids (one tab of the feed); otherwise the whole feed.
    const pick = only ? new Set(only) : null;
    const visible = notifications.value.filter(n => !pick || pick.has(n.id));
    if (!visible.length) return;
    for (const n of visible) notificationDismissals.value.add(n.id);
    notifications.value = pick ? notifications.value.filter(n => !pick.has(n.id)) : [];
    tellOtherTabs(visible.map(n => n.id), true);
    const rows = visible.map(n => ({
      notification_id: n.id,
      user_id: userId.value,
      dismissed: true,
      dismissed_at: new Date().toISOString(),
    }));
    const { error } = await supabase
      .from('notification_reads')
      .upsert(rows, { onConflict: 'notification_id,user_id' });
    if (error) console.warn('[notifications] dismiss-all failed:', error.message);
  }

  let notifStopShared: (() => void)[] = [];
  // Chosen among the tabs that are checking (signed in), so the leader is
  // never a signed-out tab with nothing to ask for.
  const notifLeader = leadership('pyr-leader-notifications');
  function subscribeToNotifications() {
    if (notifSubscription) return;
    // Only a signed-in tab stands: a signed-out one holding the lock would
    // keep every signed-in tab from checking.
    const stopStanding = watch(userId, id => { if (id) notifLeader.join(); else notifLeader.leave(); }, { immediate: true });
    // Private notifications cannot use an anonymous Realtime subscription.
    // Refresh through the identity-verified route, without overlapping requests.
    let refreshing = false;
    let checks = 0;
    // A new achievement in the feed opens its celebration, in every tab.
    const celebrateNew = (existing: Set<number>) => {
      const row = notifications.value.find(n => !existing.has(n.id) &&
        n.title?.includes('New Achievement') && n.target_type !== 'group' &&
        (!n.target_id || n.target_id === userId.value));
      if (row) pendingBadgeCelebration.value = {
        title: row.title, body: row.body || '', imageUrl: row.image_url || row.thumbnail_url || '',
      };
    };
    // Only the leader tab asks (Ames 2026-10-25: one check per browser, not
    // one per tab), while any Pyr tab is in front, and shares what it read.
    const check = async () => {
      if (refreshing || !notifLeader.isLeader() || !anyTabVisible()) return;
      refreshing = true;
      checks++;
      const existing = new Set(notifications.value.map(n => n.id));
      try {
        await loadNotifications({ newOnly: checks % 10 !== 0, share: true });
        celebrateNew(existing);
      } catch { /* retry on the next refresh */ }
      finally { refreshing = false; }
    };
    notifSubscription = setInterval(() => { void check(); }, 60_000);
    notifStopShared = [
      stopStanding,
      onSharedResult<SharedNotifs>('notifications', async shared => {
        const existing = new Set(notifications.value.map(n => n.id));
        try { await loadNotifications({ shared }); celebrateNew(existing); } catch { /* the next one */ }
      }),
      // The leader closed and this tab took over: its check is due now.
      notifLeader.onChange((leader, takeover) => { if (leader && takeover) void check(); }),
    ];
  }

  function unsubscribeFromNotifications() {
    if (notifSubscription) {
      clearInterval(notifSubscription);
      notifSubscription = null;
    }
    for (const stop of notifStopShared) stop();
    notifStopShared = [];
    notifLeader.leave();
  }

  // ── User Groups ───────────────────────────────────────────────────────

  interface UserGroup {
    id: number;
    name: string;
    description: string;
    color: string;
    created_at: string;
    member_count?: number;
  }

  interface GroupMember {
    user_id: string;
    display_name: string;
    email: string;
    added_at: string;
  }

  const groups = ref<UserGroup[]>([]);

  async function loadGroups() {
    const { data } = await supabase
      .from('user_groups')
      .select('*')
      .order('name');
    if (data) {
      // Get member counts
      for (const g of data) {
        const { count } = await supabase
          .from('user_group_members')
          .select('id', { count: 'exact', head: true })
          .eq('group_id', g.id);
        g.member_count = count || 0;
      }
      groups.value = data;
    }
  }

  async function createGroup(name: string, description: string = '', color: string = '#4a9eff') {
    if (!isAdmin.value || !userId.value) return;
    const { error: err } = await supabase.from('user_groups').insert({
      name, description, color, created_by: userId.value,
    });
    if (err) console.warn('[admin] createGroup error:', err.message);
    await loadGroups();
  }

  async function deleteGroup(groupId: number) {
    if (!isAdmin.value) return;
    await supabase.from('user_groups').delete().eq('id', groupId);
    await loadGroups();
  }

  async function loadGroupMembers(groupId: number): Promise<GroupMember[]> {
    const { data } = await supabase
      .from('user_group_members')
      .select('user_id, added_at, users!user_group_members_user_id_fkey(display_name, middleauth_email)')
      .eq('group_id', groupId);
    return (data || []).map((m: any) => ({
      user_id: m.user_id,
      display_name: m.users?.display_name || '',
      email: m.users?.middleauth_email || '',
      added_at: m.added_at,
    }));
  }

  async function addGroupMembers(groupId: number, userIds: string[]) {
    if (!isAdmin.value || !userId.value) return;
    const rows = userIds.map(uid => ({
      group_id: groupId, user_id: uid, added_by: userId.value,
    }));
    await supabase.from('user_group_members').upsert(rows, { onConflict: 'group_id,user_id' });
    await loadGroups();
  }

  async function removeGroupMember(groupId: number, memberId: string) {
    if (!isAdmin.value) return;
    await supabase.from('user_group_members')
      .delete()
      .eq('group_id', groupId)
      .eq('user_id', memberId);
    await loadGroups();
  }

  /** Find players by display name or @username. Email is private (the public
   *  key may not read users.middleauth_email), and asking for it made the whole
   *  query fail, so chat names, the Admin Hub member search and badge awards
   *  all found nobody (2026-09-28). */
  async function searchUsers(query: string): Promise<Array<{id: string; display_name: string; username: string; email: string}>> {
    const q = (query || '').trim().replace(/^@/, '').replace(/[,()*%]/g, '');
    if (q.length < 2) return [];
    const { data, error: e } = await supabase
      .from('users')
      .select('id, display_name, username')
      .or(`display_name.ilike.%${q}%,username.ilike.%${q}%`)
      .limit(10);
    if (e) console.warn('[backend] searchUsers error:', e.message);
    return (data || []).map((u: any) => ({
      id: u.id, display_name: u.display_name, username: u.username || '', email: '',
    }));
  }

  // ── Special Badges ────────────────────────────────────────────────────

  interface SpecialBadge {
    id: number;
    name: string;
    description: string;
    slug: string;
    image_url: string;
    thumbnail_url: string | null;
    created_at: string;
  }

  interface SpecialBadgeAward {
    id: number;
    badge_id: number;
    user_id: string;
    awarded_at: string;
    awarded_by: string | null;
    reason: string;
    badge?: SpecialBadge;
  }

  const specialBadges = ref<SpecialBadge[]>([]);
  const mySpecialBadges = ref<SpecialBadgeAward[]>([]);

  async function loadSpecialBadges() {
    const { data } = await supabase
      .from('special_badges')
      .select('*')
      .order('created_at', { ascending: false });
    specialBadges.value = data || [];
  }

  async function loadMySpecialBadges() {
    if (!userId.value) return;
    const { data } = await supabase
      .from('special_badge_awards')
      .select('*, special_badges(*)')
      .eq('user_id', userId.value)
      .order('awarded_at', { ascending: false });
    mySpecialBadges.value = (data || []).map((a: any) => ({
      ...a,
      badge: a.special_badges || undefined,
    }));
  }

  /** Load special badges for any user (for viewing other profiles). */
  async function loadUserSpecialBadges(uid: string): Promise<SpecialBadgeAward[]> {
    const { data } = await supabase
      .from('special_badge_awards')
      .select('*, special_badges(*)')
      .eq('user_id', uid)
      .order('awarded_at', { ascending: false });
    return (data || []).map((a: any) => ({
      ...a,
      badge: a.special_badges || undefined,
    }));
  }

  // ── Building/Exploration badge awards ─────────────────────────────────────
  // Persist the first time each edits/cells threshold is crossed, so a badge
  // stays earned even if the underlying stat later drops. Keyed 'track:badgeId'.
  const myBadgeAwards = ref<Set<string>>(new Set());
  const badgeAwardsLoaded = ref(false);
  function badgeAwardKey(track: string, badgeId: number): string {
    return `${track}:${badgeId}`;
  }
  async function loadMyBadgeAwards() {
    if (!userId.value) return;
    const { data, error: err } = await supabase
      .from('badge_awards')
      .select('track,badge_id')
      .eq('user_id', userId.value);
    myBadgeAwards.value = new Set((data || []).map((r: any) => badgeAwardKey(r.track, r.badge_id)));
    // Only a list that was really read may be used to decide what is new.
    if (!err && data) badgeAwardsLoaded.value = true;
  }
  // Saved Achievements of OTHER players, for their profile and the
  // leaderboard (Ames 2026-10-11: a tier's number can change, an earned
  // Achievement cannot be lost). Read once per player per session.
  const savedAwards = ref<Record<string, Set<string>>>({});
  async function loadSavedAwards(ids: Array<string | null | undefined>) {
    const want = [...new Set(ids)].filter((id): id is string => !!id && !savedAwards.value[id]);
    for (let i = 0; i < want.length; i += 40) {
      const part = want.slice(i, i + 40);
      const next: Record<string, Set<string>> = {};
      let read = true;
      for (let from = 0; ; from += 500) {
        const { data, error: err } = await supabase.from('badge_awards').select('user_id,track,badge_id')
          .in('user_id', part).order('user_id').order('track').order('badge_id').range(from, from + 499);
        if (err || !data) { read = false; break; }   // not read: ask again next time
        for (const r of data as any[]) (next[r.user_id] ||= new Set()).add(badgeAwardKey(r.track, r.badge_id));
        if (data.length < 500) break;
      }
      if (!read) continue;
      for (const id of part) if (!next[id]) next[id] = new Set();
      savedAwards.value = { ...savedAwards.value, ...next };
    }
  }
  /** This player holds this Achievement as a saved record. */
  function holdsSaved(who: string | null | undefined, track: string, badgeId: number | null | undefined): boolean {
    if (!who || badgeId == null) return false;
    const key = badgeAwardKey(track, badgeId);
    if (who === userId.value && myBadgeAwards.value.has(key)) return true;
    return !!savedAwards.value[who]?.has(key);
  }
  async function recordBadgeAward(track: string, badgeId: number) {
    if (!userId.value || badgeId == null) return;
    const key = badgeAwardKey(track, badgeId);
    if (myBadgeAwards.value.has(key)) return;   // already recorded this session
    myBadgeAwards.value.add(key);
    const { error: err } = await supabase
      .from('badge_awards')
      .upsert({ user_id: userId.value, track, badge_id: badgeId },
              { onConflict: 'user_id,track,badge_id' });
    if (err) console.warn('[badge] recordBadgeAward failed:', err.message);
  }

  async function createSpecialBadge(data: {
    name: string; description: string; slug: string;
    image_url: string; thumbnail_url?: string;
  }) {
    if (!isAdmin.value || !userId.value) return;
    const { error: err } = await supabase.from('special_badges').insert({
      ...data, thumbnail_url: data.thumbnail_url || null, created_by: userId.value,
    });
    if (err) console.warn('[admin] createSpecialBadge error:', err.message);
    await loadSpecialBadges();
  }

  async function awardBadge(badgeId: number, userIds: string[], reason: string = '') {
    if (!isAdmin.value || !userId.value) return;
    const rows = userIds.map(uid => ({
      badge_id: badgeId, user_id: uid, awarded_by: userId.value, reason,
    }));
    const { error: err } = await supabase.from('special_badge_awards')
      .upsert(rows, { onConflict: 'badge_id,user_id' });
    if (err) { console.warn('[admin] awardBadge error:', err.message); return; }
    // Say so in chat, one line per player, however many there are (Ames
    // 2026-10-08: "post them all in chat, it's sort of a fun tradition in
    // eyewire chat"). One at a time with a short gap: the server turns away
    // a burst with "Please wait", and a line that was turned away is sent again.
    void (async () => {
      for (const uid of userIds) {
        for (let attempt = 0; attempt < 4; attempt++) {
          try { await secureWrite('chat.achievement', { track: 'special', badgeId, userId: uid }); break; }
          catch (e: any) {
            if (!/please wait/i.test(e?.message ?? '')) break;
            await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
          }
        }
        await new Promise(r => setTimeout(r, 350));
      }
    })();
  }

  async function awardBadgeToGroup(badgeId: number, groupId: number, reason: string = '') {
    if (!isAdmin.value || !userId.value) return;
    const { data: members } = await supabase
      .from('user_group_members')
      .select('user_id')
      .eq('group_id', groupId);
    if (members && members.length > 0) {
      await awardBadge(badgeId, members.map((m: any) => m.user_id), reason);
    }
  }

  async function revokeBadge(badgeId: number, uid: string) {
    if (!isAdmin.value) return;
    await supabase.from('special_badge_awards')
      .delete()
      .eq('badge_id', badgeId)
      .eq('user_id', uid);
  }

  // ── Image Upload ──────────────────────────────────────────────────────

  /**
   * Upload limits for admin images.
   *
   * The feed renders the thumbnail for every card, so a heavy image is paid for
   * on every open by every user, and the detail view loads the full image.
   * 3 MB (Ames 2026-09-30, was 8) keeps those opens quick; the feed itself
   * always loads the small thumbnail, never the full file.
   */
  const MAX_ADMIN_IMAGE_BYTES = 3 * 1024 * 1024;
  const ALLOWED_ADMIN_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

  /** Validate before upload. Returns an error string, or '' when acceptable. */
  function validateAdminImage(file: File): string {
    if (!ALLOWED_ADMIN_IMAGE_TYPES.includes(file.type)) {
      return `Unsupported format (${file.type || 'unknown'}). Use PNG, JPEG, WebP or GIF.`;
    }
    if (file.size > MAX_ADMIN_IMAGE_BYTES) {
      const mb = (file.size / (1024 * 1024)).toFixed(1);
      return `Image is ${mb} MB. The limit is ${MAX_ADMIN_IMAGE_BYTES / (1024 * 1024)} MB, please resize it.`;
    }
    return '';
  }

  async function uploadAdminImage(file: File, type: 'notifications' | 'badges'): Promise<{fullUrl:string;thumbUrl:string}> {
    const invalid=validateAdminImage(file); if(invalid) throw new Error(invalid);
    const fullUrl=await secureUpload(file,type);
    const thumbUrl=await secureUpload(await generateThumbnail(file,120),type);
    return {fullUrl,thumbUrl};
  }

  async function uploadHelpScreenshot(blob: Blob): Promise<string> {
    // Saved compressed (WebP, or JPEG): a fraction of the PNG's size.
    const { compressScreenshot } = await import('./util/compress_screenshot');
    return secureUpload(await compressScreenshot(blob),'help');
  }

  async function uploadAdminIcon(file: File): Promise<string> {
    const invalid=validateAdminImage(file); if(invalid) throw new Error(invalid);
    return secureUpload(await generateThumbnail(file,128),'notifications');
  }

  function generateThumbnail(file: File, maxWidth: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(maxWidth / img.width, 1);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('Canvas not supported')); return; }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => {
          if (blob) resolve(blob);
          else reject(new Error('Thumbnail generation failed'));
        }, 'image/png');
      };
      img.onerror = () => reject(new Error('Image load failed'));
      img.src = URL.createObjectURL(file);
    });
  }

  // ── Favorite Badge (persisted to Supabase) ─────────────────────────────
  const favoriteBadgeSlug: Ref<string> = ref('');

  async function loadFavoriteBadge() {
    if (!userId.value) return;
    try {
      const { data } = await supabase
        .from('users')
        .select('favorite_badge')
        .eq('id', userId.value)
        .single();
      if (data?.favorite_badge) {
        favoriteBadgeSlug.value = data.favorite_badge;
      }
    } catch (e: any) {
      console.warn('[backend] loadFavoriteBadge error:', e.message);
    }
    silverBadgeSlugs.value = await loadSilverBadges(userId.value);
    bronzeBadgeSlugs.value = await loadBronzeBadges(userId.value);
    trophyOrder.value = await loadTrophyOrder(userId.value);
  }

  // Silver favorites (Ames 2026-10-01): one gold favorite, plus a row of
  // silver ones. users.favorite_badges, a JSON list of badge slugs. Read on
  // its own so a profile still loads if the column is not there.
  const MAX_SILVER_BADGES = 5;
  const silverBadgeSlugs: Ref<string[]> = ref([]);
  async function loadSilverBadges(id: string | null): Promise<string[]> {
    if (!id) return [];
    try {
      const { data, error } = await supabase.from('users').select('favorite_badges').eq('id', id).single();
      const list = (data as any)?.favorite_badges;
      return !error && Array.isArray(list) ? list.filter((x: any) => typeof x === 'string').slice(0, MAX_SILVER_BADGES) : [];
    } catch { return []; }
  }
  async function saveSilverBadges(list: string[]) {
    silverBadgeSlugs.value = list.slice(0, MAX_SILVER_BADGES);
    if (!userId.value) return;
    try {
      const { error } = await supabase.from('users').update({ favorite_badges: silverBadgeSlugs.value }).eq('id', userId.value);
      if (error) console.warn('[backend] saveSilverBadges failed:', error.message);
    } catch (e: any) {
      console.warn('[backend] saveSilverBadges error:', e.message);
    }
  }

  // Bronze favorites (Ames 2026-10-09): a third, longer strip, for players
  // who want more of their achievements on show. users.favorite_badges_bronze.
  const MAX_BRONZE_BADGES = 10;
  const bronzeBadgeSlugs: Ref<string[]> = ref([]);
  /** A list column of the player's row; [] when it can not be read (the
   *  column is not there yet, or offline). */
  async function loadUserList(id: string | null, column: string, max: number): Promise<string[]> {
    if (!id) return [];
    try {
      const { data, error } = await supabase.from('users').select(column).eq('id', id).single();
      const list = (data as any)?.[column];
      return !error && Array.isArray(list) ? list.filter((x: any) => typeof x === 'string').slice(0, max) : [];
    } catch { return []; }
  }
  const loadBronzeBadges = (id: string | null) => loadUserList(id, 'favorite_badges_bronze', MAX_BRONZE_BADGES);
  async function saveBronzeBadges(list: string[]) {
    bronzeBadgeSlugs.value = list.slice(0, MAX_BRONZE_BADGES);
    if (!userId.value) return;
    try {
      const { error } = await supabase.from('users').update({ favorite_badges_bronze: bronzeBadgeSlugs.value }).eq('id', userId.value);
      if (error) console.warn('[backend] saveBronzeBadges failed:', error.message);
    } catch (e: any) { console.warn('[backend] saveBronzeBadges error:', e.message); }
  }
  // The order a player keeps their Trophy Case sections in. users.trophy_order.
  const trophyOrder: Ref<string[]> = ref([]);
  const loadTrophyOrder = (id: string | null) => loadUserList(id, 'trophy_order', 4);
  async function saveTrophyOrder(list: string[]) {
    trophyOrder.value = list.slice(0, 4);
    if (!userId.value) return;
    try {
      const { error } = await supabase.from('users').update({ trophy_order: trophyOrder.value }).eq('id', userId.value);
      if (error) console.warn('[backend] saveTrophyOrder failed:', error.message);
    } catch (e: any) { console.warn('[backend] saveTrophyOrder error:', e.message); }
  }

  async function saveFavoriteBadge(slug: string) {
    favoriteBadgeSlug.value = slug;
    if (!userId.value) return;
    try {
      await supabase
        .from('users')
        .update({ favorite_badge: slug || null })
        .eq('id', userId.value);
    } catch (e: any) {
      console.warn('[backend] saveFavoriteBadge error:', e.message);
    }
  }

  return {
    userId, userEmail, userName, tasks, activeTaskId, activityFeed, loading, error,
    username, chatHandle, validateUsername, isUsernameAvailable, saveUsername, suggestUsername,
    leaderboard, leaderboardMyRanks, loadDayTotal, loadAccessGrants, leaderboardState, leaderboardAt, loadLeaderboardDetail,
    syncUser, captureCaveUserId, loadTasks, claimTask, releaseTask, completeTask,
    tasksPaged, taskCounts, taskPages, loadingMore, loadMoreTasks, findTasks, loadAvailableAfter,
    logEdit, postActivity, subscribeToFeed, unsubscribeFromFeed,
    importFromGoogleSheet, syncStats, saveProfileFields, loadUserStats, loadUserProfile, loadLeaderboard, loadWeeklyPodium,
    // Point-in-space claims
    claimCell, releaseCell, releaseBySegment, releaseTaskById, reopenTask, saveWorkingLink, loadMyActiveClaims, isClaimedPoint, isClaimedSegment, myActiveClaimCount,
    refreshSegmentIds, currentRoots, isTaskCell, repairListedIds,
    liveRoots, refreshLiveRoots, anchorClaimAt,
    MAX_CLAIMS, claimLimitFor,
    // Admin Hub
    isAdmin, checkAdmin, isBlogAuthor, playerView, setPlayerView, showAdminUi,
    // Notifications
    notifications, notificationReads, notifRead, huntReportsShown, unreadNotificationCount, unreadAdminNotificationCount, unreadStaffNotificationCount, loadNotifications,
    notificationsHaveMore, loadMoreNotifications,
    adminNotifications, adminNotifHasMore, loadAdminNotifications, updateNotification,
    markNotificationRead, markAllNotificationsRead, markReportNotesRead, markReportsNotesRead,
    createNotification, createSelfNotification, announceAchievement, postDueChatAnnouncements, deleteNotification, dismissNotification,
    dismissAllNotifications,
    subscribeToNotifications, unsubscribeFromNotifications,
    pendingBadgeCelebration,
    pendingCellCelebration,
    // User Groups
    groups, loadGroups, createGroup, deleteGroup,
    loadGroupMembers, addGroupMembers, removeGroupMember, searchUsers,
    // Special Badges
    specialBadges, mySpecialBadges,
    myBadgeAwards, badgeAwardsLoaded, loadMyBadgeAwards, recordBadgeAward, loadSavedAwards, holdsSaved,
    loadSpecialBadges, loadMySpecialBadges, loadUserSpecialBadges,
    createSpecialBadge, awardBadge, awardBadgeToGroup, revokeBadge,
    // Image Upload
    uploadAdminImage, uploadAdminIcon, uploadHelpScreenshot, validateAdminImage, MAX_ADMIN_IMAGE_BYTES,
    // Favorite Badge
    favoriteBadgeSlug, loadFavoriteBadge, saveFavoriteBadge,
    silverBadgeSlugs, loadSilverBadges, saveSilverBadges, MAX_SILVER_BADGES,
    bronzeBadgeSlugs, loadBronzeBadges, saveBronzeBadges, MAX_BRONZE_BADGES,
    trophyOrder, loadTrophyOrder, saveTrophyOrder,
  };
});

/** The address at page load, read before neuroglancer rewrites it with the
 *  restored state (store.ts is imported before the viewer is made). */
const BOOT_HASH = window.location.hash;

export const useVolumesStore = defineStore('volumes', () => {
  const volumes: Ref<Volume[]> = ref([]);

  async function loadVolumes(_viewer?: any) {
      if (!CONFIG || !CONFIG.volumes_url) return;
      const {url, credentialsProvider} = parseSpecialUrl(CONFIG.volumes_url, defaultCredentialsManager);
      const response = await cancellableFetchSpecialOk(credentialsProvider, url, {}, responseJson);

        for (const [key, value] of Object.entries(response as any)) {
          if (CONFIG.volumes_enabled && !CONFIG.volumes_enabled.includes(key)) {
            continue;
          }
          volumes.value.push({
            name: key,
            description: (value as any).description,
            image_layers: (value as any).image_layers.map((x: any) => {
              x.type = 'image';
              x.source = [x.image_source];
              return x;
            }),
            segmentation_layers: (value as any).segmentation_layers.map((x: any) => {
              x.type = 'segmentation';
              x.source = [x.segmentation_source];
              if (x.skeleton_source) {
                x.source.push(x.skeleton_source);
              }
              return x;
            }),
          });
        }

        // Auto-select default volume if no layers loaded yet
        const layerStore = useLayersStore();
        const layersEmpty = () => layerStore.activeLayers.size === 0 || (layerStore.activeLayers.size === 1 && layerStore.activeLayers.has(''));
        // A state link in the address (#!https://...) is fetched after this
        // list arrives, so layers are still empty here on a refresh. Wait for
        // it, or the curated redirect below replaced the player's view.
        if (layersEmpty() && /^#!([a-z][a-z\d+-.]*):\/\//.test(BOOT_HASH)) {
          await new Promise<void>(resolve => {
            const done = () => { clearTimeout(timer); stop(); resolve(); };
            const timer = setTimeout(done, 20000);
            const stop = watch(() => layerStore.activeLayers.size, () => { if (!layersEmpty()) done(); });
          });
        }
        if (layersEmpty()) {
          if (CONFIG.volumes_default) {
            const volume = volumes.value.find(x => x.name === CONFIG.volumes_default?.name);
            if (volume) {
              const imageLayer = volume.image_layers.find(x => x.name === CONFIG.volumes_default?.image);
              const segmentationLayer = volume.segmentation_layers.find(x => x.name === CONFIG.volumes_default?.segmentation);
              if (imageLayer && segmentationLayer) {
                // The address held the player's own view (not empty, not the
                // curated one) that has not loaded: show the volume in place
                // rather than redirecting away from it.
                const curated = getDatasetCaveConfig(segmentationLayer.name).defaultStateUrl || '';
                const curatedHash = curated.includes('#') ? curated.slice(curated.indexOf('#')) : '';
                const userHash = !!BOOT_HASH && BOOT_HASH !== '#' && BOOT_HASH !== '#!' && BOOT_HASH !== curatedHash;
                layerStore.selectLayers([imageLayer, segmentationLayer], {keepHash: userHash});
              }
            }
          }
        } else if (!BOOT_HASH || BOOT_HASH === '#' || BOOT_HASH === '#!') {
          // A bare address opens the build's own default view, which already
          // has its layers, so the switch above never ran and the landing had
          // no cells (Ames 2026-10-20). Arrive at that dataset the way a
          // switch to it does: the player's start view, the curated view or
          // its starter cells, by the same Settings.
          const v: any = (window as any).viewer;
          const seg = v?.layerManager?.managedLayers?.find((l: any) => String(l?.layer?.constructor?.type || '').startsWith('segmentation'));
          const visible = seg?.layer?.displayState?.segmentationGroupState?.value?.visibleSegments;
          if (seg && visible && visible.size === 0 && getDatasetCaveConfig(seg.name).defaultSegments?.length) {
            const layers = v.state.toJSON().layers;
            if (Array.isArray(layers)) void layerStore.selectLayers(layers);
          }
        }
  }

  // Auto-load on store creation
  loadVolumes();

  return {loadVolumes, volumes};
});

// ════════════════════════════════════════════════════════════════════════════════
// Chat Store — Supabase Realtime Broadcast (no separate WebSocket server needed)
// ════════════════════════════════════════════════════════════════════════════════

export interface MessagePart {
  type: 'text' | 'link' | 'sender' | 'segment' | 'mention';
  text: string;
}

export interface ChatMessage {
  type: 'message' | 'join' | 'leave' | 'disconnected' | 'time' | 'complete';
  name: string;
  rank: string;
  time?: string;
  dateTime: Date;
  parts: MessagePart[];
  /** Segmentation layer the sender was viewing, for #SegID disambiguation.
   *  Null on messages sent before this was recorded. */
  dataset?: string | null;
  /** Set on announcement messages so clicking opens that notification. */
  notificationId?: number | null;
  /** chat_messages row id (a uuid), for deletion and reactions. */
  id?: string | number | null;
  /** A 'complete' line: how many cells it stands for (a run by one player). */
  count?: number;
  /** Verified sender (chat_messages.user_id), so authors can delete their own. */
  userId?: string | null;
  /** Set on nkem_test's replies: the language of its "for science". */
  botLanguage?: string;
  /** The message this one replies to (chat_messages.reply_to), if any. */
  replyTo?: string | null;
  /** Nurro's daily leaders card (the text part is the plain fallback). */
  daily?: { edits: Array<{ name: string; n: number }>; cells: Array<{ name: string; n: number }> };
  /** Only on this screen: your Nurro command and Nurro's answer (Amy 2026-09-30). */
  private?: boolean;
}

/**
 * Name of the active segmentation layer — our dataset key.
 *
 * Root IDs only mean anything within a single segmentation, so anything that
 * shares a segment ID between users has to record which dataset it came from.
 */
function currentDatasetName(): string | null {
  try {
    const viewer: any = (window as any)['viewer'];
    const layers = viewer?.layerManager?.managedLayers || [];
    for (const l of layers) {
      const cn = l?.layer?.constructor?.name || '';
      if (cn.includes('Segmentation') || l?.layer?.type === 'segmentation') {
        return l.name || null;
      }
    }
  } catch { /* viewer not ready */ }
  return null;
}

/** Parse message text into parts (text + auto-detected links + #SegID references) */
/** How many digits a #number needs to count as a cell ID. Cell IDs on the
 *  proofreading datasets are 15 digits or more; "#12345" in ordinary talk was
 *  being turned into a cell link (Krzysztof 2026-10-09). The explore only
 *  volumes with short body IDs (H01, MANC, MCNS, MAOL) keep the old 5. */
function minIdDigits(dataset?: string | null): number {
  return /h01|manc|mcns|malecns|maol|optic_lobe/i.test(dataset || '') ? 5 : 15;
}
function parseMessageParts(name: string, text: string, dataset?: string | null): MessagePart[] {
  const parts: MessagePart[] = [{ type: 'sender', text: name }];
  const idDigits = minIdDigits(dataset);
  // Split on URLs, #SegmentID references, and @mentions.
  //
  // A mention is a SINGLE token: we can't reliably guess where a multi-word
  // display name ends in free text ("@Amy S. and Bob" vs "@celia look at this"
  // are indistinguishable), and trying to swallow a second word grabbed the
  // following ordinary word instead. So match one token and let the self-match
  // below compare against the first word of a display name.
  //
  // The lookbehind prevents matching the "@" inside an email address
  // (a@b.com) or a doubled "@@".
  const tokenRegex = /(https?:\/\/\S+|#\d{5,}|(?<![\w@.])@[A-Za-z0-9._-]{2,})/g;
  const segments = text.split(tokenRegex);
  for (const seg of segments) {
    if (!seg) continue;
    if (/^https?:\/\//.test(seg)) {
      parts.push({ type: 'link', text: seg });
    } else if (/^#\d{5,}$/.test(seg) && seg.length - 1 >= idDigits) {
      parts.push({ type: 'segment', text: seg });
    } else if (/^@[A-Za-z0-9._-]/.test(seg)) {
      parts.push({ type: 'mention', text: seg });
    } else {
      parts.push({ type: 'text', text: seg });
    }
  }
  return parts;
}

/** Chat messages are capped at 280 characters (Ames 2026-10-05, up from 140), and links don't count
 *  toward it (Amy 2026-10-05), so a shared view link never eats the limit. */
export const CHAT_MAX_CHARS = 280;
export function chatTextLength(text: string): number {
  return Array.from(text.replace(/https?:\/\/\S+/g, '').trim()).length;
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/**
 * Is this @mention token aimed at the given user? The unique, space-free
 * username is the reliable match; the display name ("Amy S.") is a fuzzy
 * fallback for people who haven't set a username yet.
 */
export function isSelfMentionToken(token: string, username?: string | null, displayName?: string | null): boolean {
  const norm = (s: string) => s.replace(/[.\s]+$/, '').trim().toLowerCase();
  const target = norm(token.replace(/^@/, ''));
  if (!target) return false;
  const handle = (username || '').trim();
  if (handle) return target === norm(handle);
  const me = (displayName || '').trim();
  if (!me) return false;
  return target === norm(me) || target === norm(me.split(' ')[0]);
}

const MENTION_NOTIFY_KEY = 'nge-chat-mention-notify';
function readMentionNotifyPref(): boolean {
  try {
    return localStorage.getItem(MENTION_NOTIFY_KEY) === '1'
      && typeof Notification !== 'undefined' && Notification.permission === 'granted';
  } catch { return false; }
}

/** Reactions offered on chat messages. The ewCommunityData gateway accepts
 *  exactly these, so keep functions/community-data.js in step. */
/** The Pyr gem as a reaction (Ames 2026-10-27). Not an emoji: stored as this
 *  code and drawn as the gem picture (ChatPanel). */
export const CHAT_REACTION_GEM = ':pyr-gem:';
export const CHAT_REACTION_QUICK = [CHAT_REACTION_GEM, '👍', '❤️', '🔥', '😂', '🎉', '🧠'];
/** More reactions, behind the picker's "+" (Nseraf 2026-10-11 asked for
 *  more; Ames approved this list). All friendly or neutral. */
export const CHAT_REACTION_MORE = ['👋', '👌', '👏', '🙌', '🙏', '💯', '✅', '🤝', '🫡', '😀', '😄', '😊', '😎', '🤩', '🥳', '😅', '🤗', '🤔', '👀', '😮', '🤯', '💡', '❓', '😢', '🫂', '💙', '💜', '🔬', '🧬', '🧪', '✂️', '🔗', '🧩', '🗺️', '🏆', '⭐', '✨', '🚀', '🌈'];
export const CHAT_REACTION_EMOJI = [...CHAT_REACTION_QUICK, ...CHAT_REACTION_MORE];

export const useChatStore = defineStore('chat', () => {
  const chatMessages = ref<ChatMessage[]>([]);
  const connected = ref(false);
  const unreadMessages = ref(false);
  /** Numeric unread counter for the green pip on the chat toolbar
   *  icon. Cleared on markRead(); muting prevents increments. */
  const unreadCount = ref(0);
  /** True while the chat panel is open and expanded (ChatPanel sets it).
   *  Messages you can already see don't add to the toolbar counter (Ames,
   *  2026-09-28: "if I have chat open, I don't need a message counter"). */
  const panelVisible = ref(false);
  /** Bumped each time someone @mentions you, so the toolbar chat button and
   *  the chat panel can flash to draw your eye. */
  const mentionPing = ref(0);
  const lastMentionFrom = ref('');
  /** History paging: the oldest message loaded, and whether older ones exist. */
  const hasMoreHistory = ref(true);
  const loadingHistory = ref(false);

  // ── The pinned message (Ames 2026-10-10) ───────────────────────────────
  // An admin pins a message; the server writes a chat row of rank 'pin'
  // (functions/chat-pin.js). The newest such row is the pin: it names the
  // message and carries its words, or says the pin was taken down.
  interface ChatPin { rowId: string; messageId: string; text: string; by: string; at: number; }
  const pinned = ref<ChatPin | null>(null);
  let pinAt = 0;
  /** Take a chat row of rank 'pin' into account if it is the newest seen. */
  function notePinRow(row: any) {
    if (!row || row.rank !== 'pin') return;
    const at = new Date(row.created_at ?? Date.now()).getTime();
    if (at < pinAt) return;
    pinAt = at;
    const m = /^pin:([A-Za-z0-9-]{1,64})$/.exec(String(row.dataset || ''));
    pinned.value = !m || m[1] === 'none' ? null
      : { rowId: String(row.id ?? ''), messageId: m[1], text: String(row.text || '').replace(/^\u{1F4CC}\s*/u, ''), by: String(row.name || ''), at };
  }
  /** The newest pin row, wherever it is in the history. */
  async function loadPin() {
    try {
      const { data } = await supabase.from('chat_messages').select('id,name,rank,text,dataset,created_at')
        .eq('rank', 'pin').order('created_at', { ascending: false }).limit(1);
      notePinRow((data as any[] | null)?.[0]);
    } catch { /* no pin shown */ }
  }
  /** Admins: pin a message for everyone, or take the pin down. */
  async function pinMessage(id: string | null): Promise<boolean> {
    try { await secureWrite(id ? 'chat.pin' : 'chat.unpin', id ? { id } : {}); return true; }
    catch (e: any) { console.warn('[chat] pin failed:', e?.message); return false; }
  }
  let oldestLoadedAt: string | null = null;
  /** Reactions per message id: emoji to who reacted. */
  const reactions = ref<Record<string, Record<string, Array<{ userId: string; name: string }>>>>({});
  /** Browser notification on @mentions while EyeWire is in the background. */
  const mentionNotify = ref(readMentionNotifyPref());

  let channel: ReturnType<typeof supabase.channel> | null = null;
  let connecting = false;
  // ── Chat without live updates (Ames 2026-10-13) ──────────────────────────
  // Messages arrive over a live connection (Supabase Realtime). When that
  // service is down the connection cannot be made, and chat used to go dead:
  // the box was switched off and nothing could be sent or seen, for everyone,
  // until the service came back. But SENDING never needed the live
  // connection (a message is saved through the server), and new messages can
  // be asked for. So while the live connection is down, chat asks for new
  // messages every few seconds and stays usable, and it keeps trying to get
  // the live connection back, a little less often each time.
  const polling = ref(false);
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let rejoinTimer: ReturnType<typeof setTimeout> | null = null;
  let rejoinDelay = 5000;
  let openChannel: (() => void) | null = null;
  const POLL_MS = 8000;
  function startPolling() {
    if (polling.value) return;
    polling.value = true;
    void loadMissed();
    pollTimer = setInterval(() => { void loadMissed(); }, POLL_MS);
    // Being online, and who else is, never needed the live connection either.
    void heartbeat().then(() => loadOnline());
    startPresenceLoops();
  }
  function stopPolling() {
    polling.value = false;
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  }
  function rejoinSoon() {
    if (rejoinTimer || !openChannel) return;
    rejoinTimer = setTimeout(() => {
      rejoinTimer = null;
      if (connected.value || !openChannel) return;
      const old = channel;
      channel = null;                       // so the old one's closing is not taken for a new drop
      if (old) { try { void supabase.removeChannel(old); } catch { /* already gone */ } }
      rejoinDelay = Math.min(rejoinDelay * 2, 60_000);
      openChannel();
    }, rejoinDelay);
  }
  let lastMessageDate = '';

  // ── Presence (Amy 2026-09-28: join notices and "# online" came back) ──────
  // chat_presence rows are written only through the verified gateway: it sets
  // user_id, name and last_seen_at from the signed-in identity, and a database
  // trigger stamps joined_at when someone returns after 2+ minutes away
  // (supabase-chat-presence-and-link-screenshots.sql). So a join notice can't
  // be forged, unlike the old open broadcast the security fix removed.
  // A hidden tab's heartbeat can come up to about 2 minutes apart.
  const ONLINE_MS = 150_000;
  const HEARTBEAT_MS = 60_000;
  const online = ref<Record<string, { name: string; lastSeen: number; dataset?: string }>>({});
  /** Bumped every 30 s so onlineCount re-evaluates as people go stale. */
  const presenceTick = ref(0);
  const onlineCount = computed(() => {
    void presenceTick.value; // re-evaluate on the tick; Date.now() is not reactive
    const cutoff = Date.now() - ONLINE_MS;
    return Object.values(online.value).filter(p => p.lastSeen >= cutoff).length;
  });
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  let staleTimer: ReturnType<typeof setInterval> | null = null;
  // One tab per browser says the player is here and reads who else is
  // (Ames 2026-10-25), among the tabs with chat connected; the rest are
  // handed the list. presenceTick runs in every tab so stale names drop.
  const presenceLeader = leadership('pyr-leader-chat');
  // Only a signed-in tab stands, as for notifications: a signed-out one
  // holding the lock would send no heartbeat for anyone.
  let standingWatched = false;
  function startPresenceLoops() {
    if (!standingWatched) {
      standingWatched = true;
      watch(() => useProofreadingBackendStore().userId, id => {
        if (id && heartbeatTimer) presenceLeader.join(); else presenceLeader.leave();
      });
    }
    if (useProofreadingBackendStore().userId) presenceLeader.join();
    if (!heartbeatTimer) heartbeatTimer = setInterval(() => { if (presenceLeader.isLeader()) void heartbeat(); }, HEARTBEAT_MS);
    if (!staleTimer) staleTimer = setInterval(() => {
      presenceTick.value++;
      if (presenceLeader.isLeader() && anyTabVisible()) void loadOnline(true);
    }, 60_000);
  }
  // The leader closed: this tab says so at once instead of up to a minute on.
  presenceLeader.onChange((leader, takeover) => {
    if (leader && takeover && channel) void heartbeat().then(() => loadOnline(true));
  });
  onSharedResult<{ user: string; online: typeof online.value }>('chat-online', m => {
    if (channel && m && m.user === useProofreadingBackendStore().userId) online.value = m.online;
  });

  function sysLine(type: 'join' | 'leave', name: string) {
    const now = new Date();
    addTimeSeparatorIfNeeded(now);
    chatMessages.value.push({ type, name, rank: '', time: formatTime(now), dateTime: now,
      parts: [{ type: 'text', text: `${name} ${type === 'join' ? 'joined' : 'left'} the chat` }] });
  }

  // Ticker: "amy completed a cell", a grey line like the join note (Ames
  // 2026-10-01). One completion logs 'mark_complete' and/or 'complete_task'
  // seconds apart, so the pair is folded into one line: same cell id, or, when
  // one of the two rows carries no id, within ten seconds of the last line.
  const tickerSeen: Record<string, { at: number; cell: string }[]> = {};
  const tickerNames: Record<string, string> = {};
  async function onCompletion(payload: any) {
    const row = payload.new || {};
    if (!row.user_id || row.success === false) return;
    if (row.operation !== 'mark_complete' && row.operation !== 'complete_task') return;
    const md = row.metadata || {};
    const cell = String(md.final_segment_id || md.root_id || md.segment_id || '');
    const now = Date.now();
    const seen = (tickerSeen[row.user_id] = (tickerSeen[row.user_id] || []).filter(e => now - e.at < 10 * 60_000));
    if (seen.some(e => (cell && e.cell === cell) || ((!cell || !e.cell) && now - e.at < 10_000))) return;
    seen.push({ at: now, cell });
    let name = online.value[row.user_id]?.name || tickerNames[row.user_id] || '';
    if (!name) {
      const { data } = await supabase.from('user_edit_counts').select('display_name').eq('id', row.user_id).limit(1);
      name = (data as any[] | null)?.[0]?.display_name || '';
      if (name) tickerNames[row.user_id] = name;
    }
    if (!name) return;
    pushCompletion(name, new Date(), completionKind(row.dataset));
  }

  // Which kind of cell it was, from the dataset the completion was logged in
  // (Nik 2026-10-10: "<player name> completed a retinal/cortex/hippocampus
  // cell"). '' for a dataset with no short word: the line then says "a cell".
  function completionKind(dataset: unknown): string {
    const d = String(dataset ?? '').toLowerCase();
    if (/retina|^eyewire_ii$/.test(d)) return 'retina';
    if (/pinky/.test(d)) return 'Sandbox';
    if (/ca3/.test(d)) return 'hippocampus';
    if (/mec/.test(d)) return 'entorhinal cortex';
    if (/h01/.test(d)) return 'human cortex';
    if (/minnie|microns/.test(d)) return 'cortex';
    if (/banc|brain_and_nerve_cord/.test(d)) return 'fly';
    if (/flywire|fafb/.test(d)) return 'fly brain';
    return '';
  }
  const completionText = (name: string, n: number, kind = '') => {
    const cell = kind ? `${kind} cell` : 'cell';
    if (n > 1) return `${name} completed ${n.toLocaleString()} ${cell}s`;
    return `${name} completed ${/^[aeiou]/i.test(cell) ? 'an' : 'a'} ${cell}`;
  };
  /** One more completion for `name`. A run by one player with nothing said in
   *  between is ONE line that counts up ("Nseraf completed 240 cells"), so a
   *  batch of hundreds does not push the conversation out of the chat. */
  function pushCompletion(name: string, at: Date, kind = '') {
    const list = chatMessages.value;
    const last = list[list.length - 1];
    // a run counts up only while it is the same player, day and kind of cell
    if (last && last.type === 'complete' && last.name === name && (last.dataset || '') === kind
        && last.dateTime.toDateString() === at.toDateString()) {
      const n = (last.count || 1) + 1;
      list[list.length - 1] = { ...last, count: n, time: formatTime(at), dateTime: at, parts: [{ type: 'text', text: completionText(name, n, kind) }] };
      return;
    }
    addTimeSeparatorIfNeeded(at);
    list.push({ type: 'complete', name, rank: '', dataset: kind, count: 1, time: formatTime(at), dateTime: at, parts: [{ type: 'text', text: completionText(name, 1, kind) }] });
  }

  /** The completion lines are made from the activity log as it happens, so a
   *  refresh used to lose every one of them (Ames 2026-10-06: "after refresh
   *  they went away"). On load, the same lines are rebuilt from the log for
   *  the stretch of time the loaded chat covers, and woven in by time. */
  async function loadCompletionLines(sinceIso: string) {
    try {
      const PAGE = 1000, MAX_PAGES = 5;
      const rows: any[] = [];
      let truncated = false;
      for (let page = 0; page < MAX_PAGES; page++) {
        const { data, error } = await supabase.from('edit_log')
          .select('user_id,operation,timestamp,metadata,success,dataset')
          .in('operation', ['mark_complete', 'complete_task'])
          .gte('timestamp', sinceIso)
          .order('timestamp', { ascending: false })
          .range(page * PAGE, page * PAGE + PAGE - 1);
        if (error || !data) return;
        rows.push(...(data as any[]));
        if (data.length < PAGE) break;
        if (page === MAX_PAGES - 1) truncated = true;
      }
      rows.reverse();                                        // chronological
      // The same folding as the live ticker: one completion logs up to two rows.
      const seenBy: Record<string, { at: number; cell: string }[]> = {};
      const events: { user: string; at: Date; kind: string }[] = [];
      for (const r of rows) {
        if (!r.user_id || r.success === false) continue;
        const md = r.metadata || {};
        const cell = String(md.final_segment_id || md.root_id || md.segment_id || '');
        const t = new Date(r.timestamp).getTime();
        const seen = (seenBy[r.user_id] = (seenBy[r.user_id] || []).filter(e => t - e.at < 10 * 60_000));
        if (seen.some(e => (cell && e.cell === cell) || ((!cell || !e.cell) && t - e.at < 10_000))) continue;
        seen.push({ at: t, cell });
        events.push({ user: r.user_id, at: new Date(t), kind: completionKind(r.dataset) });
      }
      if (!events.length) return;
      const ids = [...new Set(events.map(e => e.user))].filter(id => !tickerNames[id] && !online.value[id]);
      for (let i = 0; i < ids.length; i += 100) {
        const { data } = await supabase.from('user_edit_counts').select('id,display_name').in('id', ids.slice(i, i + 100));
        for (const u of (data as any[] | null) || []) if (u.display_name) tickerNames[u.id] = u.display_name;
      }
      // Weave into the loaded chat by time. Lines already there (made live
      // while this was loading) are dropped first so nothing is counted twice.
      const base = chatMessages.value.filter(m => m.type !== 'time' && m.type !== 'complete');
      const timeline: { at: number; msg?: ChatMessage; name?: string; kind?: string }[] = base.map(m => ({ at: m.dateTime.getTime(), msg: m }));
      for (const e of events) {
        const name = online.value[e.user]?.name || tickerNames[e.user];
        if (name) timeline.push({ at: e.at.getTime(), name, kind: e.kind });
      }
      timeline.sort((a, b) => a.at - b.at);
      const out: ChatMessage[] = [];
      for (const item of timeline) {
        if (item.msg) { out.push(item.msg); continue; }
        const at = new Date(item.at), name = item.name!, kind = item.kind || '';
        const last = out[out.length - 1];
        if (last && last.type === 'complete' && last.name === name && (last.dataset || '') === kind && last.dateTime.toDateString() === at.toDateString()) {
          const n = (last.count || 1) + 1;
          out[out.length - 1] = { ...last, count: n, time: formatTime(at), dateTime: at, parts: [{ type: 'text', text: completionText(name, n, kind) }] };
        } else {
          out.push({ type: 'complete', name, rank: '', dataset: kind, count: 1, time: formatTime(at), dateTime: at, parts: [{ type: 'text', text: completionText(name, 1, kind) }] });
        }
      }
      // More completions than could be read: the oldest run would be a part
      // count, so it is left out rather than shown short.
      if (truncated) { const i = out.findIndex(m => m.type === 'complete'); if (i >= 0) out.splice(i, 1); }
      chatMessages.value = out;
      rebuildSeparators();
    } catch (e) {
      console.warn('[chat] loadCompletionLines failed:', e);
    }
  }

  // Which dataset you have open goes with the heartbeat, for the online list
  // (Krzysztof Kruk 2026-10-11). If the server does not take it yet, the
  // plain heartbeat is used for a few minutes and then it is tried again, so
  // being online never depends on it and a tab left open picks it up by itself.
  let presenceRetryAt = 0;
  const presenceTakesDataset = () => Date.now() >= presenceRetryAt;
  const presenceWithoutDataset = () => { presenceRetryAt = Date.now() + 5 * 60_000; };
  // Online means the tab is open, looked at or not (Ames 2026-10-30), so a
  // hidden tab keeps its heartbeat even with its timers slowed down. One that
  // was asleep (a closed laptop, a frozen tab) stays quiet until someone looks
  // at it, so it never announces anyone at 3 am (Nseraf, report 307).
  let lastBeatAt = 0;
  const BEAT_GONE_MS = 10 * 60_000;
  function beatWhenBack() { if (!document.hidden) void heartbeat(); }
  async function heartbeat() {
    if (document.hidden && Date.now() - lastBeatAt > BEAT_GONE_MS) return;
    lastBeatAt = Date.now();
    if (presenceTakesDataset()) {
      const { error } = await supabase.from('chat_presence').upsert({ dataset: currentDatasetTag() }, { onConflict: 'user_id' });
      if (!error) return;
      presenceWithoutDataset();
    }
    const { error } = await supabase.from('chat_presence').upsert({}, { onConflict: 'user_id' });
    if (error) console.warn('[chat] presence heartbeat failed:', error.message);
  }

  async function loadOnline(share = false) {
    const since = new Date(Date.now() - ONLINE_MS).toISOString();
    let { data, error } = await supabase.from('chat_presence')
      .select(presenceTakesDataset() ? 'user_id,name,last_seen_at,dataset' : 'user_id,name,last_seen_at').gte('last_seen_at', since).limit(500);
    if (error && presenceTakesDataset()) {
      presenceWithoutDataset();
      ({ data, error } = await supabase.from('chat_presence').select('user_id,name,last_seen_at').gte('last_seen_at', since).limit(500));
    }
    if (error || !data) return;
    const next: Record<string, { name: string; lastSeen: number; dataset?: string }> = {};
    for (const r of data as any[]) next[r.user_id] = { name: r.name, lastSeen: new Date(r.last_seen_at).getTime(), ...(r.dataset ? { dataset: r.dataset } : {}) };
    online.value = next;
    if (share) shareResult('chat-online', { user: useProofreadingBackendStore().userId, online: next });
  }

  function onPresence(payload: any) {
    const me = useProofreadingBackendStore().userId;
    if (payload.eventType === 'DELETE') {
      const old = payload.old || {};
      const name = old.name || online.value[old.user_id]?.name;
      if (old.user_id) delete online.value[old.user_id];
      if (name && old.user_id !== me && mayAnnounce('leave', old.user_id)) sysLine('leave', name);
      return;
    }
    const row = payload.new || {};
    if (!row.user_id) return;
    const seen = new Date(row.last_seen_at).getTime();
    const joined = new Date(row.joined_at).getTime();
    // Someone already listed as online (a reload, reopening chat, a second
    // tab) is not news: Ames saw "celiad joined the chat" three times in a
    // row. Only announce people who weren't here.
    const wasOnline = !!online.value[row.user_id];
    // Seen a short while ago: a slowed tab or a reload, not someone arriving.
    const seenLately = seen - (lastSeenBy.get(row.user_id) ?? 0) < BACK_AFTER_MS;
    lastSeenBy.set(row.user_id, seen);
    online.value[row.user_id] = { name: row.name, lastSeen: seen, ...(row.dataset ? { dataset: row.dataset } : {}) };
    // joined_at is only moved on a real (re)join, so a routine heartbeat is silent.
    if (row.user_id !== me && !wasOnline && !seenLately && Math.abs(seen - joined) < 5000 && mayAnnounce('join', row.user_id)) sysLine('join', row.name);
  }

  // Two tabs, a reload or a shaky connection can still come and go: one line
  // per person per quarter hour (report 307).
  const ANNOUNCE_EVERY_MS = 15 * 60_000;
  const announced = new Map<string, number>();
  const lastSeenBy = new Map<string, number>();
  const BACK_AFTER_MS = 15 * 60_000;
  function mayAnnounce(kind: 'join' | 'leave', userId: string): boolean {
    const key = `${kind}:${userId}`, now = Date.now();
    if (now - (announced.get(key) ?? 0) < ANNOUNCE_EVERY_MS) return false;
    announced.set(key, now);
    return true;
  }

  function addTimeSeparatorIfNeeded(date: Date) {
    const dateStr = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    if (dateStr !== lastMessageDate) {
      lastMessageDate = dateStr;
      chatMessages.value.push({
        type: 'time',
        name: '',
        rank: '',
        time: dateStr,
        dateTime: date,
        parts: [],
      });
    }
  }

  /** Seed the panel with the most recent persisted messages (chronological, with
   *  timestamps) so users see context instead of a blank chat on open. Silently
   *  no-ops if the `chat_messages` table isn't present yet. */
  function rowToMessage(r: any): ChatMessage {
    const date = new Date(r.created_at);
    return {
      type: 'message',
      name: r.name,
      rank: r.user_id || (r.rank === 'bot' && r.name === NURRO_NAME) ? (r.rank || 'player') : 'player',
      time: formatTime(date),
      dateTime: date,
      parts: parseMessageParts(r.name, r.text, r.dataset),
      dataset: r.dataset ?? null,
      notificationId: r.notification_id ?? null,
      id: r.id ?? null,
      userId: r.user_id ?? null,
      replyTo: r.reply_to ?? null,
    };
  }

  const HISTORY_PAGE = 30;
  /** False once a read with reply_to has been refused (column not there yet). */
  let replyColumnOk = true;

  /** nkem_test answers "for science" (src/chat_bot.ts). Local only. */
  /** nkem_test ("for science") and Nurro ("!" commands), src/chat_bot.ts.
   *  Local only: every reader's browser works out the same reply. */
  function botReplyTo(m: ChatMessage, live = false): ChatMessage | null {
    if (m.type !== 'message' || m.notificationId || m.id == null || m.rank === 'bot') return null;
    const text = m.parts.filter(p => p.type !== 'sender').map(p => p.text).join('');
    const cutoff = Date.now() - ONLINE_MS;
    const here = live ? Object.values(online.value).filter(p => p.lastSeen >= cutoff).map(p => p.name) : null;
    const r = botReply(String(m.id), text, here);
    if (!r) return null;
    const at = new Date(m.dateTime.getTime() + 1);
    return { type: 'message', name: r.name, rank: 'bot', time: formatTime(at), dateTime: at,
      parts: [{ type: 'sender', text: r.name }, { type: 'text', text: r.text }], botLanguage: r.language };
  }
  // ── Nurro's daily leaders (Ames 2026-09-29) ──
  // Once a day, the first time you open chat, Nurro posts the last 24 hours'
  // top editors and top cell completers (the leaderboard's 24H numbers,
  // queried directly so a new player's big day isn't cut by the top 50 list).
  // Local like the other bot lines: it isn't written to the database.
  const DAILY_LEADERS_KEY = 'nge-chat-daily-leaders';
  async function announceDailyLeaders(force = false) {
    const today = new Date().toLocaleDateString('en-CA');       // local YYYY-MM-DD
    try { if (!force && localStorage.getItem(DAILY_LEADERS_KEY) === today) return; } catch { /* private mode */ }
    try {
      const top = (col: 'edits_24h' | 'completions_24h') => supabase.from('user_edit_counts')
        .select(`display_name,${col}`).gt(col, 0).order(col, { ascending: false }).limit(3);
      const [e, c] = await Promise.all([top('edits_24h'), top('completions_24h')]);
      if (e.error && c.error) return;
      const list = (rows: any[] | null, col: string) => (rows || []).map(r => `${r.display_name} (${r[col]})`).join(', ');
      const parts: string[] = [];
      if (e.data?.length) parts.push(`Most edits: ${list(e.data, 'edits_24h')}.`);
      if (c.data?.length) parts.push(`Most cells completed: ${list(c.data, 'completions_24h')}.`);
      const text = parts.length
        ? `☀️ Today's leaders, last 24 hours. ${parts.join(' ')} Nice work, everyone!`
        : `☀️ No edits in the last 24 hours yet. The top spot is wide open!`;
      const at = new Date();
      addTimeSeparatorIfNeeded(at);
      const rows = (d: any[] | null, col: string) => (d || []).map(r => ({ name: String(r.display_name || 'Player'), n: Number(r[col]) || 0 }));
      chatMessages.value.push({ type: 'message', name: NURRO_NAME, rank: 'bot', time: formatTime(at), dateTime: at,
        parts: [{ type: 'sender', text: NURRO_NAME }, { type: 'text', text }],
        daily: { edits: rows(e.data, 'edits_24h'), cells: rows(c.data, 'completions_24h') } });
      try { localStorage.setItem(DAILY_LEADERS_KEY, today); } catch { /* private mode */ }
    } catch (err) {
      console.warn('[chat] daily leaders failed:', err);
    }
  }

  /** Nurro's "!online name": online now, else last chat message and last
   *  edit. Presence rows are deleted on leave, so they can't say "last seen". */
  async function answerOnline(handle: string) {
    const lc = handle.toLowerCase();
    const cutoff = Date.now() - ONLINE_MS;
    const here = Object.values(online.value).find(p => (p.name || '').toLowerCase() === lc && p.lastSeen >= cutoff);
    let text: string;
    if (here) {
      text = describeLastSeen(handle, { name: here.name, onlineNow: true, exists: true });
    } else {
      // Exact, case-insensitive match: escape LIKE wildcards ("_" is common in handles).
      const pat = handle.replace(/[\\%_]/g, m => '\\' + m);
      try {
        const [u, msg] = await Promise.all([
          supabase.from('users').select('username,last_edit_at').ilike('username', pat).limit(1),
          supabase.from('chat_messages').select('name,created_at').ilike('name', pat)
            .order('created_at', { ascending: false }).limit(1),
        ]);
        const user = (u.data as any[] | null)?.[0];
        const last = (msg.data as any[] | null)?.[0];
        text = describeLastSeen(handle, {
          name: user?.username || last?.name,
          exists: !!(user || last),
          lastChat: last?.created_at ? new Date(last.created_at) : null,
          lastEdit: user?.last_edit_at ? new Date(user.last_edit_at) : null,
        });
      } catch {
        text = `I couldn't look up ${handle} just now. Try again in a moment?`;
      }
    }
    const at = new Date();
    chatMessages.value.push({ type: 'message', name: NURRO_NAME, rank: 'bot', time: formatTime(at), dateTime: at,
      parts: [{ type: 'sender', text: NURRO_NAME }, { type: 'text', text }] });
  }

  function withBot(m: ChatMessage): ChatMessage[] {
    const r = botReplyTo(m);
    // Nurro answers only your own commands; nkem_test's "for science" is for all.
    if (r && r.name === NURRO_NAME && m.userId !== useProofreadingBackendStore().userId) return [m];
    return r ? [m, r] : [m];
  }

  async function fetchPage(before: string | null) {
    // reply_to arrived with replies (Ames 2026-10-05). Until its column and
    // the gateway know it, the read falls back to the old shape, so chat
    // never goes blank over a missing migration.
    const page = (cols: string) => {
      let q = supabase.from('chat_messages').select(cols).order('created_at', { ascending: false }).limit(HISTORY_PAGE);
      if (before) q = q.lt('created_at', before);
      return q;
    };
    const BASE = 'id, user_id, name, rank, text, created_at, dataset, notification_id';
    let { data, error }: { data: any[] | null; error: any } = replyColumnOk ? await page(BASE + ', reply_to') as any : { data: null, error: true };
    if (error || !data) {
      if (replyColumnOk) replyColumnOk = false;
      ({ data, error } = await page(BASE) as any);
    }
    if (error || !data) return null;
    if (data.length < HISTORY_PAGE) hasMoreHistory.value = false;
    if (data.length) oldestLoadedAt = data[data.length - 1].created_at;
    return data.slice().reverse();                        // chronological
  }

  /** Seed the panel with the last page of persisted messages (Ames
   *  2026-09-28: it used to show only 5, so people arrived to no context).
   *  Silently no-ops if the `chat_messages` table isn't present yet. */
  async function loadRecentMessages() {
    try {
      const rows = await fetchPage(null);
      if (!rows) return;
      for (const r of rows) {
        addTimeSeparatorIfNeeded(new Date(r.created_at));
        chatMessages.value.push(...withBot(rowToMessage(r)));
      }
      void loadReactions(rows.map((r: any) => String(r.id)).filter((id: string) => id && id !== 'null'));
      // The cells completed over the same stretch of time, at most two days back.
      const oldest = rows.length ? new Date(rows[0].created_at).getTime() : Date.now() - 6 * 3600_000;
      void loadCompletionLines(new Date(Math.max(oldest, Date.now() - 48 * 3600_000)).toISOString());
    } catch (e) {
      console.warn('[chat] loadRecentMessages failed:', e);
    }
  }

  /** Scrolled to the top: fetch the page before the oldest loaded message. */
  async function loadOlder(): Promise<number> {
    if (loadingHistory.value || !hasMoreHistory.value || !oldestLoadedAt) return 0;
    loadingHistory.value = true;
    try {
      const rows = await fetchPage(oldestLoadedAt);
      if (!rows?.length) return 0;
      const known = new Set(chatMessages.value.map(m => m.id).filter(id => id != null));
      const older = rows.filter((r: any) => !known.has(r.id)).flatMap((r: any) => withBot(rowToMessage(r)));
      chatMessages.value = [...older, ...chatMessages.value];
      rebuildSeparators();
      void loadReactions(older.filter(m => m.id != null).map(m => String(m.id)));
      return older.length;
    } catch (e) {
      console.warn('[chat] loadOlder failed:', e);
      return 0;
    } finally {
      loadingHistory.value = false;
    }
  }

  /** Realtime came back after a drop: fetch what was posted while it was
   *  down, newer than the newest message shown, and fold it in by time. */
  async function loadMissed() {
    const shown = chatMessages.value.filter(m => m.type === 'message' && m.id != null);
    if (!shown.length) { await loadRecentMessages(); return; }
    let since = new Date(Math.max(...shown.map(m => m.dateTime.getTime()))).toISOString();
    const known = new Set(shown.map(m => m.id));
    const BASE = 'id, user_id, name, rank, text, created_at, dataset, notification_id';
    const page = (cols: string) => supabase.from('chat_messages').select(cols)
      .gt('created_at', since).order('created_at', { ascending: true }).limit(HISTORY_PAGE);
    const missed: ChatMessage[] = [];
    try {
      // A long outage can span several pages; stop at ten so a stale tab
      // doesn't pull the whole table.
      for (let i = 0; i < 10; i++) {
        let { data, error }: { data: any[] | null; error: any } = replyColumnOk ? await page(BASE + ', reply_to') as any : { data: null, error: true };
        if (error || !data) ({ data, error } = await page(BASE) as any);
        if (error || !data?.length) break;
        // created_at is finer than a JS Date, so the newest shown row can come back.
        missed.push(...data.filter((r: any) => !known.has(r.id)).flatMap((r: any) => withBot(rowToMessage(r))));
        if (data.length < HISTORY_PAGE) break;
        since = data[data.length - 1].created_at;
      }
    } catch (e) {
      console.warn('[chat] loadMissed failed:', e);
    }
    if (!missed.length) return;
    chatMessages.value = [...chatMessages.value, ...missed].sort((a, b) => a.dateTime.getTime() - b.dateTime.getTime());
    rebuildSeparators();
    void loadReactions(missed.filter(m => m.id != null).map(m => String(m.id)));
  }

  /** Re-derive the date separators after older messages are prepended. */
  function rebuildSeparators() {
    const out: ChatMessage[] = [];
    let last = '';
    for (const m of chatMessages.value) {
      if (m.type === 'time') continue;
      const d = m.dateTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      if (d !== last) {
        out.push({ type: 'time', name: '', rank: '', time: d, dateTime: m.dateTime, parts: [] });
        last = d;
      }
      out.push(m);
    }
    chatMessages.value = out;
    lastMessageDate = last;
  }

  // Reactions (Ames 2026-09-28). chat_reactions rows are written only through
  // the verified gateway, which stamps user_id and name from the signed-in
  // identity; everyone may read them.
  function addReactionLocal(messageId: string, emoji: string, userId: string, name: string) {
    const byEmoji = reactions.value[messageId] ?? (reactions.value[messageId] = {});
    const list = byEmoji[emoji] ?? (byEmoji[emoji] = []);
    if (!list.some(r => r.userId === userId)) list.push({ userId, name });
  }
  function removeReactionLocal(messageId: string, emoji: string, userId: string) {
    const byEmoji = reactions.value[messageId];
    const list = byEmoji?.[emoji];
    if (!list) return;
    const i = list.findIndex(r => r.userId === userId);
    if (i >= 0) list.splice(i, 1);
    if (!list.length) delete byEmoji[emoji];
  }

  async function loadReactions(ids: string[]) {
    if (!ids.length) return;
    const { data, error } = await supabase.from('chat_reactions')
      .select('message_id,user_id,name,emoji').in('message_id', ids).limit(2000);
    if (error || !data) return;            // table not created yet: no reactions
    for (const r of data as any[]) addReactionLocal(r.message_id, r.emoji, r.user_id, r.name);
  }

  async function toggleReaction(messageId: string, emoji: string) {
    const backend = useProofreadingBackendStore();
    const me = backend.userId;
    if (!me || !CHAT_REACTION_EMOJI.includes(emoji)) return;
    const mine = reactions.value[messageId]?.[emoji]?.some(r => r.userId === me);
    if (mine) {
      removeReactionLocal(messageId, emoji, me);
      const { error } = await supabase.from('chat_reactions').delete().eq('message_id', messageId).eq('emoji', emoji);
      if (error) { console.warn('[chat] unreact failed:', error.message); addReactionLocal(messageId, emoji, me, backend.chatHandle); }
    } else {
      addReactionLocal(messageId, emoji, me, backend.chatHandle);
      const { error } = await supabase.from('chat_reactions').insert({ message_id: messageId, emoji });
      if (error && error.code !== '23505') { console.warn('[chat] react failed:', error.message); removeReactionLocal(messageId, emoji, me); }
    }
  }

  // Chat while EyeWire is in the background (Ames 2026-09-28): the tab title
  // counts new messages until you come back, plus an optional notification.
  let baseTitle = '';
  function stopTitleFlash() {
    if (document.hidden || !document.hasFocus()) return;
    if (awayCount) document.title = baseTitle;
    awayCount = 0;
    document.removeEventListener('visibilitychange', stopTitleFlash);
    window.removeEventListener('focus', stopTitleFlash);
  }
  // Every new message while you are away (Amy 2026-09-28), not only
  // @mentions: the tab title counts them, and with the bell on each one also
  // raises a browser notification. A mention is called out as a mention.
  // The tab shows a steady count of new messages, "(3) EyeWire II", no
  // blinking (Amy 2026-09-28); it clears when you come back.
  let awayCount = 0;
  function alertMentionAway(from: string, text: string, isMention = true) {
    if (!document.hidden && document.hasFocus()) return;
    if (!awayCount) {
      baseTitle = document.title;
      document.addEventListener('visibilitychange', stopTitleFlash);
      window.addEventListener('focus', stopTitleFlash);
    }
    awayCount++;
    document.title = `(${awayCount}) ${baseTitle}`;
    if (mentionNotify.value && 'Notification' in window && Notification.permission === 'granted') {
      try {
        const n = new Notification(isMention ? `${from} mentioned you in EyeWire II chat` : `${from} in EyeWire II chat`, {
          body: text.slice(0, 160), tag: isMention ? 'ew-chat-mention' : 'ew-chat',
        });
        n.onclick = () => { window.focus(); n.close(); };
      } catch { /* notifications unavailable in this context */ }
    }
  }

  /** Turn browser notifications for @mentions on or off. Asking for
   *  permission needs a click, so this is called from the chat's bell. */
  async function setMentionNotify(on: boolean): Promise<boolean> {
    if (on && !('Notification' in window)) on = false;
    if (on && Notification.permission !== 'granted') {
      try { if (await Notification.requestPermission() !== 'granted') on = false; } catch { on = false; }
    }
    mentionNotify.value = on;
    try { localStorage.setItem(MENTION_NOTIFY_KEY, on ? '1' : '0'); } catch { /* private mode */ }
    return on;
  }

  async function connect() {
    if (channel || connecting) return; // already connected / connecting

    const backend = useProofreadingBackendStore();
    // Login gate: don't connect anonymous users — chat needs an authenticated identity.
    if (!backend.userId || !backend.userName) {
      console.warn('[chat] connect: skipped — user not logged in');
      return;
    }
    // Prefer the unique username; fall back to display name until one is set.
    const displayName = backend.chatHandle;
    if (displayName === 'Anonymous') {
      console.warn('[chat] connect: displayName fell through to "Anonymous" — defensive only');
    }

    connecting = true;
    // Show the recent conversation before we announce the join / go live.
    await loadRecentMessages();
    void announceDailyLeaders();

    // Only database rows written by the verified backend reach the chat UI.
    // Public broadcast events are never treated as authenticated messages.
    let subscribedOnce = false;
    openChannel = () => {
    const mine = supabase.channel('eyewire-ii-chat-verified');
    channel = mine;
    mine.on('postgres_changes', {event:'INSERT', schema:'public', table:'chat_messages'}, payload => {
      const row = payload.new;
      // No sender: only Nurro's own lines, the Monthly Champions announcement.
      if (!row.user_id && !(row.rank === 'bot' && row.name === NURRO_NAME)) return;
      notePinRow(row);
      const date = new Date(row.created_at);
      addTimeSeparatorIfNeeded(date);
      chatMessages.value.push({type:'message', name:row.name, rank:row.rank || 'player', time:formatTime(date), dateTime:date,
        parts:parseMessageParts(row.name,row.text,row.dataset), dataset:row.dataset ?? null, notificationId:row.notification_id ?? null, id:row.id ?? null, userId:row.user_id ?? null, replyTo:row.reply_to ?? null});
      // "!online name": Nurro looks the person up, then answers (live only).
      // "!leaders" / "!today": Nurro posts the daily leaders card again.
      // Nurro answers privately (Amy 2026-09-30): only the asker's screen
      // answers, and current clients never post their commands at all.
      const mine = row.user_id === backend.userId;
      if (mine && row.notification_id == null && /^\s*!(leaders|today)\b/i.test(row.text || '')) void announceDailyLeaders(true);
      const target = mine && row.notification_id == null ? onlineTarget(row.text || '') : null;
      if (target) void answerOnline(target);
      // nkem_test takes a beat to answer, like it used to.
      // Lines sent close together can arrive out of order (Ames 2026-11-01).
      const pushed = chatMessages.value[chatMessages.value.length - 1], before = chatMessages.value[chatMessages.value.length - 2];
      if (before && before.dateTime.getTime() > pushed.dateTime.getTime()) {
        chatMessages.value = [...chatMessages.value].sort((a, b) => a.dateTime.getTime() - b.dateTime.getTime());
      }
      const reply = botReplyTo(pushed, true);
      if (reply && (reply.name !== NURRO_NAME || mine)) setTimeout(() => { reply.dateTime = new Date(); reply.time = formatTime(reply.dateTime); chatMessages.value.push(reply); }, 900);
      if (row.user_id !== backend.userId) {
        // A direct @mention always gets through, even with chat muted.
        // A reply to one of your messages reaches you like an @mention does.
        const repliedToMe = !!row.reply_to && chatMessages.value.some(m => m.id === row.reply_to && m.userId === backend.userId);
        const mention = repliedToMe || (mentionsMe(row.text || '') && !onlineTarget(row.text || ''));
        if (mention) {
          mentionPing.value++; lastMentionFrom.value = row.name || '';
          alertMentionAway(row.name || 'Someone', row.text || '', true);
        } else if (!useUserPreferencesStore().prefs.chatMuted && row.notification_id == null && row.rank !== 'achievement') {
          alertMentionAway(row.name || 'Someone', row.text || '', false);
        }
        // An achievement line is a quiet aside: it never lights the unread count.
        if (!useUserPreferencesStore().prefs.chatMuted && row.rank !== 'achievement') {
          unreadMessages.value = true;
          if (!panelVisible.value) unreadCount.value++;
        }
      }
    }).on('postgres_changes', {event:'DELETE', schema:'public', table:'chat_messages'}, payload => {
      // A deleted message disappears for everyone watching.
      const id = (payload.old as any)?.id;
      if (id != null) removeLocal(id);
    }).on('postgres_changes', {event:'INSERT', schema:'public', table:'chat_reactions'}, payload => {
      const r = payload.new as any;
      if (r?.message_id != null) addReactionLocal(r.message_id, r.emoji, r.user_id, r.name);
    }).on('postgres_changes', {event:'DELETE', schema:'public', table:'chat_reactions'}, payload => {
      const r = payload.old as any;
      if (r?.message_id != null) removeReactionLocal(r.message_id, r.emoji, r.user_id);
    }).on('postgres_changes', {event:'*', schema:'public', table:'chat_presence'}, onPresence)
      .on('postgres_changes', {event:'INSERT', schema:'public', table:'edit_log', filter:'operation=in.(mark_complete,complete_task)'}, onCompletion)
      .subscribe(status => {
        if (channel !== mine) return;       // an earlier connection, already replaced
        connected.value = status === 'SUBSCRIBED';
        if (status !== 'SUBSCRIBED') {
          // The live connection could not be made, or dropped: carry on by
          // asking for new messages, and try for it again.
          if (status === 'TIMED_OUT' || status === 'CLOSED' || status === 'CHANNEL_ERROR') { startPolling(); rejoinSoon(); }
          return;
        }
        stopPolling();
        rejoinDelay = 5000;
        if (status === 'SUBSCRIBED') {
          // A later SUBSCRIBED is a rejoin after a drop: backfill the gap.
          if (subscribedOnce) void loadMissed();
          subscribedOnce = true;
          // Say we're here (other open chats show the join), then who else is.
          void heartbeat().then(() => loadOnline());
          startPresenceLoops();
        }
      });
    };
    openChannel();
    window.addEventListener('pagehide', leavePresence);
    document.addEventListener('visibilitychange', beatWhenBack);
    connecting = false; // channel is now assigned; the guard above holds
  }

  /**
   * @param notificationId set on announcement messages, so the client can render
   *   the message as a link that opens that exact notification instead of
   *   telling people to go and find it.
   */
  function sendMessage(text: string, notificationId: number | null = null, replyTo: string | null = null) {
    // Sending goes through the server, not the live connection, so it also
    // works while chat is asking for messages on a timer.
    if (!connected.value && !polling.value) return;
    const backend = useProofreadingBackendStore();
    const name = backend.chatHandle;
    // 'eyewirer' with production access, 'explorer' without, 'player' while
    // it is not known. The saved message gets the server's own answer.
    const production = editsAny(DATASETS.filter(d => d.section === 'production').map(d => d.caveDataset));
    const rank = backend.isAdmin ? 'admin' : production === true ? 'eyewirer' : production === false ? 'explorer' : 'player';
    // The box blocks long messages too; this holds the limit if it is bypassed.
    // Announcements carry a notification title and are not capped.
    if (notificationId == null && chatTextLength(text) > CHAT_MAX_CHARS) return;
    // A Nurro command is answered on your screen only and never posted, so
    // asking Nurro does not fill everyone's chat (Amy 2026-09-30). "!science"
    // belongs to nkem_test's public joke and is still posted.
    if (notificationId == null && /^\s*(!([a-z]+)|@nurro)\b/i.test(text)) {
      const probe = botReply('probe', text, []);
      if (!probe || probe.name === NURRO_NAME) { askNurroPrivately(name, rank, text); return; }
    }
    // Persist for history so the last messages show on next open (best-effort;
    // no-ops if the chat_messages table isn't present).
    const row: Record<string, any> = { name, rank, text, dataset: currentDatasetName(), notification_id: notificationId };
    const post = (r: Record<string, any>) => supabase.from('chat_messages').insert(r);
    // With no live connection the message will not come back by itself: fetch it.
    const shown = () => { if (polling.value) void loadMissed(); };
    if (!replyTo) { post(row).then(({ error }) => { if (error) console.warn('[chat] persist failed:', error.message); else shown(); }); return; }
    // A reply the database cannot hold yet is still sent, as a plain message.
    post({ ...row, reply_to: replyTo }).then(({ error }) => {
      if (!error) { shown(); return; }
      console.warn('[chat] reply link not stored, sent as a plain message:', error.message);
      post(row).then(({ error: e2 }) => { if (e2) console.warn('[chat] persist failed:', e2.message); });
    });
  }

  /** An announcement in chat for a notification: true once the server kept
   *  it, false when chat is not open or the server refused it (only public
   *  notifications may be posted), so the caller can try again later. */
  async function postAnnouncement(text: string, notificationId: number): Promise<boolean> {
    if (!connected.value && !polling.value) return false;
    const backend = useProofreadingBackendStore();
    const { error } = await supabase.from('chat_messages').insert({
      name: backend.chatHandle, rank: backend.isAdmin ? 'admin' : 'player', text, dataset: currentDatasetName(), notification_id: notificationId,
    });
    if (error) { console.warn('[chat] announcement not posted:', error.message); return false; }
    if (polling.value) void loadMissed();
    return true;
  }

  function askNurroPrivately(name: string, rank: string, text: string) {
    const now = new Date();
    addTimeSeparatorIfNeeded(now);
    chatMessages.value.push({ type: 'message', name, rank, time: formatTime(now), dateTime: now,
      parts: parseMessageParts(name, text), private: true });
    if (/^\s*!(leaders|today)\b/i.test(text)) { void announceDailyLeaders(true); return; }
    const target = onlineTarget(text);
    if (target) { void answerOnline(target); return; }
    const cutoff = Date.now() - ONLINE_MS;
    const here = Object.values(online.value).filter(p => p.lastSeen >= cutoff).map(p => p.name);
    const r = botReply(String(now.getTime()), text, here);
    if (!r) return;
    setTimeout(() => {
      const at = new Date();
      chatMessages.value.push({ type: 'message', name: r.name, rank: 'bot', time: formatTime(at), dateTime: at,
        parts: [{ type: 'sender', text: r.name }, { type: 'text', text: r.text }], private: true });
    }, 400);
  }

  function removeLocal(id: string | number) {
    const i = chatMessages.value.findIndex(m => m.id === id);
    if (i >= 0) chatMessages.value.splice(i, 1);
  }

  /**
   * Delete a persisted message. Admin only: the ewCommunityData gateway allows
   * chat_messages changes other than POST only for admins, by message id, so
   * this is enforced server side as well as hidden in the UI.
   */
  async function deleteMessage(id: string | number): Promise<boolean> {
    // The gateway scopes a player's delete to their own messages (admins: any),
    // so a zero-row result means "not yours", not success.
    const { data, error } = await supabase.from('chat_messages').delete().eq('id', id).select('id');
    if (error || !data?.length) { console.warn('[chat] delete failed:', error?.message ?? 'no row'); return false; }
    removeLocal(id);
    return true;
  }

  /** Remove our presence row so other chats show the leave. Best effort. */
  function leavePresence() {
    const me = useProofreadingBackendStore().userId;
    if (me) void supabase.from('chat_presence').delete().eq('user_id', me);
  }

  function markRead() {
    unreadMessages.value = false;
    unreadCount.value = 0;
  }

  function setPanelVisible(v: boolean) {
    panelVisible.value = v;
    if (v) unreadCount.value = 0;
  }

  /** Does this message text @mention the current user? */
  function mentionsMe(text: string): boolean {
    const b = useProofreadingBackendStore();
    return parseMessageParts('', text).some(p => p.type === 'mention' && isSelfMentionToken(p.text, b.username, b.userName));
  }

  function disconnect() {
    if (channel) {
      leavePresence();
      if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
      if (staleTimer) { clearInterval(staleTimer); staleTimer = null; }
      presenceLeader.leave();
      window.removeEventListener('pagehide', leavePresence);
      document.removeEventListener('visibilitychange', beatWhenBack);
      online.value = {};
      const old = channel;
      channel = null;
      openChannel = null;
      stopPolling();
      if (rejoinTimer) { clearTimeout(rejoinTimer); rejoinTimer = null; }
      supabase.removeChannel(old);
      connected.value = false;
      connecting = false;
    }
  }

  return { chatMessages, connected, polling, unreadMessages, unreadCount, connect, sendMessage, postAnnouncement, markRead, disconnect, deleteMessage, onlineCount, online,
    pinned, loadPin, pinMessage,
    panelVisible, setPanelVisible, mentionPing, lastMentionFrom,
    hasMoreHistory, loadingHistory, loadOlder, reactions, toggleReaction, mentionNotify, setMentionNotify };
});
